/**
 * ─────────────────────────────────────────────────────────────────────────────
 * @module  public/js/controllers/focusmodecallstepcontroller.js
 * @class   FocusModeCallStepController
 * @summary Dynamic step‐controller for CALL, EMAIL, LINKEDIN & TASK flows.
 *
 * @description
 * • Primary CTA label / colour adapt to `task.type` (CALL, EMAIL, or TASK)
 * • For CALL: pulls phone options from task & ContactsStaging (lookupStagedPhones)
 *   and prefixes +1 if needed
 * • CALL → twilioVoice (Twilio v2) connect() then show secondary CTAs
 * • EMAIL → lookup staging email → open composer → sendCustomEmail → mark outcome
 * • TASK → mark complete then finish
 * • Secondary CTAs (“GO / No Resp / No Go”) only after CALL
 *
 * @author   TopSail AI
 * @version  2.0.0
 * @since    2025-05-15
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { DEFAULT_TWILIO_EDGE } from '../services/twilioEdgeConfiguration.js';
import { openEmailComposer } from '../modals/emailComposerModal.js';
import { openCallScriptModal } from '../modals/callScriptModal.js';
import { openLinkedinMessageModal } from '../modals/linkedinMessageModal.js';
import { openSmsMessageModal } from '../modals/smsMessageModal.js';
import { openGoModal } from '../modals/goModal.js';
import { LinkedInModal } from '../ui/modals/linkedin-modal.js';
import { lookupStagedEmail } from '../services/contactsStagingEmailFinder.js';
import { lookupStagedPhones } from '../services/contactsStagingPhoneFinder.js';
import { saveStagedPhones } from '../services/contactsStagingPhoneWriter.js';
import { openNoRespModal } from '../modals/noRespModal.js';
import {
  isFollowUpDestinationEligible,
  isFollowUpEnrollmentEnabled,
  localDateKey,
  openFollowUpEnrollmentModal,
  resolveFollowUpEnrollmentTrigger,
} from '../modals/followUpEnrollmentModal.js';
import { docWithTenant } from '../utils/firestoreHelpers.js';
import { normaliseUrl } from '../utils/linkBuilder.js';
import { patchStagingSameAsContactEdit } from '../services/contactsStagingService.js';
import {
  applySmsBodyToDeeplink,
  buildTextDeeplink,
  extractSmsBodyFromLink,
  SMS_PROVIDER,
} from '../utils/smsDeeplink.js';
import useTenant from '../useTenant.js';
import LoadingIndicatorService from '../services/loadingindicatorservice.js';
import { resolveActiveTenantId } from '../utils/tenantUtils.js';
import {
  fetchVoiceToken,
  resolveTwilioVoiceService,
} from '../services/twilioVoiceBootstrap.js';
import { getSmsProvider } from '../config/smsProvider.js';
import { applyFocusModeTooltips } from '../focusmode/tooltipService.js';
import {
  CONTACT_LOCAL_CALL_WINDOW_DEFERRED_BY,
  CONTACT_LOCAL_CALL_WINDOW_HELD_BY,
  evaluateContactLocalCallWindow,
} from '../focusmode/contactLocalCallWindow.js';
import {
  FOCUSMODE_PHONE_ORDER_OPTIONS,
  normalizeFocusModePhoneOrder,
} from '../focusmode/phoneOrder.js';
import {
  FOCUS_MODE_CALL_MODE,
  resolveFocusModeCallMode,
} from '../utils/focusModeCallMode.js';
import {
  clearFocusModeSystemCallState,
  loadFocusModeSystemCallState,
  saveFocusModeSystemCallState,
} from '../focusmode/systemCallState.js';
import { resolveContactTimeZone } from '../services/contactTimeZoneResolver.js';
import { dateKeyInTz } from '../services/focusModeTaskService.js';
import {
  normaliseCallScripts,
  normaliseTemplateIdentifiers,
} from '../utils/taskMeta.js';
import {
  extensionFieldForPhoneField,
  normalizePhoneNumberWithExtension,
  sanitizePhoneExtension,
} from '../utils/phoneExtension.js';

const STARTING_CALL_MESSAGE = 'Starting your call…';
const FOLLOW_UP_EMAIL_MESSAGE = 'Sending follow-up email…';
const LOGGING_OUTCOME_MESSAGE = 'Logging your outcome…';
const CONTACT_LOCAL_CALL_WINDOW_MODAL_ID = 'focusModeCallWindowModal';
const REDIAL_COOLDOWN_MS = 2000;

/**
 * Normalize a phone number for comparison by stripping punctuation.
 * Removes a leading US country code when present.
 * @param {string|number|null|undefined} v - Raw phone input.
 * @returns {string}
 */
function normPhone(v) {
  const digits = String(v || '').replace(/\D/g, '');
  if (!digits) return '';
  return digits.length === 11 && digits.startsWith('1')
    ? digits.slice(1)
    : digits;
}

/**
 * Resolve the active sequence enrolment identifier from a Focus Mode task.
 *
 * Older task payloads can carry the enrolment reference under legacy keys,
 * so this helper keeps sequence-scoped features working retroactively.
 *
 * @param {object|null|undefined} task - Focus Mode task payload.
 * @returns {string|null} Enrolment identifier or `null` when unavailable.
 */
function resolveSequenceEnrolmentId(task) {
  if (!task || typeof task !== 'object') return null;
  const candidates = [
    task.sequenceId,
    task.enrolId,
    task.sequence_id,
    task.sequence?.id,
  ];
  for (const candidate of candidates) {
    if (candidate == null) continue;
    const value = String(candidate).trim();
    if (value) return value;
  }
  return null;
}

/**
 * Build delete updates for any phone field matching the dialed value.
 * @param {object} params
 * @param {string|number|null|undefined} params.dialedRaw - Dialed phone value.
 * @param {object} params.contact - Contact record with phone fields.
 * @returns {{ updates: Record<string, null>, dialed: string, matched: string[] }}
 */
function buildDeleteByValueUpdates({ dialedRaw, contact }) {
  const dialed = normPhone(dialedRaw);
  const updates = {};
  const fields = ['mobilePhone', 'officePhone', 'mainOfficePhone'];
  if (!dialed) return { updates, dialed, matched: [] };

  const matched = [];
  for (const field of fields) {
    const cur = contact?.[field];
    if (normPhone(cur) && normPhone(cur) === dialed) {
      updates[field] = null;
      matched.push(field);
    }
  }
  return { updates, dialed, matched };
}

const MOBILE_NO_RESP_REASONS = [
  {
    label: 'Left voicemail',
    value: 'voicemail',
    action: 'next',
  },
  {
    label: 'Left a message',
    value: 'message',
    action: 'next',
  },
  {
    label: 'No message',
    value: 'none',
    action: 'next',
  },
  {
    label: 'Out of office',
    value: 'outOfOffice',
    action: 'next',
    useModal: true,
  },
  {
    label: 'Disconnected',
    value: 'disconnected',
    action: 'next',
  },
  {
    label: 'Wrong number',
    value: 'wrong',
    action: 'next',
  },
];

const PHONE_ORDER_LABELS = Object.freeze(
  Object.fromEntries(
    FOCUSMODE_PHONE_ORDER_OPTIONS.map((option) => [option.key, option.label])
  )
);

export default class FocusModeCallStepController {
  /** Toggle verbose console traces at runtime: `window.__FM_DEBUG = true` */
  static DEBUG = false;
  static EMAIL_OPT_OUT_CACHE = new Map();

  /** tiny helpers to keep logs consistent */
  #d() {
    return Boolean(window.__FM_DEBUG ?? FocusModeCallStepController.DEBUG);
  }
  #log(...args) {
    if (this.#d()) console.log('[FocusModeCallStep]', ...args);
  }
  #warn(...args) {
    if (this.#d()) console.warn('[FocusModeCallStep]', ...args);
  }
  #group(label) {
    if (this.#d()) console.groupCollapsed(`🔎 ${label}`);
  }
  #groupEnd() {
    if (this.#d()) console.groupEnd();
  }

  /**
   * Update the Focus Mode tooltip copy and sync any existing bubble text.
   * @param {HTMLElement|null} el - Tooltip target element.
   * @param {string} text - Tooltip content.
   * @returns {void}
   */
  #setFocusTooltip(el, text) {
    if (!el) return;
    el.dataset.focusTooltip = text || '';
    const doc = el.ownerDocument;
    const tooltipsDisabled =
      this.#services?.focusModeTooltips === false ||
      doc?.body?.dataset?.focusTooltips === 'false' ||
      el.closest?.('[data-focus-tooltips="false"]');
    if (tooltipsDisabled) return;
    const tooltipId = el.getAttribute('data-focus-tooltip-id');
    if (!tooltipId) return;
    const tip = doc?.getElementById(tooltipId);
    if (tip) tip.textContent = text || '';
  }

  /**
   * Determine whether a given task type should follow the phone-call flow.
   * @param {string} [value=this.#task.type] - Raw task type identifier.
   * @returns {boolean}
   */
  #isCallType(value = this.#task.type) {
    const normalized = String(value ?? '').toUpperCase();
    return (
      normalized === 'CALL' ||
      normalized === 'CALL_EMAIL' ||
      normalized === 'CALL_TEXTMESSAGE'
    );
  }

  /**
   * Determine whether a given task type belongs to the LinkedIn task family.
   *
   * @param {string} [value=this.#task.type] - Raw task type identifier.
   * @returns {boolean} True when the task is a LinkedIn step.
   */
  #isLinkedInTask(value = this.#task.type) {
    const normalized = String(value ?? '').toUpperCase();
    return (
      normalized === 'LINKEDIN' ||
      normalized === 'LINKEDIN_ACTIVITY' ||
      normalized === 'LINKEDIN_MESSAGE'
    );
  }

  /**
   * Determine whether the current task is the return-to-log LinkedIn variant.
   *
   * @param {string} [value=this.#task.type] - Raw task type identifier.
   * @returns {boolean} True when the task is LINKEDIN_ACTIVITY.
   */
  #isLinkedInActivityTask(value = this.#task.type) {
    return String(value ?? '').toUpperCase() === 'LINKEDIN_ACTIVITY';
  }

  /**
   * Check whether the current task uses the Call + Email hybrid behaviour.
   * @returns {boolean}
   */
  #isCallEmailTask() {
    return String(this.#task.type ?? '').toUpperCase() === 'CALL_EMAIL';
  }

  /**
   * Resolve the default template set for Call + Email follow-up actions.
   *
   * @returns {{ templateId: string|null, templateIds: string[], source: 'default' }}
   */
  #getDefaultCallEmailTemplateSet() {
    const templateIds = normaliseTemplateIdentifiers([
      ...(Array.isArray(this.#meta?.templates) ? this.#meta.templates : []),
      ...(Array.isArray(this.#task?.templateIds) ? this.#task.templateIds : []),
      ...(Array.isArray(this.#task?.templates) ? this.#task.templates : []),
      this.#meta?.tpl,
      this.#meta?.templateId,
      this.#task?.templateId,
      this.#task?.tpl,
    ]);

    return {
      templateId: templateIds[0] ?? null,
      templateIds,
      source: 'default',
    };
  }

  /**
   * Resolve the optional GO-specific template set for Call + Email steps.
   *
   * @returns {{ templateId: string|null, templateIds: string[], source: 'go' }}
   */
  #getGoCallEmailTemplateSet() {
    const templateIds = normaliseTemplateIdentifiers([
      ...(Array.isArray(this.#meta?.goEmailTemplates)
        ? this.#meta.goEmailTemplates
        : []),
      ...(Array.isArray(this.#task?.goEmailTemplates)
        ? this.#task.goEmailTemplates
        : []),
      this.#meta?.goEmailTpl,
      this.#task?.goEmailTpl,
    ]);

    return {
      templateId: templateIds[0] ?? null,
      templateIds,
      source: 'go',
    };
  }

  /**
   * Load GO-email templates from the backing playbook step when the task
   * itself predates GO-email metadata support.
   *
   * @returns {Promise<{ templateId: string|null, templateIds: string[], source: 'go' }>}
   */
  async #loadGoCallEmailTemplateSetFromPlaybook() {
    const playbookId =
      this.#meta?.seqId ??
      this.#task?.sequenceId ??
      this.#task?.playbookId ??
      this.#task?.enrolId ??
      null;
    const rawStepIdx =
      this.#meta?.stepIdx ??
      this.#task?.stepIdx ??
      this.#task?.stepIndex ??
      null;
    if (!playbookId || rawStepIdx === null || rawStepIdx === undefined) {
      return { templateId: null, templateIds: [] };
    }

    const fsService = this.#services?.fsService;
    if (!fsService?.getDocument) {
      return { templateId: null, templateIds: [] };
    }

    try {
      const playbook = await fsService.getDocument(
        `Playbooks/${playbookId}`,
        false
      );
      const step =
        playbook?.steps?.[String(rawStepIdx)] ??
        playbook?.steps?.[Number(rawStepIdx)] ??
        null;
      const templateIds = normaliseTemplateIdentifiers([
        ...(Array.isArray(step?.goEmailTemplates) ? step.goEmailTemplates : []),
        step?.goEmailTpl,
      ]);

      if (templateIds.length) {
        this.#task.goEmailTpl = templateIds[0] ?? null;
        this.#task.goEmailTemplates = templateIds;
        this.#meta.goEmailTpl = templateIds[0] ?? null;
        this.#meta.goEmailTemplates = templateIds;
      }

      return {
        templateId: templateIds[0] ?? null,
        templateIds,
        source: 'go',
      };
    } catch (err) {
      console.warn(
        '[CallStepController] unable to load GO email templates from playbook',
        err
      );
      return { templateId: null, templateIds: [] };
    }
  }

  /**
   * Resolve the template set used for Call + Email composers.
   *
   * @param {{ preferGoTemplates?: boolean }} [options={}]
   * @returns {Promise<{ templateId: string|null, templateIds: string[] }>}
   */
  async #resolveCallEmailTemplateSet(options = {}) {
    const { preferGoTemplates = false } = options;
    const defaultSet = this.#getDefaultCallEmailTemplateSet();
    const goSet = this.#getGoCallEmailTemplateSet();
    if (preferGoTemplates && goSet.templateIds.length) return goSet;
    if (preferGoTemplates) {
      const playbookSet = await this.#loadGoCallEmailTemplateSetFromPlaybook();
      if (playbookSet.templateIds.length) return playbookSet;
    }
    return defaultSet;
  }

  /**
   * Resolve the channel string recorded on outcome payloads.
   * @returns {string}
   */
  #channelForOutcome() {
    const t = String(this.#task.type ?? '').toUpperCase();
    if (['SMS', 'TEXT', 'SMS_MESSAGE', 'TEXT_MESSAGE'].includes(t))
      return 'SMS';
    if (t === 'CALL_EMAIL') return 'PHONE_EMAIL';
    if (t === 'CALL_TEXTMESSAGE') return 'PHONE_TEXT';
    if (t === 'CALL') return 'PHONE';
    if (t) return t;
    return 'TASK';
  }

  /**
   * Resolve whether Zoom Phone should be offered as a provider option.
   * @returns {boolean}
   */
  #isZoomEnabled() {
    return this.#zoomVoiceEnabled === true;
  }

  /**
   * Persist provider choice and refresh any dependent UI state.
   * @param {'twilio'|'zoom'|'system'} provider
   */
  #setCallProvider(provider = 'twilio') {
    const twilioAllowed =
      this.#services.twilioCanCall ?? this.#services.canCall ?? false;
    let normalized;
    if (this.#usesSystemDialer()) {
      normalized = 'system';
    } else if (provider === 'zoom' && this.#isZoomEnabled()) {
      normalized = 'zoom';
    } else if (provider === 'twilio' && twilioAllowed) {
      normalized = 'twilio';
    } else {
      normalized = this.#isZoomEnabled() ? 'zoom' : 'twilio';
    }
    if (this.#callProvider !== normalized) {
      this.#callProvider = normalized;
      if (normalized === 'twilio' && !this.#services.twilioReady) {
        this.#services.twilioReady = this.#initTwilio();
      }
      this.#render();
    }
  }

  /** Per-refresh tombstones telling preparePhones() to ignore specific staging keys */
  #suppressStaging = new Set();

  #state = 'initial'; // 'initial' | 'awaitingOutcome' | 'noResp'
  #el;
  #services;
  #task;
  #onComplete;
  #meta;
  #spec;
  #replySlot = null;
  #replyDismissed = false;
  #phoneOpts = [];
  #currentPhoneIdx = 0;
  #emailConnected = true;
  #emailOptOut = false;
  #activeCall = null; // current Twilio call
  #callProvider = 'twilio';
  #defaultCallProvider = 'twilio';
  #zoomVoiceEnabled = false;
  #nativePhoneMode = false;
  #keyHandler = null; // bound keyboard listener
  #clickHandler = null; // bound click listener
  #dialpadEl = null; // current dialpad element reference
  #dialpadToggleHandler = null; // bound toggle listener
  #behaviorSelections = new Map();
  #referrals = [];
  #noRespDetails = null; // { option:'voicemail'|'message'|'none'|'outOfOffice', action:'call'|'next', nextTouch?:string }
  #pendingCallTextOutcome = null;
  #callTextCompleteReady = false;
  #queuedPhoneIdxAfterOutcome = null;
  #lastDialed = null; // remembers last dialed number
  #lastDialedTo = null; // remembers last dialed number used to place call
  #lastDialedExtension = ''; // remembers extension used with the last call
  #extensionSendTimer = null;
  #showRedial = false; // flag to show redial button after hangup
  #redialCooldownUntil = 0;
  #redialCooldownTimer = null;
  #linkedinStarted = false; // flag for multi-step LinkedIn tasks
  #linkedinActivityModal = null; // shared modal instance for LINKEDIN_ACTIVITY
  #linkedinAwaitingReturn = false; // true after LinkedIn opens and before the modal appears
  #linkedinReturnSeenHidden = false; // tracks whether the tab was backgrounded
  #linkedinVisibilityHandler = null; // visibilitychange listener for return detection
  #linkedinWindowFocusHandler = null; // focus listener fallback for return detection
  #linkedinModalOpen = false; // prevents duplicate modal opens on focus churn
  #linkedinMessageMeta = null; // remembers template + copy details
  #smsStarted = false; // flag for multi-step SMS tasks
  #smsMessageMeta = null; // remembers SMS template + copy details
  #contactLocalCallWindowBlock = null;
  #contactLocalCallWindowOverrideAccepted = false;
  #contactLocalCallWindowPromptRetryHandle = null;
  #tenantIdCache = null;
  #seqMetaMissing = false;
  #sequencePhoneOrderLoaded = false;
  #activeFocusmodeCallSid = null; // PSTN leg for FocusMode
  #uiMode = 'desktop';
  #outcomeActionBusy = false;
  #busyOutcomeActionKey = '';
  #notesSavePromise = null;
  #queuedNotesText = '';
  #lastSavedNotesText = '';

  constructor({
    container,
    services,
    task,
    onComplete,
    meta = {},
    uiMode = 'desktop',
  }) {
    const metaPreloaded =
      meta.crmLess !== undefined || meta.firstName !== undefined;
    const effectiveType = task.type || meta.type || 'UNKNOWN';
    const upperType = String(effectiveType ?? '').toUpperCase();
    const normalisedType =
      upperType === 'SMS'
        ? 'TEXT'
        : upperType === 'SMS_MESSAGE'
          ? 'TEXT_MESSAGE'
          : upperType || 'UNKNOWN';
    this.#group('ctor');
    this.#log('ctor →', {
      taskId: task.id,
      taskType: effectiveType,
      metaPreloaded,
      metaSummary: Object.keys(meta || {}),
    });
    this.#groupEnd();
    this.#el = container;
    this.#replySlot =
      container?.parentElement?.querySelector('#replySlot') || null;
    this.#services = services;
    this.#task = { ...task, type: normalisedType };
    this.#onComplete = onComplete;
    this.#meta = meta;
    this.#uiMode = uiMode === 'mobile' ? 'mobile' : 'desktop';
    this.#seqMetaMissing =
      this.#meta.seqId == null || this.#meta.stepIdx == null;
    if (this.#seqMetaMissing) {
      this.#meta.seqId = this.#meta.seqId ?? null;
      this.#meta.stepIdx = 0;
    }
    const appCfg = window?.APP_CONFIG || {};
    this.#nativePhoneMode =
      resolveFocusModeCallMode() === FOCUS_MODE_CALL_MODE.NATIVE_PHONE;
    this.#defaultCallProvider = String(
      appCfg.callProvider || 'twilio'
    ).toLowerCase();
    this.#callProvider = this.#defaultCallProvider;
    this.#zoomVoiceEnabled = Boolean(
      appCfg.zoomVoiceEnabled || appCfg?.telephony?.zoomVoiceEnabled
    );
    const twilioAllowed = Boolean(
      services.twilioCanCall ?? services.canCall ?? false
    );
    const forceSystemDialerOnMobile =
      services.focusModeForceSystemDialerOnMobile !== false;
    if (this.#nativePhoneMode && forceSystemDialerOnMobile) {
      this.#defaultCallProvider = 'system';
      this.#callProvider = 'system';
    } else if (this.#callProvider === 'zoom' && !this.#zoomVoiceEnabled) {
      this.#callProvider = 'twilio';
    } else if (this.#callProvider === 'twilio' && !twilioAllowed) {
      this.#callProvider = this.#isZoomEnabled() ? 'zoom' : 'twilio';
    }
    this.#emailConnected = false;
    this.#bootstrap();
  }

  /**
   * Determine whether the dedicated phone UI is active for the current task.
   *
   * @returns {boolean} True when the mobile Focus Mode experience is active.
   */
  #isMobileUi() {
    return this.#uiMode === 'mobile';
  }

  /**
   * Refresh the merge variables used by Mustache templates and re-render the
   * controller so UI placeholders reflect the latest values.
   *
   * @param {Record<string, unknown>} vars
   * @returns {void}
   */
  updateMergeVars(vars = {}) {
    this.#meta.mergeVars = vars;
    this.#render();
  }

  /**
   * Refresh the call controls after the shared calling gate changes. A newly
   * enabled Twilio step reruns its normal bootstrap so the device is ready,
   * while an active call is left untouched until its lifecycle completes.
   *
   * @returns {Promise<void>} Resolves after the call controls are refreshed.
   */
  async refreshCallingAvailability() {
    if (!this.#isCallType(this.#spec?.type) || this.#activeCall) return;
    const twilioAllowed =
      this.#services.twilioCanCall ?? this.#services.canCall ?? false;
    if (this.#callProvider === 'twilio' && twilioAllowed) {
      await this.#bootstrap();
      return;
    }
    this.#render();
  }

  /**
   * Update the ordered template set used by composer-opening actions.
   *
   * @param {{templateId?: string, templateIds?: string[], tpl?: string, templates?: string[]}} payload
   *   Canonical template fields returned from Focus Mode step management.
   * @returns {void}
   */
  updateTemplateSet(payload = {}) {
    const templateIds = normaliseTemplateIdentifiers([
      ...(Array.isArray(payload.templateIds) ? payload.templateIds : []),
      ...(Array.isArray(payload.templates) ? payload.templates : []),
      payload.templateId,
      payload.tpl,
    ]);
    const templateId = templateIds[0] || '';
    this.#meta.templateId = templateId;
    this.#meta.tpl = templateId;
    this.#meta.templateIds = templateIds;
    this.#meta.templates = templateIds;
    this.#task.templateId = templateId;
    this.#task.tpl = templateId;
    this.#task.templateIds = templateIds;
    this.#task.templates = templateIds;
  }

  /**
   * Resolve the active task-notes textarea inside the surrounding lead card.
   *
   * @returns {HTMLTextAreaElement|null} Notes textarea when mounted, otherwise `null`.
   */
  #getTaskNotesTextarea() {
    const textarea = this.#el
      .closest('.lead-card')
      ?.querySelector('#taskNotes');
    return textarea instanceof HTMLTextAreaElement ? textarea : null;
  }

  /**
   * Read the trimmed notes value for the current Focus Mode task.
   *
   * @returns {string} Trimmed textarea value, or an empty string when missing.
   */
  #getTaskNotesText() {
    return String(this.#getTaskNotesTextarea()?.value || '').trim();
  }

  /**
   * Toggle the outcome CTA row between idle and busy states to prevent duplicate clicks.
   *
   * @param {boolean} busy - Whether outcome buttons should be disabled.
   * @param {string} [actionKey=''] - Outcome key for the actively processing CTA.
   * @returns {void}
   */
  #setOutcomeActionBusy(busy, actionKey = '') {
    this.#outcomeActionBusy = busy;
    this.#busyOutcomeActionKey = busy ? String(actionKey || '') : '';
    const scope = this.#el.closest('.lead-card') || this.#el;
    const buttons = scope.querySelectorAll('[data-action-key]');
    buttons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.disabled = busy;
      if (busy && button.dataset.actionKey === this.#busyOutcomeActionKey) {
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('aria-busy');
      }
    });
  }

  /**
   * Queue note persistence so identical in-flight saves reuse the same request while
   * preserving write order for subsequent note changes.
   *
   * @param {string} text - Free-text notes collected from the task card.
   * @returns {Promise<unknown>} Save result or `null` when nothing was written.
   */
  #queueNotesSave(text) {
    const notesText = String(text ?? '').trim();
    if (!notesText) {
      return Promise.resolve(null);
    }
    if (notesText === this.#lastSavedNotesText) {
      return this.#notesSavePromise || Promise.resolve(null);
    }
    if (this.#notesSavePromise && notesText === this.#queuedNotesText) {
      return this.#notesSavePromise;
    }

    const previous = this.#notesSavePromise;
    const saveRequest = Promise.resolve(previous)
      .catch(() => null)
      .then(async () => {
        if (notesText === this.#lastSavedNotesText) {
          return null;
        }
        const result = await this.#saveNotes(notesText);
        if (result) {
          this.#lastSavedNotesText = notesText;
        }
        return result;
      });

    const trackedRequest = saveRequest.finally(() => {
      if (this.#notesSavePromise === trackedRequest) {
        this.#notesSavePromise = null;
        this.#queuedNotesText = '';
      }
    });

    this.#queuedNotesText = notesText;
    this.#notesSavePromise = trackedRequest;
    return trackedRequest;
  }

  /**
   * Build the optional brainstorming payload for the Focus Mode email composer.
   *
   * @param {string} [templateId=''] - Active email template identifier.
   * @returns {{
   *   contactReport: string,
   *   companyReport: string,
   *   mergeVars: Record<string, unknown>,
   *   brainstormEnabled: boolean,
   *   rewriteEnabled: boolean,
   *   templateId: string,
   * }} Context payload for AI draft coaching.
   */
  #buildEmailBrainstormContext(templateId = '') {
    return {
      contactReport: String(this.#meta?.rawContactReport || ''),
      companyReport: String(this.#meta?.rawCompanyReport || ''),
      mergeVars: this.#meta?.mergeVars ?? this.#task?.mergeVars ?? {},
      brainstormEnabled:
        this.#services?.focusModeEmailBrainstormEnabled === true,
      rewriteEnabled: this.#services?.focusModeEmailRewriteEnabled === true,
      templateId: String(templateId || ''),
    };
  }

  /**
   * Build the optional composer entry point for Focus Mode step templates.
   *
   * @param {string[]} [templateIds=[]] - Current composer template ids.
   * @returns {object|null} Step-template management context.
   */
  #buildStepTemplateManagementContext(templateIds = []) {
    const templates = normaliseTemplateIdentifiers([
      ...(Array.isArray(templateIds) ? templateIds : []),
      ...(Array.isArray(this.#meta?.templates) ? this.#meta.templates : []),
      ...(Array.isArray(this.#task?.templateIds) ? this.#task.templateIds : []),
      ...(Array.isArray(this.#task?.templates) ? this.#task.templates : []),
      this.#meta?.tpl,
      this.#meta?.templateId,
      this.#task?.templateId,
      this.#task?.tpl,
    ]);
    const taskId = String(this.#task?.id || '').trim();
    if (!taskId || !templates.length) return null;

    const metadata = this.#task?.metadata || {};
    const playbookId = String(
      this.#task?.resolvedPlaybookId ||
        this.#task?.playbookId ||
        this.#meta?.resolvedPlaybookId ||
        this.#meta?.playbookId ||
        metadata?.resolvedPlaybookId ||
        metadata?.playbookId ||
        ''
    ).trim();
    const stepName = String(
      this.#meta?.name ||
        metadata?.stepDisplayName ||
        metadata?.name ||
        this.#task?.displayName ||
        this.#task?.title ||
        templates[0] ||
        'Current step'
    ).trim();
    const isTargetBacked = Boolean(
      (this.#task?.targetId || metadata?.targetId || this.#meta?.targetId) &&
        (this.#task?.targetVersionId ||
          metadata?.targetVersionId ||
          this.#meta?.targetVersionId) &&
        (this.#task?.variantKey ||
          metadata?.variantKey ||
          this.#meta?.variantKey)
    );

    return {
      taskId,
      tenantId: resolveActiveTenantId({
        tenant: this.#services?.tenant,
        auth: this.#services?.auth,
      }),
      playbookId,
      stepName,
      templateIds: templates,
      isTargetBacked,
      onSaved: (payload = {}) => {
        const nextTemplates = normaliseTemplateIdentifiers(
          payload.templateIds || payload.templates || []
        );
        this.updateTemplateSet({
          ...payload,
          templateIds: nextTemplates,
          templates: nextTemplates,
        });
      },
    };
  }

  /**
   * Resolve the call scripts configured for the current task.
   *
   * @returns {Array<{ label: string, templateId: string }>}
   */
  #getCallScripts() {
    return normaliseCallScripts(
      this.#meta?.callScripts ?? this.#task?.callScripts ?? []
    );
  }

  /**
   * Determine whether the assistive call-scripts action should be shown.
   *
   * @returns {boolean}
   */
  #hasCallScripts() {
    return this.#isCallType() && this.#getCallScripts().length > 0;
  }

  /**
   * Open the read-only call scripts modal for the active task.
   *
   * @returns {Promise<void>}
   */
  async #openCallScriptsModal() {
    const callScripts = this.#getCallScripts();
    if (!callScripts.length) return;

    const templateData =
      this.#meta?.data ??
      this.#meta?.templateData ??
      this.#task?.templateData ??
      this.#task?.data ??
      {};
    const mergeVars = this.#meta?.mergeVars ?? this.#task?.mergeVars ?? {};

    try {
      await openCallScriptModal({
        title: 'Call Scripts',
        callScripts,
        templateData,
        mergeVars,
        di: this.#services,
        modalService: this.#services?.modalService || this.#services?.modal,
        templateService: this.#services?.templateService,
        errorUI: this.#services?.errorUI,
        enableTooltips: this.#services?.focusModeTooltips !== false,
      });
    } catch (err) {
      console.error('[CallStepController] call scripts modal failed', err);
      this.#services?.errorUI?.global?.({
        text: 'Unable to load call scripts right now.',
        type: 'error',
        duration: 4000,
      });
    }
  }

  /**
   * Append the neutral Call Scripts utility action when scripts are configured.
   *
   * @returns {void}
   */
  #appendCallScriptsButton() {
    if (!this.#hasCallScripts()) return;

    const inlineButton =
      this.#el.closest('.lead-card')?.querySelector('#call-scripts-btn') ??
      this.#el.parentElement?.querySelector('#call-scripts-btn') ??
      null;
    if (inlineButton) {
      const wiredButton = inlineButton.cloneNode(true);
      wiredButton.dataset.focusTooltip =
        'Open the scripts configured for this call step.';
      wiredButton.dataset.focusTooltipPlacement = 'top';
      wiredButton.addEventListener('click', () => this.#openCallScriptsModal());
      inlineButton.replaceWith(wiredButton);
      return;
    }

    const row = document.createElement('div');
    row.className = 'focus-call-scripts-row';

    const btn = this.#makeBtn(
      'Call Script Available',
      () => this.#openCallScriptsModal(),
      'btn--grey btn--cta-sm btn--secondary-action'
    );
    btn.id = 'call-scripts-btn';
    btn.classList.add('focus-call-scripts-row__button');
    btn.dataset.focusTooltip =
      'Open the scripts configured for this call step.';
    btn.dataset.focusTooltipPlacement = 'top';
    row.appendChild(btn);
    this.#el.appendChild(row);
  }

  /**
   * Determine whether the current Focus Mode session should launch the native phone app.
   *
   * @param {string} [provider=this.#callProvider] - Provider key to evaluate.
   * @returns {boolean} True when the call flow uses the system dialer.
   */
  #usesSystemDialer(provider = this.#callProvider) {
    return provider === 'system';
  }

  /**
   * Start the temporary redial lockout after a browser call ends.
   *
   * @param {number} [durationMs=REDIAL_COOLDOWN_MS] - Cooldown length in milliseconds.
   * @returns {void}
   */
  #startRedialCooldown(durationMs = REDIAL_COOLDOWN_MS) {
    this.#clearRedialCooldown();
    this.#redialCooldownUntil = Date.now() + durationMs;
    this.#redialCooldownTimer = setTimeout(() => {
      this.#redialCooldownTimer = null;
      if (!this.#isRedialCoolingDown()) {
        this.#redialCooldownUntil = 0;
        this.#render();
      }
    }, durationMs);
  }

  /**
   * Clear any pending redial lockout timer.
   *
   * @returns {void}
   */
  #clearRedialCooldown() {
    if (this.#redialCooldownTimer) {
      clearTimeout(this.#redialCooldownTimer);
      this.#redialCooldownTimer = null;
    }
    this.#redialCooldownUntil = 0;
  }

  /**
   * Check whether accidental redial protection is currently active.
   *
   * @returns {boolean} True while redial should remain disabled.
   */
  #isRedialCoolingDown() {
    return this.#redialCooldownUntil > Date.now();
  }

  /**
   * Restore a pending native-phone call state for the active task.
   *
   * @returns {boolean} True when a saved state was restored.
   */
  #restoreSystemCallState() {
    if (!this.#usesSystemDialer()) return false;
    const pending = loadFocusModeSystemCallState();
    if (!pending || pending.taskId !== this.#task.id) return false;

    this.#lastDialed = pending.to || '';
    this.#lastDialedTo = pending.to || '';
    this.#lastDialedExtension = sanitizePhoneExtension(pending.extension || '');
    this.#showRedial = !!pending.to;
    this.#state = 'awaitingOutcome';
    return true;
  }

  /**
   * Persist a pending native-phone call for recovery after returning from the phone app.
   *
   * @param {string} to - Dialed phone number.
   * @param {string} [extension=''] - Optional phone extension.
   * @returns {void}
   */
  #persistSystemCallState(to, extension = '') {
    saveFocusModeSystemCallState({
      taskId: this.#task.id,
      to,
      extension: sanitizePhoneExtension(extension),
    });
  }

  /**
   * Clear any saved native-phone call state.
   *
   * @returns {void}
   */
  #clearSystemCallState() {
    clearFocusModeSystemCallState();
  }

  /**
   * Finalize the current step and clear any saved native-phone return state.
   *
   * @param {Record<string, unknown>} [completion={}] - Optional completion payload for the parent queue controller.
   * @returns {Promise<void>} Resolves after the parent queue finishes advancing.
   */
  async #finishTaskStep(completion = {}) {
    this.#teardownLinkedInReturnListeners();
    this.#clearSystemCallState();
    this.#clearContactLocalCallWindowPromptRetry();
    this.#closeContactLocalCallWindowModal();
    this.#contactLocalCallWindowBlock = null;
    this.#contactLocalCallWindowOverrideAccepted = false;
    await this.#onComplete(completion);
  }

  /**
   * Resolve the source playbook id carried by the task or its enrolment.
   *
   * @returns {Promise<string|null>} Canonical source playbook id.
   */
  async #resolveFollowUpSourcePlaybookId() {
    const metadata = this.#task?.metadata || {};
    const directCandidates = [
      this.#task?.resolvedPlaybookId,
      this.#task?.playbookId,
      this.#meta?.resolvedPlaybookId,
      this.#meta?.playbookId,
      metadata?.resolvedPlaybookId,
      metadata?.playbookId,
    ];
    for (const candidate of directCandidates) {
      const value = String(candidate || '').trim();
      if (value) return value;
    }

    const enrolmentId = resolveSequenceEnrolmentId(this.#task);
    if (!enrolmentId || !this.#services?.fsService?.getDocument) return null;
    try {
      const enrolment = await this.#services.fsService.getDocument(
        `SequenceEnrolments/${enrolmentId}`,
        false
      );
      return (
        String(
          enrolment?.resolvedPlaybookId || enrolment?.playbookId || ''
        ).trim() || null
      );
    } catch (err) {
      console.warn(
        '[CallStepController] unable to resolve follow-up source playbook',
        err
      );
      return null;
    }
  }

  /**
   * Add a validated next-sequence selection to a terminal outcome.
   *
   * Prompt failures never block the current task outcome; the server still
   * validates the source opt-in, destination access, and date before queuing.
   *
   * @param {Record<string, unknown>} outcomeDto - Pending outcome payload.
   * @returns {Promise<Record<string, unknown>>} Outcome with optional follow-up metadata.
   */
  async #withFollowUpEnrollment(outcomeDto = {}) {
    const existingMetadata = outcomeDto?.metadata || {};
    if (existingMetadata.followUpEnrollment) return outcomeDto;
    const fsService = this.#services?.fsService;
    const modalService =
      this.#services?.modalService || this.#services?.modal || null;
    if (!fsService?.getDocument || !fsService?.query || !modalService) {
      return outcomeDto;
    }

    try {
      const sourcePlaybookId = await this.#resolveFollowUpSourcePlaybookId();
      if (!sourcePlaybookId) return outcomeDto;
      const sourcePlaybook = await fsService.getDocument(
        `Playbooks/${sourcePlaybookId}`,
        false
      );
      if (!isFollowUpEnrollmentEnabled(sourcePlaybook)) return outcomeDto;

      const trigger = resolveFollowUpEnrollmentTrigger({
        outcome: outcomeDto.outcome,
        metadata: existingMetadata,
        currentStepIndex:
          outcomeDto.stepIndex ??
          this.#task?.stepIndex ??
          this.#task?.stepIdx ??
          this.#meta?.stepIdx,
        steps: sourcePlaybook?.steps || {},
      });
      if (!trigger) return outcomeDto;

      const userId = String(
        outcomeDto.userId ||
          this.#task?.userId ||
          this.#services?.auth?.currentUser?.uid ||
          ''
      ).trim();
      const playbooks = (await fsService.query('Playbooks')).filter(
        (playbook) => isFollowUpDestinationEligible(playbook, userId)
      );
      if (!playbooks.length) return outcomeDto;

      const selection = await openFollowUpEnrollmentModal({
        playbooks,
        trigger,
        modalService,
        minimumDate: localDateKey(),
      });
      if (!selection) return outcomeDto;

      return {
        ...outcomeDto,
        metadata: {
          ...existingMetadata,
          followUpEnrollment: selection,
        },
      };
    } catch (err) {
      console.warn(
        '[CallStepController] follow-up enrollment prompt unavailable',
        err
      );
      return outcomeDto;
    }
  }

  /**
   * Complete an outcome after applying the optional terminal enrollment flow.
   *
   * @param {Record<string, unknown>} task - Focus Mode task.
   * @param {Record<string, unknown>} outcomeDto - Outcome payload.
   * @param {Record<string, unknown>} [options={}] - Completion service options.
   * @returns {Promise<unknown>} Completion service result.
   */
  async #completeWithOutcome(task, outcomeDto, options = {}) {
    const enrichedOutcome = await this.#withFollowUpEnrollment(outcomeDto);
    return this.#services.taskCompletionService.completeWithOutcome(
      task,
      enrichedOutcome,
      options
    );
  }

  /**
   * Cancel any pending retry that is waiting to surface the call-window modal.
   *
   * @returns {void}
   */
  #clearContactLocalCallWindowPromptRetry() {
    if (!this.#contactLocalCallWindowPromptRetryHandle) return;
    clearTimeout(this.#contactLocalCallWindowPromptRetryHandle);
    this.#contactLocalCallWindowPromptRetryHandle = null;
  }

  /**
   * Determine whether a given overlay is currently visible.
   *
   * @param {string} id - Overlay element id.
   * @returns {boolean} True when the overlay is active on screen.
   */
  #isOverlayVisible(id) {
    if (typeof document === 'undefined') return false;
    const el = document.getElementById(id);
    if (!(el instanceof HTMLElement)) return false;
    if (el.classList.contains('hidden')) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    if (typeof window?.getComputedStyle === 'function') {
      const styles = window.getComputedStyle(el);
      if (styles.display === 'none' || styles.visibility === 'hidden') {
        return false;
      }
    }
    return true;
  }

  /**
   * Check whether another higher-priority Focus Mode overlay should remain on top.
   *
   * @returns {boolean} True when the loading or start overlay is still active.
   */
  #hasContactLocalCallWindowPromptBlocker() {
    const loadingVisible =
      this.#services?.loading?.isVisible?.('tasks') === true ||
      this.#isOverlayVisible('tasksSpinner');
    return loadingVisible || this.#isOverlayVisible('focusStartModal');
  }

  /**
   * Re-attempt the manual call-window prompt after blocking overlays clear.
   *
   * @param {number} [delay=180] - Retry delay in milliseconds.
   * @returns {void}
   */
  #scheduleContactLocalCallWindowPromptRetry(delay = 180) {
    this.#clearContactLocalCallWindowPromptRetry();
    this.#contactLocalCallWindowPromptRetryHandle = setTimeout(() => {
      this.#contactLocalCallWindowPromptRetryHandle = null;
      this.#promptContactLocalCallWindowOnLoad().catch((err) => {
        this.#warn('promptContactLocalCallWindowOnLoad retry failed', err);
      });
    }, delay);
  }

  /**
   * Focus the primary call CTA after a modal closes.
   *
   * @returns {void}
   */
  #focusPrimaryActionButton() {
    const primaryButton = this.#el?.querySelector?.('#call-btn');
    if (primaryButton instanceof HTMLButtonElement) {
      primaryButton.focus();
    }
  }

  /**
   * Create or reuse the manual contact-local call-window modal host.
   *
   * @returns {HTMLElement|null} Modal overlay host.
   */
  #ensureContactLocalCallWindowModalHost() {
    if (typeof document === 'undefined') return null;

    let host = document.getElementById(CONTACT_LOCAL_CALL_WINDOW_MODAL_ID);
    if (!host) {
      host = document.createElement('div');
      host.id = CONTACT_LOCAL_CALL_WINDOW_MODAL_ID;
      host.className = 'hidden modal-overlay';
      host.setAttribute('aria-hidden', 'true');
      host.setAttribute('role', 'dialog');
      document.body.appendChild(host);
      const modalService =
        this.#services?.modalService || this.#services?.modal;
      if (modalService?.register) {
        modalService.register(CONTACT_LOCAL_CALL_WINDOW_MODAL_ID, host);
      }
    }

    return host;
  }

  /**
   * Close the manual contact-local call-window modal when it is present.
   *
   * @returns {void}
   */
  #closeContactLocalCallWindowModal() {
    const host = document.getElementById(CONTACT_LOCAL_CALL_WINDOW_MODAL_ID);
    if (!host) return;

    const modalService = this.#services?.modalService || this.#services?.modal;
    if (modalService?.close) {
      modalService.close(CONTACT_LOCAL_CALL_WINDOW_MODAL_ID);
      return;
    }

    host.classList.add('hidden');
    host.setAttribute('aria-hidden', 'true');
  }

  /**
   * Return the preference payload used for contact-local call-window checks.
   *
   * @returns {{
   *   contactLocalCallWindowEnabled: boolean,
   *   contactLocalCallWindowMode: 'automatic'|'manual'|undefined,
   *   contactLocalCallWindowStart: string|undefined,
   *   contactLocalCallWindowEnd: string|undefined,
   * }} Raw preference object for the shared evaluator.
   */
  #getContactLocalCallWindowPreferencePayload() {
    return {
      contactLocalCallWindowEnabled:
        this.#services?.contactLocalCallWindowPreferences?.enabled === true,
      contactLocalCallWindowMode:
        this.#services?.contactLocalCallWindowPreferences?.mode,
      contactLocalCallWindowStart:
        this.#services?.contactLocalCallWindowPreferences?.start,
      contactLocalCallWindowEnd:
        this.#services?.contactLocalCallWindowPreferences?.end,
    };
  }

  /**
   * Build the deferral metadata for a contact-local call-window decision.
   *
   * @param {ReturnType<typeof evaluateContactLocalCallWindow>} decision - Decision payload for one task.
   * @returns {Record<string, string>} Metadata written alongside the defer marker.
   */
  #buildContactLocalCallWindowDeferMetadata(decision) {
    return {
      deferReason: 'outside_contact_local_call_window',
      contactTimeZone: decision?.contactTimeZone || '',
      contactLocalTime: decision?.contactLocalTimeLabel || '',
      contactLocalWindowStart: decision?.windowStart || '',
      contactLocalWindowEnd: decision?.windowEnd || '',
    };
  }

  /**
   * Build the same-day hold metadata for a contact-local early-window decision.
   *
   * @param {ReturnType<typeof evaluateContactLocalCallWindow>} decision - Decision payload for one task.
   * @returns {Record<string, string>} Metadata written alongside the hold marker.
   */
  #buildContactLocalCallWindowHoldMetadata(decision) {
    return {
      availabilityHoldReason: 'before_contact_local_call_window',
      contactTimeZone: decision?.contactTimeZone || '',
      contactLocalTime: decision?.contactLocalTimeLabel || '',
      contactLocalWindowStart: decision?.windowStart || '',
      contactLocalWindowEnd: decision?.windowEnd || '',
      contactLocalNextEligibleAt: decision?.nextEligibleAt || '',
      contactLocalNextEligibleTime: decision?.nextEligibleLabel || '',
    };
  }

  /**
   * Build the local queue patch used after placing a task on a same-day hold.
   *
   * @param {object} task - Focus Mode task payload.
   * @param {ReturnType<typeof evaluateContactLocalCallWindow>} decision - Decision payload for one task.
   * @returns {object} Updated task payload carrying the active hold marker.
   */
  #buildContactLocalCallWindowHoldUpdate(task, decision) {
    return {
      ...task,
      availabilityHoldBy: CONTACT_LOCAL_CALL_WINDOW_HELD_BY,
      availabilityHoldUntilAt: decision?.nextEligibleAt || '',
      availabilityHoldReason: 'before_contact_local_call_window',
      contactLocalNextEligibleAt: decision?.nextEligibleAt || '',
      contactLocalNextEligibleTime: decision?.nextEligibleLabel || '',
    };
  }

  /**
   * Determine whether a decision belongs to the same local-hours action family
   * as the currently blocked task.
   *
   * @param {ReturnType<typeof evaluateContactLocalCallWindow>} currentDecision - Latest decision for the active task.
   * @param {ReturnType<typeof evaluateContactLocalCallWindow>} candidateDecision - Decision for another queued task.
   * @returns {boolean} True when both decisions represent the same manual action.
   */
  #matchesContactLocalCallWindowAction(currentDecision, candidateDecision) {
    const targetState = currentDecision?.windowState || '';
    if (targetState === 'early') {
      return candidateDecision?.shouldHold === true;
    }
    if (targetState === 'late') {
      return candidateDecision?.shouldDefer === true;
    }
    return false;
  }

  /**
   * Collect the active blocked task plus any later visible call tasks that need
   * the same early/late contact-local call-window action.
   *
   * @param {ReturnType<typeof evaluateContactLocalCallWindow>} currentDecision - Latest decision for the active task.
   * @returns {Promise<Array<{ task: object, decision: ReturnType<typeof evaluateContactLocalCallWindow> }>>}
   *   Matching task candidates in queue order.
   */
  async #collectContactLocalCallWindowActionTasks(currentDecision) {
    const currentId = String(this.#task?.id || '').trim();
    const maybeQueue = await Promise.resolve(
      this.#services?.getVisibleQueueTasks?.() || []
    );
    const queue = Array.isArray(maybeQueue) ? maybeQueue : [];
    const currentIndex = queue.findIndex(
      (task) => String(task?.id || '').trim() === currentId
    );
    const remainingQueue =
      currentIndex >= 0 ? queue.slice(currentIndex) : queue.slice();
    const tasksToEvaluate = remainingQueue.length
      ? remainingQueue
      : [this.#task];
    const prefs = this.#getContactLocalCallWindowPreferencePayload();
    const now = new Date();
    const deferredTasks = [];
    const seenIds = new Set();

    if (
      currentId &&
      !tasksToEvaluate.some(
        (task) => String(task?.id || '').trim() === currentId
      )
    ) {
      tasksToEvaluate.unshift(this.#task);
    }

    for (const queuedTask of tasksToEvaluate) {
      const task =
        String(queuedTask?.id || '').trim() === currentId
          ? this.#task
          : queuedTask;
      const taskId = String(task?.id || '').trim();
      if (!taskId || seenIds.has(taskId)) continue;
      seenIds.add(taskId);

      let decision = null;
      if (taskId === currentId) {
        decision = currentDecision;
      } else {
        const contactTimeZone =
          (await resolveContactTimeZone({
            task,
            services: this.#services,
          }).catch(() => null)) || null;
        if (contactTimeZone) {
          task.contactTimeZone = contactTimeZone;
        }
        decision = evaluateContactLocalCallWindow({
          taskType: task?.type,
          preferences: prefs,
          contactTimeZone,
          now,
        });
      }

      if (
        this.#matchesContactLocalCallWindowAction(currentDecision, decision)
      ) {
        deferredTasks.push({ task, decision });
      }
    }

    return deferredTasks;
  }

  /**
   * Persist contact-local call-window deferrals for the supplied tasks.
   *
   * @param {Array<{ task: object, decision: ReturnType<typeof evaluateContactLocalCallWindow> }>} blockedTasks - Tasks to defer.
   * @returns {Promise<string[]>} Identifiers successfully deferred to tomorrow.
   */
  async #deferQueuedContactLocalCallWindowTasks(blockedTasks) {
    if (!Array.isArray(blockedTasks) || blockedTasks.length === 0) return [];

    const sellerTimeZone =
      this.#services?.focusModeUserTimezone ||
      this.#services?.focusModeTaskService?.getUserTimezone?.() ||
      null;
    const fromISO =
      dateKeyInTz(new Date(), sellerTimeZone) ||
      new Date().toISOString().slice(0, 10);
    const deferredIds = [];

    for (const entry of blockedTasks) {
      const taskId = String(entry?.task?.id || '').trim();
      if (!taskId || deferredIds.includes(taskId)) continue;
      const result =
        (await this.#services?.focusModeTaskService?.deferTasksToTomorrow?.(
          [entry.task],
          {
            fromISO,
            tz: sellerTimeZone,
            deferredBy: CONTACT_LOCAL_CALL_WINDOW_DEFERRED_BY,
            metadata: this.#buildContactLocalCallWindowDeferMetadata(
              entry.decision
            ),
          }
        )) || [];
      if (result.includes(taskId)) {
        deferredIds.push(taskId);
      }
    }

    return deferredIds;
  }

  /**
   * Persist same-day contact-local call-window holds for the supplied tasks.
   *
   * @param {Array<{ task: object, decision: ReturnType<typeof evaluateContactLocalCallWindow> }>} blockedTasks - Tasks to hold until later today.
   * @returns {Promise<string[]>} Identifiers successfully placed on hold.
   */
  async #holdQueuedContactLocalCallWindowTasks(blockedTasks) {
    if (!Array.isArray(blockedTasks) || blockedTasks.length === 0) return [];

    const heldIds =
      (await this.#services?.focusModeTaskService?.holdTasksUntil?.(
        blockedTasks.map((entry) => ({
          task: entry.task,
          holdUntilAt: entry.decision?.nextEligibleAt || '',
          metadata: this.#buildContactLocalCallWindowHoldMetadata(
            entry.decision
          ),
        })),
        {
          holdBy: CONTACT_LOCAL_CALL_WINDOW_HELD_BY,
        }
      )) || [];

    return heldIds;
  }

  /**
   * Open the manual contact-local call-window modal for the active task.
   *
   * @param {ReturnType<typeof evaluateContactLocalCallWindow>} decision - Latest window evaluation.
   * @returns {Promise<void>}
   */
  async #openContactLocalCallWindowModal(decision) {
    if (
      (!decision?.shouldHold && !decision?.shouldDefer) ||
      decision.mode !== 'manual' ||
      !this.#isCallType() ||
      this.#state !== 'initial'
    ) {
      return;
    }

    if (this.#hasContactLocalCallWindowPromptBlocker()) {
      this.#contactLocalCallWindowBlock = decision;
      this.#scheduleContactLocalCallWindowPromptRetry();
      return;
    }

    const host = this.#ensureContactLocalCallWindowModalHost();
    if (!host) return;

    this.#clearContactLocalCallWindowPromptRetry();
    this.#contactLocalCallWindowBlock = decision;
    const matchingBlockedTasks =
      await this.#collectContactLocalCallWindowActionTasks(decision);
    const canBulkActionRemaining = matchingBlockedTasks.length > 1;
    const isEarlyWindow = decision.shouldHold === true;
    const modalTitle = isEarlyWindow
      ? 'Too early for local calling hours'
      : 'Outside local calling hours';
    const primaryActionLabel = isEarlyWindow
      ? 'Hold until local hours open'
      : 'Defer to tomorrow';
    const bulkActionLabel = isEarlyWindow
      ? 'Hold all early calls for later today'
      : 'Defer all out-of-window calls';
    const primaryBusyLabel = isEarlyWindow
      ? 'Holding for later today…'
      : 'Deferring to tomorrow…';
    const bulkBusyLabel = isEarlyWindow
      ? 'Holding remaining calls for later today…'
      : 'Deferring remaining calls…';

    host.innerHTML = /* html */ `
      <div
        class="modal-container modal-auto modal-teal focusmode-call-window-modal"
        role="document"
        aria-modal="true"
        aria-labelledby="focusModeCallWindowTitle"
      >
        <header class="focus-modal__header">
          <div class="focus-modal__title-group">
            <h2 id="focusModeCallWindowTitle">${modalTitle}</h2>
          </div>
        </header>
        <p class="focusmode-call-window-modal__copy">
          Current local time:
          <strong>${decision.contactLocalTimeLabel || 'Unavailable'}${
            decision.contactTimeZone ? ` (${decision.contactTimeZone})` : ''
          }</strong>
        </p>
        <p class="focusmode-call-window-modal__copy">
          Allowed window:
          <strong>${decision.windowLabel}</strong>
        </p>
        ${
          isEarlyWindow
            ? `<p class="focusmode-call-window-modal__copy">
          Calling opens:
          <strong>${decision.nextEligibleLabel || decision.windowStart}${
            decision.contactTimeZone ? ` (${decision.contactTimeZone})` : ''
          }</strong>
        </p>`
            : ''
        }
        <div class="focusmode-call-window-modal__actions">
          ${
            canBulkActionRemaining
              ? `
          <button
            id="focusModeCallWindowBulkActionBtn"
            type="button"
            class="btn-pill btn--orange btn--info-sm btn--primary-action focusmode-call-window-modal__bulk-action"
          >
            ${bulkActionLabel}
          </button>`
              : ''
          }
          <footer class="focus-modal__footer">
            <div class="focus-modal__footer-actions" data-actions="2">
              <button
                id="focusModeCallWindowProceedBtn"
                type="button"
                class="btn-pill btn--grey btn--info-sm btn--secondary-action focusmode-call-window-modal__action"
              >
                Call Anyway
              </button>
              <button
                id="focusModeCallWindowPrimaryActionBtn"
                type="button"
                class="btn-pill btn--orange btn--info-sm btn--primary-action focusmode-call-window-modal__action"
              >
                ${primaryActionLabel}
              </button>
            </div>
          </footer>
        </div>
      </div>
    `;

    const bulkActionBtn = host.querySelector(
      '#focusModeCallWindowBulkActionBtn'
    );
    const primaryActionBtn = host.querySelector(
      '#focusModeCallWindowPrimaryActionBtn'
    );
    const proceedBtn = host.querySelector('#focusModeCallWindowProceedBtn');

    const setBusy = (busy) => {
      [bulkActionBtn, primaryActionBtn, proceedBtn].forEach((button) => {
        if (button instanceof HTMLButtonElement) {
          button.disabled = busy;
        }
      });
    };

    if (bulkActionBtn instanceof HTMLButtonElement) {
      bulkActionBtn.onclick = async () => {
        try {
          setBusy(true);
          this.#services?.loading?.showAfterDelay('tasks', 200, bulkBusyLabel);
          const blockedTasks =
            await this.#collectContactLocalCallWindowActionTasks(
              this.#contactLocalCallWindowBlock || decision
            );
          if (blockedTasks.length === 0) {
            this.#contactLocalCallWindowBlock = null;
            this.#closeContactLocalCallWindowModal();
            this.#focusPrimaryActionButton();
            return;
          }
          if (isEarlyWindow) {
            const heldIds =
              await this.#holdQueuedContactLocalCallWindowTasks(blockedTasks);
            if (!heldIds.includes(this.#task.id)) {
              throw new Error('Could not hold this task for later today.');
            }
            if (heldIds.length < blockedTasks.length) {
              this.#services?.errorUI?.global?.({
                text: 'Some remaining early calls could not be held for later.',
                type: 'warning',
                duration: 4000,
              });
            }
            this.#contactLocalCallWindowBlock = null;
            await this.#finishTaskStep({
              heldTaskUpdates: blockedTasks
                .filter((entry) => heldIds.includes(entry.task?.id))
                .map((entry) =>
                  this.#buildContactLocalCallWindowHoldUpdate(
                    entry.task,
                    entry.decision
                  )
                ),
            });
            return;
          }
          const deferredIds =
            await this.#deferQueuedContactLocalCallWindowTasks(blockedTasks);
          if (!deferredIds.includes(this.#task.id)) {
            throw new Error('Could not defer this task to tomorrow.');
          }
          if (deferredIds.length < blockedTasks.length) {
            this.#services?.errorUI?.global?.({
              text: 'Some remaining out-of-window calls could not be deferred.',
              type: 'warning',
              duration: 4000,
            });
          }
          this.#contactLocalCallWindowBlock = null;
          await this.#finishTaskStep({ deferredTaskIds: deferredIds });
        } catch (err) {
          this.#services?.errorUI?.global?.({
            text: err?.message || 'Unable to defer these tasks right now.',
            type: 'error',
            duration: 4000,
          });
          setBusy(false);
        } finally {
          this.#services?.loading?.hide?.('tasks');
        }
      };
    }

    if (primaryActionBtn instanceof HTMLButtonElement) {
      primaryActionBtn.onclick = async () => {
        try {
          setBusy(true);
          this.#services?.loading?.showAfterDelay(
            'tasks',
            200,
            primaryBusyLabel
          );
          if (isEarlyWindow) {
            await this.#holdForContactLocalCallWindow(
              this.#contactLocalCallWindowBlock || decision
            );
            return;
          }
          await this.#deferForContactLocalCallWindow(
            this.#contactLocalCallWindowBlock || decision
          );
        } catch (err) {
          this.#services?.errorUI?.global?.({
            text:
              err?.message ||
              (isEarlyWindow
                ? 'Unable to hold this task for later today.'
                : 'Unable to defer this task right now.'),
            type: 'error',
            duration: 4000,
          });
          setBusy(false);
        } finally {
          this.#services?.loading?.hide?.('tasks');
        }
      };
    }

    if (proceedBtn instanceof HTMLButtonElement) {
      proceedBtn.onclick = async () => {
        try {
          setBusy(true);
          this.#contactLocalCallWindowOverrideAccepted = true;
          this.#closeContactLocalCallWindowModal();
          this.#focusPrimaryActionButton();
        } finally {
          setBusy(false);
        }
      };
    }

    const modalService = this.#services?.modalService || this.#services?.modal;
    if (modalService?.open) {
      modalService.open(CONTACT_LOCAL_CALL_WINDOW_MODAL_ID);
      return;
    }

    host.classList.remove('hidden');
    host.setAttribute('aria-hidden', 'false');
    primaryActionBtn?.focus?.();
  }

  /**
   * Prompt for a manual contact-local call-window decision as soon as the task loads.
   *
   * @returns {Promise<void>}
   */
  async #promptContactLocalCallWindowOnLoad() {
    if (
      this.#state !== 'initial' ||
      !this.#isCallType() ||
      this.#contactLocalCallWindowOverrideAccepted
    ) {
      this.#clearContactLocalCallWindowPromptRetry();
      return;
    }

    const decision = await this.#evaluateContactLocalCallWindow();
    if (
      (!decision.shouldHold && !decision.shouldDefer) ||
      decision.mode !== 'manual'
    ) {
      this.#clearContactLocalCallWindowPromptRetry();
      this.#contactLocalCallWindowBlock = null;
      this.#closeContactLocalCallWindowModal();
      return;
    }

    if (this.#hasContactLocalCallWindowPromptBlocker()) {
      this.#contactLocalCallWindowBlock = decision;
      this.#closeContactLocalCallWindowModal();
      this.#scheduleContactLocalCallWindowPromptRetry();
      return;
    }

    await this.#openContactLocalCallWindowModal(decision);
  }

  /**
   * Evaluate the contact-local call window for the active task.
   *
   * @returns {Promise<ReturnType<typeof evaluateContactLocalCallWindow>>}
   */
  async #evaluateContactLocalCallWindow() {
    const contactTimeZone =
      (await resolveContactTimeZone({
        task: this.#task,
        services: this.#services,
      }).catch(() => null)) || null;
    if (contactTimeZone) {
      this.#task.contactTimeZone = contactTimeZone;
    }

    return evaluateContactLocalCallWindow({
      taskType: this.#task?.type,
      preferences: this.#getContactLocalCallWindowPreferencePayload(),
      contactTimeZone,
    });
  }

  /**
   * Defer the active task because the contact is outside local calling hours.
   *
   * @param {ReturnType<typeof evaluateContactLocalCallWindow>} decision - Latest window evaluation.
   * @returns {Promise<void>}
   */
  async #deferForContactLocalCallWindow(decision) {
    const deferredIds = await this.#deferQueuedContactLocalCallWindowTasks([
      { task: this.#task, decision },
    ]);
    if (!deferredIds.includes(this.#task.id)) {
      throw new Error('Could not defer this task to tomorrow.');
    }
    this.#contactLocalCallWindowBlock = null;
    await this.#finishTaskStep({ deferredTaskIds: deferredIds });
  }

  /**
   * Hold the active task until the contact's local calling window opens later today.
   *
   * @param {ReturnType<typeof evaluateContactLocalCallWindow>} decision - Latest window evaluation.
   * @returns {Promise<void>}
   */
  async #holdForContactLocalCallWindow(decision) {
    const heldIds = await this.#holdQueuedContactLocalCallWindowTasks([
      { task: this.#task, decision },
    ]);
    if (!heldIds.includes(this.#task.id)) {
      throw new Error('Could not hold this task for later today.');
    }
    this.#contactLocalCallWindowBlock = null;
    await this.#finishTaskStep({
      heldTaskUpdates: [
        this.#buildContactLocalCallWindowHoldUpdate(this.#task, decision),
      ],
    });
  }

  /**
   * Resolve the best LinkedIn profile URL for the active task.
   *
   * @returns {string} Normalized LinkedIn URL or an empty string.
   */
  #resolveLinkedInProfileUrl() {
    const anchorLink = this.#el
      .closest('.lead-card')
      ?.querySelector('.linkedin-link')?.href;
    const metaLink =
      typeof this.#meta?.linkedinUrl === 'string'
        ? this.#meta.linkedinUrl.trim()
        : '';
    const rawLink = (metaLink || anchorLink || '').trim();
    return rawLink ? normaliseUrl(rawLink) : '';
  }

  /**
   * Lazily create the shared LinkedIn activity modal used by LINKEDIN_ACTIVITY.
   *
   * @returns {LinkedInModal|null} Modal instance when the host exists.
   */
  #getLinkedInActivityModal() {
    if (this.#linkedinActivityModal) return this.#linkedinActivityModal;
    try {
      this.#linkedinActivityModal = new LinkedInModal({
        rootId: 'linkedinModal',
      });
      return this.#linkedinActivityModal;
    } catch (err) {
      console.warn(
        '[CallStepController] LinkedIn activity modal unavailable',
        err
      );
      return null;
    }
  }

  /**
   * Remove any pending LinkedIn return listeners and reset their state.
   *
   * @returns {void}
   */
  #teardownLinkedInReturnListeners() {
    if (this.#linkedinVisibilityHandler) {
      document.removeEventListener(
        'visibilitychange',
        this.#linkedinVisibilityHandler
      );
      this.#linkedinVisibilityHandler = null;
    }
    if (this.#linkedinWindowFocusHandler) {
      window.removeEventListener('focus', this.#linkedinWindowFocusHandler);
      this.#linkedinWindowFocusHandler = null;
    }
    this.#linkedinAwaitingReturn = false;
    this.#linkedinReturnSeenHidden = false;
  }

  /**
   * Start watching for the seller to return from LinkedIn.
   *
   * @returns {void}
   */
  #armLinkedInReturnListeners() {
    this.#teardownLinkedInReturnListeners();
    if (!this.#isLinkedInActivityTask()) return;

    this.#linkedinAwaitingReturn = true;
    this.#linkedinReturnSeenHidden = false;

    this.#linkedinVisibilityHandler = () => {
      if (!this.#linkedinAwaitingReturn || this.#linkedinModalOpen) return;
      if (document.visibilityState === 'hidden') {
        this.#linkedinReturnSeenHidden = true;
        return;
      }
      if (
        document.visibilityState === 'visible' &&
        this.#linkedinReturnSeenHidden
      ) {
        void this.#openLinkedInActivityModal({ trigger: 'visibilitychange' });
      }
    };

    this.#linkedinWindowFocusHandler = () => {
      if (
        !this.#linkedinAwaitingReturn ||
        !this.#linkedinReturnSeenHidden ||
        this.#linkedinModalOpen
      ) {
        return;
      }
      void this.#openLinkedInActivityModal({ trigger: 'focus' });
    };

    document.addEventListener(
      'visibilitychange',
      this.#linkedinVisibilityHandler
    );
    window.addEventListener('focus', this.#linkedinWindowFocusHandler);
  }

  /**
   * Open the LinkedIn activity modal for LINKEDIN_ACTIVITY tasks.
   *
   * @param {{ trigger?: string }} [params={}] - Trigger metadata for debug traces.
   * @returns {Promise<boolean>} True when the modal was opened.
   */
  async #openLinkedInActivityModal({ trigger = 'manual' } = {}) {
    if (
      !this.#isLinkedInActivityTask() ||
      !this.#linkedinStarted ||
      this.#linkedinModalOpen
    ) {
      return false;
    }

    this.#teardownLinkedInReturnListeners();

    const modal = this.#getLinkedInActivityModal();
    if (!modal) {
      this.#services?.errorUI?.global?.({
        text: 'LinkedIn activity logging is unavailable right now.',
        type: 'error',
        duration: 4000,
      });
      return false;
    }

    this.#linkedinModalOpen = true;
    modal.open({
      onSave: async (actions) => {
        await this.#completeLinkedInActivityTask(actions);
        this.#linkedinModalOpen = false;
      },
      onCancel: () => {
        this.#linkedinModalOpen = false;
        this.#log('LinkedIn activity modal cancelled', { trigger });
      },
    });
    return true;
  }

  /**
   * Complete a LINKEDIN_ACTIVITY task with the selected LinkedIn actions.
   *
   * @param {string[]} actions - LinkedIn actions selected in the modal.
   * @returns {Promise<void>}
   */
  async #completeLinkedInActivityTask(actions = []) {
    const linkedinActions = Array.isArray(actions)
      ? actions.map((value) => String(value || '').trim()).filter(Boolean)
      : [];
    const action = linkedinActions[0] || null;
    if (!action) {
      throw new Error('Select at least one LinkedIn action.');
    }

    const notesText = this.#getTaskNotesText();
    if (notesText) {
      void this.#queueNotesSave(notesText);
    }

    const tenantId = await this.#resolveTenantId();
    const contactId =
      this.#task.contactId ||
      this.#task.associated_contact_id ||
      this.#task.contact_id ||
      null;
    await this.#completeWithOutcome(
      this.#task,
      {
        taskId: this.#task.id,
        userId: this.#task.userId ?? this.#services.auth?.currentUser?.uid,
        outcome: 'TASK_COMPLETE',
        action,
        channel: 'LINKEDIN',
        sequenceId: this.#task.sequenceId || this.#task.enrolId,
        stepIndex: this.#task.stepIdx,
        metadata: {
          stepType: 'LINKEDIN_ACTIVITY',
          contactId,
          linkedinActions,
        },
      },
      { tenantIdOverride: tenantId }
    );
    try {
      await this.#services.focusModeTaskService.completeTask(
        this.#task.id,
        null,
        {
          logOutcome: false,
          tenantIdOverride: tenantId,
        }
      );
    } catch (e) {
      console.warn('[CallStepController] mirror completion failed', {
        taskId: this.#task.id,
        e,
      });
    }
    await this.#finishTaskStep();
  }

  #e164(n) {
    return normalizePhoneNumberWithExtension(n).phone || '';
  }

  /**
   * Refresh phone numbers for the current task and rebuild the call button.
   * Only fields present on the `updates` object will be modified; absent keys
   * leave existing values intact. Strings are trimmed and empty values are
   * treated as `null`.
   *
   * @param {object} [updates={}] - Partial set of phone fields to update.
   * @param {object} [opts]
   * @param {boolean} [opts.preferUpdated=true] - If true, the next selection will
   *   prefer any numbers provided in `updates` over the previous selection (number/label).
   * @param {string[]} [opts.phoneOrder] - Latest saved sequence-scoped phone order.
   * @returns {Promise<void>}
   */
  async refreshPhones(updates = {}, opts = {}) {
    const {
      preferUpdated = true,
      persistToStaging = false,
      phoneOrderChanged = false,
      phoneOrder = undefined,
    } = opts;
    const prevOpt = this.#phoneOpts[this.#currentPhoneIdx] || null;
    const updatedNumbers = [];
    const suppress = new Set(); // tombstones for this refresh
    const stagingPayload = {};
    const phoneSelectionKey = (value) =>
      this.#e164(value) || String(value || '');

    const normaliseScalar = (value) => {
      if (value == null) return null;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed === '' ? null : trimmed;
      }
      const stringified = String(value).trim();
      return stringified === '' ? null : stringified;
    };

    const applyTextField = (key, targetKey = key) => {
      if (!Object.prototype.hasOwnProperty.call(updates, key)) return;
      const finalValue = normaliseScalar(updates[key]);
      this.#task[targetKey] = finalValue;
      stagingPayload[key] = finalValue ?? '';
    };

    this.#group('refreshPhones(start)');
    this.#log('inputs:', {
      updates,
      opts,
      prevOpt,
      currentIdx: this.#currentPhoneIdx,
    });

    if (phoneOrderChanged) {
      this.#task.focusModePhoneOrder = normalizeFocusModePhoneOrder(
        phoneOrder ?? this.#task?.focusModePhoneOrder
      );
    }

    const applyPhoneField = (key, { mirrorToPhone = false } = {}) => {
      const extKey = extensionFieldForPhoneField(key);
      const hasPhone = Object.prototype.hasOwnProperty.call(updates, key);
      const hasExtension = Object.prototype.hasOwnProperty.call(
        updates,
        extKey
      );
      if (!hasPhone && !hasExtension) return;

      let parsedExtension = '';
      if (hasPhone) {
        const finalValue = normaliseScalar(updates[key]);
        if (finalValue != null) {
          const parsed = normalizePhoneNumberWithExtension(finalValue);
          parsedExtension = parsed.extension;
          const normalized = parsed.phone || this.#e164(finalValue);
          const nextValue = normalized || parsed.baseRaw || finalValue;
          this.#task[key] = nextValue;
          if (mirrorToPhone) this.#task.phone = nextValue;
          stagingPayload[key] = nextValue;
          const selectionValue = phoneSelectionKey(nextValue);
          if (selectionValue && !updatedNumbers.includes(selectionValue)) {
            updatedNumbers.push(selectionValue);
          }
          this.#log(`update.${key} →`, {
            finalValue,
            normalized,
            extension: parsedExtension,
          });
        } else {
          this.#task[key] = null;
          if (mirrorToPhone) this.#task.phone = null;
          this.#task[extKey] = null;
          stagingPayload[key] = '';
          stagingPayload[extKey] = '';
          suppress.add(key);
        }
      }

      if (hasExtension || parsedExtension) {
        const finalExtension = hasExtension
          ? sanitizePhoneExtension(updates[extKey])
          : parsedExtension;
        this.#task[extKey] = finalExtension || null;
        stagingPayload[extKey] = finalExtension || '';
      }
    };

    applyPhoneField('mobilePhone');
    applyPhoneField('officePhone', { mirrorToPhone: true });
    applyPhoneField('mainOfficePhone');

    if (Object.prototype.hasOwnProperty.call(updates, 'phoneExtension')) {
      const finalExtension = sanitizePhoneExtension(updates.phoneExtension);
      this.#task.phoneExtension = finalExtension || null;
      stagingPayload.phoneExtension = finalExtension || '';
    }

    applyTextField('firstName');
    applyTextField('lastName');
    applyTextField('linkedinUrl');
    applyTextField('email');

    // ── Persist to ContactsStaging if requested (adds/updates + deletes) ──
    if (persistToStaging) {
      try {
        const contactId =
          this.#task.contactId ||
          this.#task.associated_contact_id ||
          this.#task.contact_id;
        const tenantId = await this.#resolveTenantId();
        if (tenantId && contactId) {
          this.#log('persistToStaging payload', {
            tenantId,
            contactId,
            updates,
          });
          const payload = { ...stagingPayload };
          if (Object.keys(payload).length) {
            await saveStagedPhones(tenantId, contactId, payload);
            this.#log('staging persisted ✔', { contactId, payload });
            if (window.firebase?.firestore) {
              const snapshot = await window.firebase
                .firestore()
                .collection('tenants')
                .doc(tenantId)
                .collection('ContactsStaging')
                .doc(contactId)
                .get({ source: 'server' });
              console.log('[FocusMode] post-write verify', snapshot.data());
            }

            if (this.#services?.contactMetaService) {
              const metaPatch = {};
              if (
                Object.prototype.hasOwnProperty.call(payload, 'mobilePhone')
              ) {
                metaPatch.mobilePhone =
                  payload.mobilePhone === '' ? null : payload.mobilePhone;
              }
              if (
                Object.prototype.hasOwnProperty.call(
                  payload,
                  'mobilePhoneExtension'
                )
              ) {
                metaPatch.mobilePhoneExtension =
                  payload.mobilePhoneExtension === ''
                    ? null
                    : payload.mobilePhoneExtension;
              }
              if (
                Object.prototype.hasOwnProperty.call(payload, 'officePhone')
              ) {
                const value =
                  payload.officePhone === '' ? null : payload.officePhone;
                metaPatch.officePhone = value;
                metaPatch.phone = value;
              }
              if (
                Object.prototype.hasOwnProperty.call(
                  payload,
                  'officePhoneExtension'
                )
              ) {
                const value =
                  payload.officePhoneExtension === ''
                    ? null
                    : payload.officePhoneExtension;
                metaPatch.officePhoneExtension = value;
                metaPatch.phoneExtension = value;
              }
              if (
                Object.prototype.hasOwnProperty.call(payload, 'mainOfficePhone')
              ) {
                metaPatch.mainOfficePhone =
                  payload.mainOfficePhone === ''
                    ? null
                    : payload.mainOfficePhone;
              }
              if (
                Object.prototype.hasOwnProperty.call(
                  payload,
                  'mainOfficePhoneExtension'
                )
              ) {
                metaPatch.mainOfficePhoneExtension =
                  payload.mainOfficePhoneExtension === ''
                    ? null
                    : payload.mainOfficePhoneExtension;
              }
              if (Object.prototype.hasOwnProperty.call(payload, 'firstName')) {
                metaPatch.firstName =
                  payload.firstName === '' ? null : payload.firstName;
              }
              if (Object.prototype.hasOwnProperty.call(payload, 'lastName')) {
                metaPatch.lastName =
                  payload.lastName === '' ? null : payload.lastName;
              }
              if (
                Object.prototype.hasOwnProperty.call(payload, 'linkedinUrl')
              ) {
                metaPatch.linkedinUrl =
                  payload.linkedinUrl === '' ? null : payload.linkedinUrl;
              }
              if (Object.prototype.hasOwnProperty.call(payload, 'email')) {
                metaPatch.email = payload.email === '' ? null : payload.email;
              }
              if (Object.keys(metaPatch).length) {
                await this.#services.contactMetaService.update(
                  contactId,
                  metaPatch
                );
              }
            }

            const merge = {};
            if (Object.prototype.hasOwnProperty.call(payload, 'mobilePhone')) {
              merge.mobilePhone =
                payload.mobilePhone === '' ? null : payload.mobilePhone;
            }
            if (
              Object.prototype.hasOwnProperty.call(
                payload,
                'mobilePhoneExtension'
              )
            ) {
              merge.mobilePhoneExtension =
                payload.mobilePhoneExtension === ''
                  ? null
                  : payload.mobilePhoneExtension;
            }
            if (Object.prototype.hasOwnProperty.call(payload, 'officePhone')) {
              const value =
                payload.officePhone === '' ? null : payload.officePhone;
              merge.officePhone = value;
              merge.phone = value;
            }
            if (
              Object.prototype.hasOwnProperty.call(
                payload,
                'officePhoneExtension'
              )
            ) {
              const value =
                payload.officePhoneExtension === ''
                  ? null
                  : payload.officePhoneExtension;
              merge.officePhoneExtension = value;
              merge.phoneExtension = value;
            }
            if (
              Object.prototype.hasOwnProperty.call(payload, 'mainOfficePhone')
            ) {
              merge.mainOfficePhone =
                payload.mainOfficePhone === '' ? null : payload.mainOfficePhone;
            }
            if (
              Object.prototype.hasOwnProperty.call(
                payload,
                'mainOfficePhoneExtension'
              )
            ) {
              merge.mainOfficePhoneExtension =
                payload.mainOfficePhoneExtension === ''
                  ? null
                  : payload.mainOfficePhoneExtension;
            }
            if (Object.prototype.hasOwnProperty.call(payload, 'firstName')) {
              merge.firstName =
                payload.firstName === '' ? null : payload.firstName;
            }
            if (Object.prototype.hasOwnProperty.call(payload, 'lastName')) {
              merge.lastName =
                payload.lastName === '' ? null : payload.lastName;
            }
            if (Object.prototype.hasOwnProperty.call(payload, 'linkedinUrl')) {
              merge.linkedinUrl =
                payload.linkedinUrl === '' ? null : payload.linkedinUrl;
            }
            if (Object.prototype.hasOwnProperty.call(payload, 'email')) {
              merge.email = payload.email === '' ? null : payload.email;
            }
            if (Object.keys(merge).length) {
              Object.assign(this.#task, merge);
              this.#meta.stagedPhones = {
                ...(this.#meta.stagedPhones || {}),
                ...merge,
              };
            }
          }
        } else {
          console.warn(
            '[FocusModeCallStep] staging persist skipped: missing tenantId/contactId',
            { tenantId, contactId }
          );
        }
      } catch (e) {
        this.#warn('staging persist failed:', e);
      }
    }

    // carry tombstones into preparePhones() so staging doesn’t re-add cleared fields
    this.#suppressStaging = suppress;

    await this.#preparePhones();
    this.#log('phoneOpts after prepare:', this.#phoneOpts);

    let nextIdx = 0;

    // ── Optional: force move off a removed active number when no new numbers were provided
    // Scenario: user deletes the active mobile (or its value disappears after preparePhones),
    // and Save didn't include a replacement number. In that case, pick the first
    // remaining number that differs from the removed one.
    const removedPrev =
      preferUpdated &&
      updatedNumbers.length === 0 &&
      !!prevOpt &&
      // previous number no longer present in the options
      this.#phoneOpts.findIndex(
        (o) => phoneSelectionKey(o.number) === phoneSelectionKey(prevOpt.number)
      ) === -1;

    if (removedPrev) {
      const alt = this.#phoneOpts.findIndex(
        (o) => phoneSelectionKey(o.number) !== phoneSelectionKey(prevOpt.number)
      );
      if (alt >= 0) {
        nextIdx = alt;
        this.#currentPhoneIdx = nextIdx;
        this.#render();
        return; // we've chosen a safe fallback; skip the rest of the selection logic
      }
    }
    if (phoneOrderChanged && this.#phoneOpts.length) {
      this.#log('select path: phoneOrderChanged');
      const prevNumber = prevOpt ? this.#e164(prevOpt.number) : '';
      const shouldSkipCurrent = this.#state !== 'initial' && !!prevNumber;
      const currentIdxInNewOrder = prevNumber
        ? this.#phoneOpts.findIndex(
            (opt) => this.#e164(opt.number) === prevNumber
          )
        : this.#currentPhoneIdx;
      const reorderedIdx = this.#phoneOpts.findIndex((opt) => {
        const normalized = this.#e164(opt.number);
        if (!normalized) return false;
        return shouldSkipCurrent ? normalized !== prevNumber : true;
      });
      if (reorderedIdx >= 0) {
        nextIdx = reorderedIdx;
        this.#log('phoneOrderChanged result', {
          nextIdx,
          currentIdxInNewOrder,
          shouldSkipCurrent,
          prevNumber,
        });
        if (shouldSkipCurrent) {
          if (currentIdxInNewOrder >= 0) {
            this.#currentPhoneIdx = currentIdxInNewOrder;
          }
          this.#queuedPhoneIdxAfterOutcome = nextIdx;
        } else {
          this.#queuedPhoneIdxAfterOutcome = null;
          this.#currentPhoneIdx = nextIdx;
        }
        this.#groupEnd();
        this.#render();
        return;
      }
    }
    const hasSequencePhoneOrder =
      Array.isArray(this.#task?.focusModePhoneOrder) &&
      this.#task.focusModePhoneOrder.length > 0;
    if (
      preferUpdated &&
      updatedNumbers.length &&
      hasSequencePhoneOrder &&
      this.#state === 'initial' &&
      this.#phoneOpts.length
    ) {
      this.#log('select path: persisted sequence order');
      nextIdx = 0;
      this.#queuedPhoneIdxAfterOutcome = null;
      this.#currentPhoneIdx = nextIdx;
      this.#groupEnd();
      this.#render();
      return;
    }
    // If we explicitly prefer the freshly updated numbers (e.g., after a Save),
    // try those first in the order they were provided.
    if (preferUpdated && updatedNumbers.length) {
      this.#log('select path: preferUpdated');
      for (const num of updatedNumbers) {
        const idx = this.#phoneOpts.findIndex(
          (opt) => phoneSelectionKey(opt.number) === num
        );
        this.#log('try match updated num', { num, idx });
        if (idx >= 0) {
          nextIdx = idx;
          break;
        }
      }
    } else if (prevOpt) {
      this.#log('select path: try prevOpt');
      const prevNumberIdx = this.#phoneOpts.findIndex(
        (opt) => this.#e164(opt.number) === this.#e164(prevOpt.number)
      );
      if (prevNumberIdx >= 0) {
        nextIdx = prevNumberIdx;
      } else {
        const labelIdx = this.#phoneOpts.findIndex(
          (opt) => opt.label === prevOpt.label
        );
        if (labelIdx >= 0) {
          nextIdx = labelIdx;
        } else {
          for (const num of updatedNumbers) {
            const idx = this.#phoneOpts.findIndex(
              (opt) => phoneSelectionKey(opt.number) === num
            );
            this.#log('fallback match updated num', { num, idx });
            if (idx >= 0) {
              nextIdx = idx;
              break;
            }
          }
        }
      }
    } else if (updatedNumbers.length) {
      this.#log('select path: no prevOpt, prefer updatedNumbers');
      for (const num of updatedNumbers) {
        const idx = this.#phoneOpts.findIndex(
          (opt) => phoneSelectionKey(opt.number) === num
        );
        this.#log('try match updated num', { num, idx });
        if (idx >= 0) {
          nextIdx = idx;
          break;
        }
      }
    }

    const chosen = this.#phoneOpts[nextIdx] || null;
    this.#log('selection result:', { nextIdx, chosen });
    this.#currentPhoneIdx = nextIdx;
    this.#groupEnd();
    this.#render();
  }

  /* ─────────── async boot sequence ─────────── */
  async #bootstrap() {
    // Ensure we have a UID before proceeding; if not, wait briefly for auth to rehydrate.
    const auth = this.#services?.auth;
    let user = null;
    if (typeof auth?.waitForLogin === 'function') {
      try {
        user = await auth.waitForLogin();
      } catch {
        /* swallow; fall back below */
      }
    }
    if (!user) user = auth?.currentUser ?? null;
    if (!user?.uid && typeof auth?.onAuthStateChanged === 'function') {
      let unsub = () => {};
      const timeout = setTimeout(() => {
        unsub();
      }, 5000);
      unsub = auth.onAuthStateChanged((u) => {
        if (u?.uid) {
          clearTimeout(timeout);
          unsub();
          // Resume bootstrap (ignore the original call)
          this.#bootstrap().catch((e) =>
            console.warn('[FocusMode] resume failed', e)
          );
        }
      });
      console.warn(
        '[FocusMode] auth not ready; deferring controller bootstrap'
      );
      return; // self-resume will run when auth is ready (or times out)
    }
    if (!user?.uid) {
      console.warn('[FocusMode] auth not ready after wait; giving up for now');
      this.#services?.errorUI?.global?.({
        text: 'Authentication required. Please sign in again.',
        type: 'error',
        duration: 4000,
      });
      window.location.assign('/index.html');
      return;
    }

    const emailP = this.#detectEmailConnected().catch((err) =>
      console.warn('[CallStep] detectEmailConnected failed', err)
    );
    const emailOptOutP = this.#loadEmailOptOut().catch((err) =>
      console.warn('[CallStep] loadEmailOptOut failed', err)
    );

    const metaP =
      this.#meta?.name !== undefined || this.#meta?.crmLess !== undefined
        ? Promise.resolve()
        : this.#fetchMeta().catch((err) =>
            console.warn('[CallStep] fetchMeta failed', err)
          );

    let twilioP;
    const usingTwilio = this.#callProvider === 'twilio';
    const twilioCanCall =
      this.#services.twilioCanCall ?? this.#services.canCall ?? false;
    const shouldInitTwilio = usingTwilio && twilioCanCall;
    if (shouldInitTwilio) {
      // Atomic, one-time assignment; allow retry on failure.
      if (!this.#services.twilioReady) {
        this.#services.twilioReady = this.#initTwilio()
          .then(() => {
            this.#services.twilioInitFailed = false;
            return true;
          })
          .catch((err) => {
            const isTransientFetchFailure =
              err?.name === 'TypeError' && err?.message === 'Failed to fetch';
            const logInitFailure = isTransientFetchFailure
              ? console.debug
              : console.warn;
            logInitFailure('[CallStep] initTwilio failed', err);
            this.#services.twilioInitFailed = true; // remember failure for UI
            this.#services.twilioReady = null; // let later controllers retry
            throw err; // keep UI disabled
          });
      }
      twilioP = this.#services.twilioReady;
    } else {
      twilioP = Promise.resolve();
    }

    await this.#loadSequencePhoneOrder().catch((err) => {
      this.#warn('loadSequencePhoneOrder failed', err);
    });
    await this.#preparePhones().catch((err) => {
      this.#warn('preparePhones failed', err);
    });
    this.#restoreSystemCallState();
    this.#render();
    await this.#promptContactLocalCallWindowOnLoad().catch((err) => {
      this.#warn('promptContactLocalCallWindowOnLoad failed', err);
    });

    // Don’t let Twilio failure abort bootstrap; UI remains disabled on failure.
    await Promise.all([emailP, emailOptOutP, twilioP.catch(() => null), metaP]);
    // Hydrate UI with updated email/meta readiness and (if successful) dialer readiness.
    this.#render();
  }

  /**
   * Load the user's email opt-out preference from Firestore.
   * When true, GO outcomes should not prompt to connect an email provider.
   * @returns {Promise<void>}
   */
  async #loadEmailOptOut() {
    this.#emailOptOut = false;
    const { auth, fsService } = this.#services;
    if (!fsService) return;

    const user = await auth?.waitForLogin?.().catch(() => auth?.currentUser);
    const uid = user?.uid ?? null;
    if (!uid) return;

    const tenantId = await this.#resolveTenantId();
    if (!tenantId) return;

    if (FocusModeCallStepController.EMAIL_OPT_OUT_CACHE.has(uid)) {
      this.#emailOptOut =
        FocusModeCallStepController.EMAIL_OPT_OUT_CACHE.get(uid) === true;
      return;
    }

    try {
      const prefDoc = await fsService.getDocument(`UserPreferences/${uid}`);
      this.#emailOptOut = prefDoc?.emailOptOut === true;
      FocusModeCallStepController.EMAIL_OPT_OUT_CACHE.set(
        uid,
        this.#emailOptOut
      );
      this.#log('emailOptOut =', this.#emailOptOut);
    } catch (err) {
      console.warn('[CallStepController] failed to load email opt-out', err);
      this.#emailOptOut = false;
    }
  }

  /**
   * Load contact meta for the current task using ContactMetaService.
   * Merges any existing template meta passed to the constructor.
   * @returns {Promise<void>}
   */
  async #fetchMeta() {
    try {
      if (
        this.#meta &&
        (this.#meta.crmLess !== undefined || this.#meta.firstName !== undefined)
      ) {
        return;
      }
      if (!this.#services?.contactMetaService) return;
      const contactId =
        this.#task.contactId ||
        this.#task.associated_contact_id ||
        this.#task.contact_id;
      if (!contactId) return;
      const user =
        this.#services.auth?.currentUser ||
        (await this.#services.auth?.waitForLogin?.().catch(() => null));
      const uid = user?.uid;
      if (!uid) return;
      const meta = await this.#services.contactMetaService.get(contactId, uid);
      this.#meta = { ...meta, ...this.#meta };
      this.#log('fetchMeta merged keys:', Object.keys(this.#meta || {}));
    } catch (err) {
      console.warn('[CallStepController] fetchMeta failed', err);
    }
  }

  async #detectEmailConnected() {
    try {
      // Require UID; if not present yet, treat as not connected without warning spam
      const user = await this.#services.auth
        ?.waitForLogin?.()
        .catch(() => null);
      const uid = user?.uid ?? this.#services.auth?.currentUser?.uid ?? null;
      if (!uid) {
        this.#emailConnected = false;
        return;
      }
      /* 1️⃣  Service check takes precedence (canonical truth) */
      let svcOK = false;
      if (typeof this.#services.emailService?.isConnected === 'function') {
        try {
          const raw = await this.#services.emailService.isConnected(uid); // 🔑 pass UID
          if (this.#d()) {
            console.debug('[DetectEmail] isConnected(uid) raw =', raw);
          }
          svcOK = !!raw;
        } catch (err) {
          console.warn('[DetectEmail] isConnected(uid) threw:', err);
        }
      }
      /* 2️⃣  Firestore fallbacks – only if still unknown */
      let flag = null;
      if (!svcOK && this.#services.fsService) {
        try {
          /* a) EmailOAuth/{uid} – any doc ⇒ connected */
          const oauthDoc = await this.#services.fsService.getDocument(
            `EmailOAuth/${uid}`
          );
          if (oauthDoc) flag = true;
          /* b) Users/{uid}.emailConnected – legacy support */
          if (!flag) {
            const userDoc = await this.#services.fsService.getDocument(
              `Users/${uid}`
            );
            flag = userDoc?.emailConnected ?? null;
          }
        } catch (err) {
          console.warn('[DetectEmail] Firestore fallbacks failed:', err);
        }
      }
      /* 3️⃣  Resolved truth */
      this.#emailConnected = svcOK || !!flag;
    } catch (err) {
      console.error('[DetectEmail] unexpected error:', err);
      this.#emailConnected = false;
    }
    this.#log('emailConnected =', this.#emailConnected);
  }

  /**
   * Load any sequence-scoped phone-order override for the active enrolment.
   *
   * @returns {Promise<void>}
   */
  async #loadSequencePhoneOrder() {
    if (this.#sequencePhoneOrderLoaded) return;
    this.#sequencePhoneOrderLoaded = true;

    if (this.#task?.focusModePhoneOrder) {
      this.#task.focusModePhoneOrder = normalizeFocusModePhoneOrder(
        this.#task.focusModePhoneOrder
      );
      return;
    }

    const sequenceId = resolveSequenceEnrolmentId(this.#task);
    if (!sequenceId) return;

    try {
      const sequenceDoc = await this.#services?.fsService
        ?.getDocument?.(`SequenceEnrolments/${sequenceId}`, false)
        .catch(() => null);
      if (sequenceDoc?.focusModePhoneOrder) {
        this.#task.focusModePhoneOrder = normalizeFocusModePhoneOrder(
          sequenceDoc.focusModePhoneOrder
        );
      }
    } catch (err) {
      this.#warn('sequence phone-order load failed', err);
    }
  }

  async #preparePhones() {
    const opts = [];
    this.#group('preparePhones');
    const tomb = this.#suppressStaging || new Set();
    const phoneOrder = normalizeFocusModePhoneOrder(
      this.#task?.focusModePhoneOrder
    );
    const add = (num, label, extension = '') => {
      if (!num) return;
      const raw = String(num).trim();
      const parsed = normalizePhoneNumberWithExtension(raw);
      const normalized =
        parsed.phone || this.#e164(raw) || parsed.baseRaw || raw;
      const normalizedExtension =
        sanitizePhoneExtension(extension) || parsed.extension || '';
      if (
        normalized &&
        !opts.some(
          (o) =>
            (this.#e164(o.number) || o.number) === normalized &&
            sanitizePhoneExtension(o.extension) === normalizedExtension
        )
      ) {
        // store the normalized number so dialing doesn’t need to guess
        opts.push({
          number: normalized,
          extension: normalizedExtension,
          label,
        });
        this.#log('add phone', {
          label,
          normalized,
          extension: normalizedExtension,
        });
      } else if (normalized) {
        this.#log('skip dup', {
          label,
          normalized,
          extension: normalizedExtension,
        });
      }
    };
    const taskPhoneValues = {
      mobilePhone: this.#task.mobilePhone,
      officePhone:
        this.#task.officePhone ||
        this.#task.phone ||
        this.#task.phoneNumber ||
        this.#task.contactPhone,
      mainOfficePhone: this.#task.mainOfficePhone,
    };
    const taskPhoneExtensions = {
      mobilePhone: this.#task.mobilePhoneExtension,
      officePhone:
        this.#task.officePhoneExtension ||
        this.#task.phoneExtension ||
        this.#task.extension,
      mainOfficePhone: this.#task.mainOfficePhoneExtension,
    };
    for (const key of phoneOrder) {
      add(
        taskPhoneValues[key],
        PHONE_ORDER_LABELS[key] || key,
        taskPhoneExtensions[key]
      );
    }

    const contactId =
      this.#task.contactId ||
      this.#task.associated_contact_id ||
      this.#task.contact_id;

    if (contactId) {
      const hasPreloadedStagedPhones = Object.prototype.hasOwnProperty.call(
        this.#meta || {},
        'stagedPhones'
      );
      this.#log('lookupStagedPhones → contactId', {
        contactId,
        preloaded: hasPreloadedStagedPhones,
      });
      const fs = hasPreloadedStagedPhones
        ? this.#meta.stagedPhones || {}
        : await lookupStagedPhones(contactId);
      this.#log('lookupStagedPhones result:', fs);
      // Skip staging values that the user explicitly cleared in this refresh
      for (const key of phoneOrder) {
        if (tomb.has(key)) continue;
        add(
          fs[key],
          PHONE_ORDER_LABELS[key] || key,
          fs[extensionFieldForPhoneField(key)]
        );
      }
      add(fs.phone, '', fs.phoneExtension);
    }

    // one refresh only; don’t persist tombstones across unrelated edits
    this.#suppressStaging = new Set();

    this.#phoneOpts = opts;
    this.#log('final phoneOpts:', this.#phoneOpts);
    this.#groupEnd();
  }

  /* ─────────── UI helpers ─────────── */

  #render() {
    this.#el.innerHTML = '';
    this.#el.classList.toggle('focusmode-callstep--mobile', this.#isMobileUi());
    if (this.#replySlot) this.#replySlot.innerHTML = '';
    const mobileSecondarySlot = this.#isMobileUi()
      ? (this.#el
          .closest('.lead-card')
          ?.querySelector('.focus-mobile-task__secondary-slot') ?? null)
      : null;
    if (mobileSecondarySlot) {
      mobileSecondarySlot.innerHTML = '';
    }

    const isReplyBanner =
      this.#meta.replyReceived &&
      this.#state === 'initial' &&
      !this.#replyDismissed;

    if (this.#seqMetaMissing) {
      const warn = document.createElement('div');
      warn.className = 'sequence-warning';
      warn.textContent = 'Sequence metadata missing';
      this.#el.appendChild(warn);
    }

    /* DOM hook for behavior buttons (Pass is rendered separately in the left-column dock) */
    const row =
      this.#el.closest('.lead-card')?.querySelector('.behavior-buttons') ??
      this.#el.parentElement?.querySelector('.behavior-buttons') ??
      null;
    const leftColumn =
      this.#el.closest('.lead-card')?.querySelector('.accordion-column') ??
      this.#el.parentElement?.querySelector('.accordion-column') ??
      null;
    leftColumn?.querySelector('.focus-pass-row')?.remove();
    if (row) {
      row.classList.add('hidden');
      // Remove legacy Pass buttons from the behavior row so Pass remains a standalone CTA.
      const legacyPassButtons = Array.from(
        row.querySelectorAll('.pass-btn, .behavior-btn')
      ).filter((btn) => {
        const behaviorKey = `${btn.dataset.behavior || ''}`
          .trim()
          .toLowerCase();
        const behaviorLabel =
          `${btn.dataset.behaviorLabel || btn.textContent || ''}`
            .trim()
            .toLowerCase();
        return (
          btn.classList.contains('pass-btn') ||
          behaviorKey === 'pass' ||
          behaviorLabel === 'pass'
        );
      });
      legacyPassButtons.forEach((btn) => btn.remove());
    }

    /* 0️⃣  optional “Reply received” banner (non-blocking) */
    if (isReplyBanner && this.#replySlot) {
      const div = document.createElement('div');
      div.className = 'reply-banner';
      div.setAttribute(
        'data-focus-tooltip',
        'Detected a recent reply from this sequence. Review the snippet before logging your outcome.'
      );
      const text = document.createElement('span');
      text.className = 'reply-banner__text';
      text.textContent = this.#meta.replySnippet || 'Reply received';
      div.appendChild(text);
      const dismissBtn = document.createElement('button');
      dismissBtn.type = 'button';
      dismissBtn.className = 'reply-banner__dismiss';
      dismissBtn.textContent = 'Dismiss';
      dismissBtn.setAttribute('aria-label', 'Dismiss reply flag');
      dismissBtn.setAttribute(
        'data-focus-tooltip',
        'Hide this reply flag. Future replies will still appear.'
      );
      dismissBtn.setAttribute('data-focus-tooltip-placement', 'top');
      dismissBtn.addEventListener('click', async () => {
        const replyTaskId = this.#meta.replyTaskId || this.#task.id;
        this.#replyDismissed = true;
        this.#render();
        try {
          await this.#services.taskReplyService?.acknowledge(replyTaskId);
        } catch (err) {
          console.error('[CallStepController] dismiss reply flag failed', err);
          this.#replyDismissed = false;
          this.#render();
          this.#services.errorUI?.global?.({
            text: 'Could not dismiss reply flag. Please retry.',
            type: 'error',
            duration: 3000,
          });
        }
      });
      div.appendChild(dismissBtn);
      this.#replySlot.appendChild(div);
    }

    /* 1️⃣  primary CTA */
    if (this.#state === 'initial') {
      /* Show CALL button only */
      this.#spec = this.#primarySpec();
      const { label, colour } = this.#spec;
      const callBtn = this.#makeBtn(
        label,
        (e) => this.handlePrimary(e),
        `${colour} btn--cta-primary btn--primary-action`
      );
      callBtn.id = 'call-btn';
      const activeNum =
        (this.#phoneOpts[this.#currentPhoneIdx] || {}).number || '';
      const activeExtension = sanitizePhoneExtension(
        (this.#phoneOpts[this.#currentPhoneIdx] || {}).extension || ''
      );
      callBtn.dataset.phone = activeNum;
      callBtn.dataset.extension = activeExtension;
      const providerLabel =
        this.#callProvider === 'zoom'
          ? 'Zoom Phone'
          : this.#usesSystemDialer()
            ? 'phone'
            : 'Twilio';
      const contactName = this.#meta?.name?.trim() || 'this contact';
      let primaryTooltip = 'Complete this step to move to the next task.';
      if (this.#isCallType(this.#spec.type)) {
        const numCopy = activeNum ? ` to ${activeNum}` : '';
        const extCopy = activeExtension ? ` ext. ${activeExtension}` : '';
        primaryTooltip =
          `Start a ${providerLabel} call${numCopy}${extCopy}.`.trim();
      } else if (this.#spec.type === 'EMAIL') {
        primaryTooltip = `Send an email to ${contactName}.`;
      } else if (this.#isLinkedInTask(this.#spec.type)) {
        primaryTooltip = this.#linkedinStarted
          ? this.#spec.type === 'LINKEDIN_MESSAGE'
            ? 'Mark this LinkedIn message as sent and log the outcome.'
            : 'Complete this LinkedIn step and log the outcome.'
          : `Outreach to ${contactName} on LinkedIn.`;
      } else if (
        this.#spec.type === 'TEXT' ||
        this.#spec.type === 'TEXT_MESSAGE'
      ) {
        primaryTooltip = this.#smsStarted
          ? 'Complete this text step and log the outcome.'
          : `Send a text to ${contactName}.`;
      }
      this.#setFocusTooltip(callBtn, primaryTooltip);
      callBtn.dataset.focusTooltipPlacement = 'top';
      this.#log('render primary CTA', {
        phoneOpts: this.#phoneOpts,
        currentIdx: this.#currentPhoneIdx,
        activeNum,
        activeExtension,
      });
      const twilioReady = this.#services.twilioReady;
      const usingTwilio = this.#callProvider === 'twilio';
      if (this.#isCallType(this.#spec.type) && !this.#services.canCall) {
        callBtn.disabled = true;
        this.#setFocusTooltip(callBtn, 'Calling temporarily unavailable');
      } else if (
        this.#isCallType(this.#spec.type) &&
        usingTwilio &&
        twilioReady &&
        typeof twilioReady.then === 'function'
      ) {
        callBtn.disabled = true;
        this.#setFocusTooltip(callBtn, 'Preparing dialer…');
        const spin = document.createElement('span');
        spin.className = 'spinner-inline';
        callBtn.appendChild(spin);
        twilioReady
          .then(() => {
            if (callBtn.isConnected) {
              callBtn.disabled = false;
              this.#setFocusTooltip(callBtn, primaryTooltip);
              spin.remove();
            }
          })
          .catch(() => {
            if (callBtn.isConnected) {
              this.#setFocusTooltip(callBtn, 'Dialer failed to initialize');
              spin.remove();
            }
          });
      } else if (
        this.#isCallType(this.#spec.type) &&
        usingTwilio &&
        this.#services.twilioInitFailed
      ) {
        // Keep disabled after failure on subsequent renders
        callBtn.disabled = true;
        this.#setFocusTooltip(callBtn, 'Dialer failed to initialize');
      }

      this.#el.appendChild(callBtn);
      const passBtn = this.#makeBtn(
        'Pass',
        () => this.#handlePass(),
        'btn--grey btn--info-sm pass-btn btn--primary-action'
      );
      passBtn.classList.remove(
        'behavior-btn',
        'behavior-btn--primary',
        'behavior-btn--secondary',
        'behavior-btn--tertiary',
        'ui-select-surface'
      );
      passBtn.dataset.focusTooltip =
        'Open pass reasons to skip this step or end the sequence.';
      passBtn.dataset.focusTooltipPlacement = 'top';
      if (leftColumn) {
        const passRow = document.createElement('div');
        passRow.className = 'focus-pass-row';
        passRow.appendChild(passBtn);
        if (row && row.parentElement === leftColumn) {
          leftColumn.insertBefore(passRow, row.nextSibling);
        } else {
          leftColumn.appendChild(passRow);
        }
      } else {
        this.#el.appendChild(passBtn);
      }
      const canChooseProvider =
        this.#isCallType(this.#spec.type) &&
        !this.#usesSystemDialer() &&
        this.#isZoomEnabled() &&
        this.#defaultCallProvider !== 'zoom' &&
        !this.#callTextCompleteReady;
      if (canChooseProvider) {
        const twilioAllowed =
          this.#services.twilioCanCall ?? this.#services.canCall ?? false;
        const selector = document.createElement('select');
        selector.id = 'call-provider-select';
        selector.className = 'call-provider-select';
        const providers = [
          { value: 'twilio', label: 'Twilio' },
          { value: 'zoom', label: 'Zoom Phone' },
        ];
        providers.forEach(({ value, label }) => {
          const opt = document.createElement('option');
          opt.value = value;
          opt.textContent = label;
          opt.selected = this.#callProvider === value;
          if (value === 'twilio' && !twilioAllowed) {
            opt.disabled = true;
          }
          selector.appendChild(opt);
        });
        selector.addEventListener('change', (e) => {
          const next = e.target?.value || 'twilio';
          this.#setCallProvider(next);
        });
        const wrapper = document.createElement('div');
        wrapper.className = 'call-provider-wrap';
        const labelNode = document.createElement('label');
        labelNode.textContent = 'Call via';
        labelNode.setAttribute('for', 'call-provider-select');
        wrapper.appendChild(labelNode);
        wrapper.appendChild(selector);
        this.#el.appendChild(wrapper);
      }
      if (this.#isCallType(this.#spec.type) && !this.#services.canCall) {
        const note = document.createElement('div');
        note.className = 'call-disabled-msg';
        note.textContent =
          'Calling is temporarily unavailable. We’ll keep checking; if it continues, contact your administrator.';
        this.#el.appendChild(note);
      }
    }

    if (this.#state === 'noResp') {
      const panel = document.createElement('div');
      panel.className = 'focus-mobile-noresp';

      const title = document.createElement('p');
      title.className = 'focus-mobile-noresp__title';
      title.textContent = 'No Response';
      panel.appendChild(title);

      const buttonGroup = document.createElement('div');
      buttonGroup.className = 'focus-mobile-noresp__actions';
      MOBILE_NO_RESP_REASONS.forEach((reason) => {
        const btn = this.#makeBtn(
          reason.label,
          async () => {
            if (reason.useModal) {
              const modalResp = await openNoRespModal({
                hasAnotherLine:
                  this.#currentPhoneIdx < this.#phoneOpts.length - 1,
                enableTooltips: this.#services?.focusModeTooltips !== false,
              });
              if (!modalResp) return;
              await this.handleSecondary('NO_RESP', modalResp);
              return;
            }
            await this.handleSecondary('NO_RESP', {
              option: reason.value,
              action: reason.action,
              nextTouch: '',
              deleteContactInfo: false,
              metadata: { noRespReason: reason.value },
            });
          },
          'btn--teal btn--cta-sm btn--primary-action focus-mobile-noresp__button'
        );
        btn.dataset.noRespOption = reason.value;
        buttonGroup.appendChild(btn);
      });
      panel.appendChild(buttonGroup);

      const backBtn = this.#makeBtn(
        'Go Back',
        () => {
          this.#state = 'awaitingOutcome';
          this.#render();
        },
        'btn--grey btn--cta-sm btn--primary-action focus-mobile-noresp__back'
      );
      panel.appendChild(backBtn);
      this.#el.appendChild(panel);
    }

    /* 1️⃣-b  extra CTAs when banner is shown */
    if (isReplyBanner) {
      // Older UI showed "End Sequence" & "Skip" buttons here but
      // product requirements changed. These additional CTAs have been
      // removed so the banner only displays informational text.
    }

    /* 1️⃣-c  hide behavior buttons before engaging */
    if (row) {
      row
        .querySelectorAll('.behavior-btn:not(.pass-btn)')
        .forEach((b) => b.classList.add('hidden'));
    }

    /* 2️⃣  secondary CTAs (CALL only, after a call is in progress) */
    if (this.#state === 'awaitingOutcome') {
      const isMobileUi = this.#isMobileUi();
      /* — Row A: the three behaviour CTAs — */
      const rowA = document.createElement('div');
      rowA.className = 'cta-row';

      const secondaryCtas = [
        {
          label: 'GO',
          actionKey: 'GO',
          className: 'btn--orange',
          tooltip: 'Positive outcome: log GO and move to the next task.',
        },
        {
          label: 'No Resp',
          actionKey: 'NO_RESP',
          className: 'btn--teal',
          tooltip:
            'No response: capture voicemail or follow-up details before moving on.',
        },
        {
          label: 'No Go',
          actionKey: 'NO_GO',
          className: 'btn--grey',
          tooltip: 'Mark the prospect as not moving forward on this sequence.',
        },
      ];
      secondaryCtas.forEach(({ label: lbl, actionKey, className, tooltip }) => {
        const btn = this.#makeBtn(
          lbl,
          () => this.handleSecondary(actionKey),
          `${className} btn--cta-sm btn--primary-action`
        );
        btn.dataset.actionKey = actionKey;
        btn.dataset.focusTooltip = tooltip;
        btn.dataset.focusTooltipPlacement = 'top';
        rowA.appendChild(btn);
      });

      /* — Row B: Dialpad  +  Hang-Up (stacked vertically) — */
      const rowB = document.createElement('div');
      rowB.className = 'cta-row stack';
      const usingSystemDialer = this.#usesSystemDialer();
      let hangBtn = null;
      if (!usingSystemDialer && !this.#showRedial) {
        hangBtn = this.#makeBtn(
          'Hang Up',
          () => this.#handleHangup(),
          'btn--red btn--cta-sm btn--primary-action'
        );
        hangBtn.id = 'hangup-btn';
        hangBtn.dataset.focusTooltip =
          'End the current call and choose the correct outcome.';
        hangBtn.dataset.focusTooltipPlacement = 'top';
      }

      let redialBtn = null;
      if (this.#showRedial) {
        const redialCoolingDown = this.#isRedialCoolingDown();
        const redialTooltip = usingSystemDialer
          ? 'Open the phone app and call the last number again.'
          : redialCoolingDown
            ? 'Redial will be available in a moment.'
            : 'Call the last number again. Allow microphone access if the browser prompts.';
        redialBtn = this.#makeBtn(
          'Redial',
          () => this.#handleRedial(),
          'btn--orange btn--cta-sm btn--primary-action'
        );
        redialBtn.id = 'redial-btn';
        redialBtn.dataset.phone = this.#lastDialed || '';
        redialBtn.dataset.focusTooltip = redialTooltip;
        redialBtn.dataset.focusTooltipPlacement = 'top';
        redialBtn.disabled = redialCoolingDown;
        redialBtn.dataset.redialCooldown = redialCoolingDown ? 'true' : 'false';
        if (redialCoolingDown) {
          redialBtn.setAttribute('aria-disabled', 'true');
          redialBtn.title = 'Redial available in a moment';
        }
      }

      if (!usingSystemDialer) {
        /* build (or reuse) the collapsible dial-pad */
        const padShell =
          document.getElementById('dialpad') ??
          (() => {
            const details = document.createElement('details');
            details.id = 'dialpad';
            details.className = 'ai-accordion';
            details.open = isMobileUi;

            const digits = [
              '1',
              '2',
              '3',
              '4',
              '5',
              '6',
              '7',
              '8',
              '9',
              '*',
              '0',
              '#',
            ];
            const letters = {
              1: '',
              2: 'ABC',
              3: 'DEF',
              4: 'GHI',
              5: 'JKL',
              6: 'MNO',
              7: 'PQRS',
              8: 'TUV',
              9: 'WXYZ',
              '*': '',
              0: '',
              '#': '',
            };

            details.innerHTML = `
              <summary>Dialpad</summary>
              <div class="dialpad-grid">
                ${digits
                  .map(
                    (d) => `
                  <button data-digit="${d}">
                    <span class="digit">${d}</span>
                    ${letters[d] ? `<span class="letters">${letters[d]}</span>` : ''}
                  </button>`
                  )
                  .join('')}
              </div>`;
            return details;
          })();

        padShell.open = isMobileUi;

        rowB.appendChild(padShell);
        if (hangBtn) {
          rowB.appendChild(hangBtn);
        }
      }
      if (redialBtn) rowB.appendChild(redialBtn); // ← appears after hangup

      const hasUtilityRow = rowB.childElementCount > 0;
      if (isMobileUi && hasUtilityRow) {
        this.#el.appendChild(rowB);
        if (mobileSecondarySlot) {
          mobileSecondarySlot.appendChild(rowA);
        } else {
          this.#el.appendChild(rowA);
        }
      } else {
        this.#el.appendChild(rowA);
        if (hasUtilityRow) {
          this.#el.appendChild(rowB);
        }
      }

      if (row) {
        const configuredButtons = Array.from(
          row.querySelectorAll('.behavior-btn:not(.pass-btn)')
        );
        if (!configuredButtons.length) {
          row.classList.add('hidden');
        } else {
          row.classList.remove('hidden');
          configuredButtons.forEach((b) => {
            const clone = b.cloneNode(true);
            clone.classList.remove('hidden');
            clone.addEventListener('click', (e) =>
              this.handleBehavior(e.currentTarget.dataset.behavior, e)
            );
            b.replaceWith(clone);
          });
        }
      }
    }

    this.#appendCallScriptsButton();

    const tooltipRoot = this.#el.closest('.lead-card') || this.#el;
    const tooltipRegions = [];
    if (this.#replySlot) tooltipRegions.push(this.#replySlot);
    if (row) tooltipRegions.push(row);
    if (mobileSecondarySlot?.childElementCount) {
      tooltipRegions.push(mobileSecondarySlot);
    }
    applyFocusModeTooltips(tooltipRoot, {
      enableTooltips: this.#services?.focusModeTooltips !== false,
      extraRegions: tooltipRegions,
    });
  }

  #makeBtn(label, onClick, extra = '') {
    const btn = document.createElement('button');
    btn.textContent = label;
    btn.className = `btn-pill ${extra}`.trim();
    btn.addEventListener('click', onClick);
    return btn;
  }

  /**
   * Initialize Twilio Voice for outbound calling.
   * Fetches an access token scoped to the current tenant and
   * wires up a refresher to keep it valid.
   *
   * @returns {Promise<void>} resolves when the client is ready
   */
  async #initTwilio() {
    const tenantId =
      this.#services.tenant?.getTenantId?.() ||
      this.#services.auth?.currentUser?.tenantId ||
      useTenant(this.#services.auth ?? {});
    const token = await fetchVoiceToken(this.#services, tenantId);

    const tokenRefresher = async () => {
      return fetchVoiceToken(this.#services, tenantId);
    };

    const edge = this.#services?.twilioEdge ?? DEFAULT_TWILIO_EDGE;
    const twilioVoice = await resolveTwilioVoiceService();
    await twilioVoice.prewarm({
      tokenProvider: tokenRefresher,
      options: { edge },
      initialToken: token,
    });
  }
  /* ─────────── Twilio v2 call event wiring ─────────── */
  #attachCallHandlers(call, meta = {}) {
    if (!call) return;
    const {
      to = '',
      contactId: providedContactId = '',
      taskId: providedTaskId = '',
      extension: rawExtension = '',
      skipClientLog = false,
    } = meta;
    const extension = sanitizePhoneExtension(rawExtension);
    const userId =
      this.#task.userId || this.#services.auth?.currentUser?.uid || '';
    const contactId =
      providedContactId ||
      this.#task.contactId ||
      this.#task.associated_contact_id ||
      this.#task.contact_id ||
      '';
    const taskId = providedTaskId || this.#task.id || '';
    let spinnerCleared = false;
    let legacyFallbackTimer = null;
    let clientCallLogged = false;
    let sidPollingTimer = null;
    let extensionSent = false;

    const stopSidPolling = () => {
      if (sidPollingTimer) {
        clearInterval(sidPollingTimer);
        sidPollingTimer = null;
      }
    };

    const startSidPolling = () => {
      if (sidPollingTimer) return;
      sidPollingTimer = setInterval(() => {
        if (clientCallLogged) {
          stopSidPolling();
          return;
        }
        void maybeLogClientCall('sid-poll');
      }, 400);
    };

    // Anchor Twilio client calls in Firestore so the status webhook can enrich them.
    const maybeLogClientCall = async (reason = 'unspecified') => {
      if (skipClientLog) return;
      if (clientCallLogged) return;
      const sidCandidates = {
        parametersCallSid: call?.parameters?.CallSid || null,
        parameterscallSid: call?.parameters?.callSid || null,
        topLevelCallSid: call?.callSid || null,
        // Some SDK builds tuck call identifiers under an outbound connection
        outboundParametersCallSid:
          call?.outboundConnection?.parameters?.CallSid || null,
        outboundParameterscallSid:
          call?.outboundConnection?.parameters?.callSid || null,
      };
      const sid = Object.values(sidCandidates).find(Boolean) || null;

      const toNumber = to || call?.parameters?.To || this.#lastDialed || '';
      console.info('[FocusModeCallStep] maybeLogClientCall', {
        reason,
        sid,
        sidCandidates,
        toNumber,
        hasParameters: !!call?.parameters,
        parameterKeys: Object.keys(call?.parameters || {}),
      });

      if (!sid) return;

      try {
        const tenantId = await this.#resolveTenantId();
        const logClientCall = this.#services.dialer?.logClientCall;
        if (typeof logClientCall !== 'function') return;
        const logResult = await logClientCall.call(this.#services.dialer, {
          callSid: sid,
          to: toNumber,
          extension: extension || undefined,
          contactId,
          userId,
          taskId,
          device: 'web',
          tenantId: tenantId || undefined,
        });
        if (!logResult) return;
        const { callSid: persistedSid } = logResult;
        clientCallLogged = true;
        stopSidPolling();
        console.info('[FocusModeCallStep] logClientCall persisted', {
          sid: persistedSid || sid,
          reason,
        });
      } catch (err) {
        console.warn('[FocusModeCallStep] logClientCall failed', {
          message: err?.message,
          sid,
          reason,
        });
      }
    };

    const cancelLegacyFallback = () => {
      if (legacyFallbackTimer) {
        clearTimeout(legacyFallbackTimer);
        legacyFallbackTimer = null;
      }
    };

    const hideSpinner = () => {
      if (spinnerCleared) return;
      spinnerCleared = true;
      cancelLegacyFallback();
      this.#services?.loading?.hide?.('tasks');
    };

    const clearExtensionTimer = () => {
      if (!this.#extensionSendTimer) return;
      clearTimeout(this.#extensionSendTimer);
      this.#extensionSendTimer = null;
    };

    const sendExtensionOnce = () => {
      if (!extension || extensionSent) return;
      extensionSent = true;
      clearExtensionTimer();
      this.#extensionSendTimer = setTimeout(() => {
        this.#extensionSendTimer = null;
        if (
          this.#activeCall !== call ||
          typeof call.sendDigits !== 'function'
        ) {
          return;
        }
        try {
          call.sendDigits(extension);
          this.#log('sent extension digits', { extension });
        } catch (err) {
          console.warn('[CallStepController] extension send failed', err);
        }
      }, 1500);
    };

    const scheduleLegacyFallback = () => {
      if (spinnerCleared) return;
      cancelLegacyFallback();
      legacyFallbackTimer = setTimeout(() => {
        legacyFallbackTimer = null;
        hideSpinner();
      }, 8000);
    };

    const bindKeypad = () => {
      this.#activeCall = call;

      const send = (d) => {
        if (this.#activeCall) {
          this.#activeCall.sendDigits(d);
          this.#flashKey(d);
        }
      };

      if (!this.#keyHandler) {
        this.#keyHandler = (e) => {
          const k = e.key;
          if (/^[0-9*#]$/.test(k) && !e.altKey && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            send(k);
          }
        };
        window.addEventListener('keydown', this.#keyHandler);
      }

      if (!this.#clickHandler) {
        this.#clickHandler = (ev) => {
          const btn = ev.target.closest('[data-digit]');
          if (btn) send(btn.dataset.digit);
        };
      }

      const padEl = this.#showDialPad();
      if (this.#dialpadEl && this.#clickHandler) {
        this.#dialpadEl.removeEventListener('click', this.#clickHandler);
      }
      if (this.#dialpadEl && this.#dialpadToggleHandler) {
        this.#dialpadEl.removeEventListener(
          'toggle',
          this.#dialpadToggleHandler
        );
      }
      if (padEl && this.#clickHandler) {
        padEl.addEventListener('click', this.#clickHandler);
      }
      if (!this.#dialpadToggleHandler) {
        this.#dialpadToggleHandler = () =>
          this.#syncMobileDialpadLayout(this.#dialpadEl);
      }
      if (padEl && this.#dialpadToggleHandler) {
        padEl.addEventListener('toggle', this.#dialpadToggleHandler);
      }
      this.#dialpadEl = padEl || null;
      this.#syncMobileDialpadLayout(this.#dialpadEl);
    };

    let callReadyRendered = false;
    const handleCallReady = () => {
      const shouldRender =
        !callReadyRendered || this.#state !== 'awaitingOutcome';
      this.#state = 'awaitingOutcome';
      if (shouldRender) {
        callReadyRendered = true;
        this.#render();
      }
      bindKeypad();
    };

    const teardownCall = () => {
      if (this.#keyHandler) {
        window.removeEventListener('keydown', this.#keyHandler);
        this.#keyHandler = null;
      }
      if (this.#dialpadEl && this.#clickHandler) {
        this.#dialpadEl.removeEventListener('click', this.#clickHandler);
      }
      if (this.#dialpadEl && this.#dialpadToggleHandler) {
        this.#dialpadEl.removeEventListener(
          'toggle',
          this.#dialpadToggleHandler
        );
      }
      if (this.#clickHandler) this.#clickHandler = null;
      this.#dialpadEl = null;
      this.#hideDialPad();
      this.#activeCall = null;
      this.#activeFocusmodeCallSid = null;

      stopSidPolling();
      hideSpinner();
      cancelLegacyFallback();
      clearExtensionTimer();
      this.#resetUi();
      this.#showRedial = !!this.#lastDialed;
      if (this.#showRedial) this.#startRedialCooldown();
      this.#render();
    };

    scheduleLegacyFallback();
    startSidPolling();
    void maybeLogClientCall('attach');

    call.on('ringing', (hasEarlyMedia = false) => {
      if (this.#d()) this.#log('call ringing', { hasEarlyMedia });
      void maybeLogClientCall('ringing');
      handleCallReady();
    });
    call.on('accept', () => {
      void maybeLogClientCall('accept');
      handleCallReady();
      sendExtensionOnce();
      hideSpinner();
    });
    call.on('connected', () => {
      void maybeLogClientCall('connected');
      handleCallReady();
      sendExtensionOnce();
      hideSpinner();
    });
    call.on('audio', (audioEl) => {
      if (this.#d()) this.#log('call audio element ready', { audioEl });
      hideSpinner();
    });
    call.on('disconnect', () => {
      teardownCall();
    });
    call.on('error', (err) => {
      console.warn('[CallStepController] call error', err);
      teardownCall();
    });
    call.on('cancel', () => {
      teardownCall();
    });
    call.on('reject', () => {
      teardownCall();
    });
    call.on('busy', () => {
      teardownCall();
    });
    call.on('timeout', () => {
      teardownCall();
    });
  }

  /* ─────────── dial-pad helpers ─────────── */
  /**
   * Sync the mobile task card with the dialpad open state.
   * Hides the step summary while the keypad is expanded so the keypad and
   * hangup/redial controls sit higher in the viewport.
   *
   * @param {HTMLDetailsElement | null} pad - The current dialpad details element.
   * @returns {void}
   */
  #syncMobileDialpadLayout(pad) {
    const card =
      pad?.closest?.('.focus-task-card--mobile') ||
      this.#el?.closest?.('.focus-task-card--mobile');
    if (!(card instanceof HTMLElement)) return;
    const steps = card.querySelector('.focus-mobile-task__steps');
    const isOpen = Boolean(this.#isMobileUi() && pad?.open);
    card.classList.toggle('focus-task-card--mobile-dialpad-open', isOpen);
    if (steps instanceof HTMLElement) {
      steps.classList.toggle('hidden', isOpen);
      steps.toggleAttribute('hidden', isOpen);
      steps.setAttribute('aria-hidden', String(isOpen));
    }
  }

  #showDialPad() {
    /* ensure the <details> element exists and match the current UI mode */
    const pad =
      this.#el?.querySelector?.('#dialpad') ||
      document.getElementById('dialpad');
    if (pad) {
      if (this.#isMobileUi()) {
        pad.open = true;
      } else {
        pad.removeAttribute('open');
      }
      this.#syncMobileDialpadLayout(pad);
      return pad;
    }
    /* shouldn’t happen – render() normally creates it,
        but keep this fallback just in case */
    this.#syncMobileDialpadLayout(null);
    return document.createElement('div'); // harmless dummy
  }

  #hideDialPad() {
    const pad =
      this.#el?.querySelector?.('#dialpad') ||
      document.getElementById('dialpad');
    if (pad) {
      pad.open = false; // collapse <details>
      this.#syncMobileDialpadLayout(pad);
      return;
    }
    this.#syncMobileDialpadLayout(null);
  }

  #flashKey(d) {
    /* quick visual feedback – optional */
    const btn = document.querySelector(`#dialpad button[data-digit="${d}"]`);
    if (!btn) return;
    btn.classList.add('active');
    setTimeout(() => btn.classList.remove('active'), 120);
  }

  #resetUi() {
    const callBtn = document.getElementById('call-btn');
    const hangBtn = document.getElementById('hangup-btn');
    if (callBtn) callBtn.disabled = false;
    if (hangBtn) hangBtn.disabled = true;
  }

  /**
   * Launch a Zoom Phone call via deeplink while seeding CallLogs intent.
   * @param {{ to: string, extension?: string, contactId?: string, taskId?: string }} params - Dial metadata.
   * @returns {Promise<void>} resolves once the deeplink is triggered.
   */
  async #startZoomCall({ to, extension = '', contactId, taskId }) {
    const loading = this.#ensureLoadingService();
    loading.show('tasks', 'Dialing…');

    try {
      const dialer =
        (await this.#services.getDialer?.('zoom')) ||
        (await this.#services.getDialer?.()) ||
        this.#services.dialer;

      await dialer?.call?.({
        to,
        extension,
        contactId,
        taskId,
        userId:
          this.#task.userId || this.#services.auth?.currentUser?.uid || '',
      });

      this.#lastDialedTo = to;
      this.#lastDialedExtension = sanitizePhoneExtension(extension);
      this.#state = 'awaitingOutcome';
      this.#render();
    } catch (err) {
      console.warn('[CallStepController] zoom call failed', err);
      throw err;
    } finally {
      loading.hide?.('tasks');
    }
  }

  async #handleHangup() {
    await this.#releaseActiveCall();
    this.#resetUi();
    this.#showRedial = !!this.#lastDialed;
    if (this.#showRedial) this.#startRedialCooldown();
    this.#render();
  }

  /**
   * Disconnect the active Focus Mode call and clean up any PSTN leg.
   *
   * This is used by both the explicit hang-up button and call-flow branches
   * like "Call Another Line", where the current call must be released before
   * the next attempt can start.
   *
   * @returns {Promise<void>}
   */
  async #releaseActiveCall() {
    const pstnSid = this.#activeFocusmodeCallSid;
    const hasDialerTerminate = !!this.#services?.dialer?.terminateFocusModeCall;

    if (this.#extensionSendTimer) {
      clearTimeout(this.#extensionSendTimer);
      this.#extensionSendTimer = null;
    }

    if (this.#activeCall) {
      try {
        this.#activeCall.disconnect();
      } catch (err) {
        console.warn('[CallStepController] disconnect failed', err);
      }
      this.#activeCall = null;
    }

    if (this.#keyHandler) {
      window.removeEventListener('keydown', this.#keyHandler);
      this.#keyHandler = null;
    }
    if (this.#dialpadEl && this.#clickHandler) {
      this.#dialpadEl.removeEventListener('click', this.#clickHandler);
    }
    if (this.#dialpadEl && this.#dialpadToggleHandler) {
      this.#dialpadEl.removeEventListener('toggle', this.#dialpadToggleHandler);
    }
    if (this.#clickHandler) this.#clickHandler = null;
    this.#dialpadEl = null;
    this.#hideDialPad();

    if (pstnSid && hasDialerTerminate) {
      try {
        await this.#services.dialer.terminateFocusModeCall({
          callSid: pstnSid,
        });
      } catch (err) {
        console.warn('[CallStepController] terminateFocusModeCall failed', err);
      }
    }

    this.#activeFocusmodeCallSid = null;
  }

  /**
   * Ensure a LoadingIndicatorService is available on the service container.
   * If one is absent (e.g. during tests or ad-hoc usage), a minimal instance
   * is created and, when possible, wired to the `#tasksSpinner` element so the
   * dial modal remains functional.
   *
   * @returns {object} loading service with `show`, `showAfterDelay`, `hide`.
   */
  #ensureLoadingService() {
    if (this.#services.loading) return this.#services.loading;
    const el =
      typeof document !== 'undefined'
        ? document.getElementById('tasksSpinner')
        : null;
    if (el) {
      const loading = new LoadingIndicatorService();
      try {
        loading.register('tasks', el);
      } catch {
        /* ignore registration errors */
      }
      this.#services.loading = loading;
      return loading;
    }
    const noop = {
      show: () => {},
      showAfterDelay: () => {},
      hide: () => {},
      isVisible: () => false,
    };
    this.#services.loading = noop;
    return noop;
  }

  /**
   * Launch a FocusMode call directly via the Twilio Client dial flow.
   * @param {{ to: string, extension?: string, contactId?: string, taskId?: string }} params - Dial target metadata.
   * @returns {Promise<void>} resolves once the client leg has been initiated.
   */
  async #startFocusModeCall({ to, extension = '', contactId, taskId }) {
    const loading = this.#ensureLoadingService();
    loading.show('tasks', 'Dialing…');

    const tenantId = await this.#resolveTenantId();
    if (!tenantId) {
      loading.hide?.('tasks');
      throw new Error('Unable to determine tenant for FocusMode dial');
    }

    const params = {
      contactId: contactId || '',
      taskId: taskId || '',
      tenantId,
      extension: sanitizePhoneExtension(extension),
      focusmode: '1',
      suppressStatus: '1',
    };

    const twilioVoice = await resolveTwilioVoiceService();
    const call = await twilioVoice.connect(to, { params });

    this.#activeFocusmodeCallSid = null;
    this.#activeCall = call;
    this.#lastDialedTo = to;
    this.#lastDialedExtension = sanitizePhoneExtension(extension);

    this.#attachCallHandlers(call, {
      to,
      extension,
      contactId,
      taskId,
    });
  }

  /**
   * Launch a native phone-app call and preserve Focus Mode state for the return trip.
   *
   * @param {{ to: string, extension?: string, contactId?: string, taskId?: string }} params - Dial target metadata.
   * @returns {Promise<void>} resolves once the phone app handoff has been attempted.
   */
  async #startSystemCall({ to, extension = '', contactId, taskId }) {
    const loading = this.#ensureLoadingService();
    loading.show('tasks', 'Opening your phone app…');

    const sanitizedExtension = sanitizePhoneExtension(extension);
    this.#persistSystemCallState(to, sanitizedExtension);
    this.#lastDialed = to;
    this.#lastDialedTo = to;
    this.#lastDialedExtension = sanitizedExtension;
    this.#showRedial = true;
    this.#state = 'awaitingOutcome';
    this.#render();

    try {
      const dialer =
        (await this.#services.getDialer?.('system')) || this.#services.dialer;
      if (typeof dialer?.call !== 'function') {
        throw new Error('System dialer unavailable');
      }

      await dialer.call({
        to,
        extension: sanitizedExtension,
        contactId,
        taskId,
        userId:
          this.#task.userId || this.#services.auth?.currentUser?.uid || '',
      });
    } catch (err) {
      this.#clearSystemCallState();
      this.#showRedial = false;
      this.#state = 'initial';
      this.#render();
      console.warn('[CallStepController] system call failed', err);
      throw err;
    } finally {
      loading.hide?.('tasks');
    }
  }

  async #handleRedial() {
    if (this.#isRedialCoolingDown()) return;
    const num = this.#lastDialed || '';
    const extension = sanitizePhoneExtension(this.#lastDialedExtension || '');
    if (num) {
      const usingSystemDialer = this.#usesSystemDialer();
      this.#noRespDetails = null;
      this.#showRedial = false;
      this.#clearRedialCooldown();
      if (!usingSystemDialer) {
        this.#render();
      }
      const loading = this.#ensureLoadingService();
      // Show immediately (no hide→show churn; prevents flicker)
      loading.show(
        'tasks',
        usingSystemDialer ? 'Opening your phone app…' : 'Dialing…'
      );
      try {
        const contactId =
          this.#task.contactId ||
          this.#task.associated_contact_id ||
          this.#task.contact_id ||
          '';
        if (this.#callProvider === 'zoom') {
          await this.#startZoomCall({
            to: num,
            extension,
            contactId,
            taskId: this.#task.id,
          });
        } else if (usingSystemDialer) {
          await this.#startSystemCall({
            to: num,
            extension,
            contactId,
            taskId: this.#task.id,
          });
        } else {
          await this.#startFocusModeCall({
            to: num,
            extension,
            contactId,
            taskId: this.#task.id,
          });
        }
      } catch (err) {
        console.warn('[CallStepController] redial failed', err);
        this.#showRedial = true;
        this.#render();
        loading.hide('tasks');
      }
    }
  }

  /** Derive STEP-TYPE → CTA label + colour. */
  #primarySpec() {
    const hasAction = !!this.#task.primaryAction;
    const hasType = !!this.#task.type;
    if (!hasAction && !hasType) {
      console.warn('[CallStepController] Task missing primaryAction and type', {
        id: this.#task.id,
      });
    }
    const explicitRaw = (this.#task.primaryAction || '').toUpperCase();
    const explicit =
      explicitRaw === 'SMS'
        ? 'TEXT'
        : explicitRaw === 'SMS_MESSAGE'
          ? 'TEXT_MESSAGE'
          : explicitRaw;
    if (explicit) {
      switch (explicit) {
        case 'EMAIL':
          return { type: 'EMAIL', label: 'Email', colour: 'btn--orange' };
        case 'CALL_EMAIL': {
          let label = 'Call + Email';
          if (this.#phoneOpts.length) {
            const opt = this.#phoneOpts[this.#currentPhoneIdx] || {};
            if (opt.label) label += ` (${opt.label})`;
          }
          return { type: 'CALL_EMAIL', label, colour: 'btn--orange' };
        }
        case 'CALL_TEXTMESSAGE': {
          if (this.#callTextCompleteReady) {
            return {
              type: 'CALL_TEXTMESSAGE_COMPLETE',
              label: 'Complete',
              colour: 'btn--orange',
            };
          }
          let label = 'Call + Text';
          if (this.#phoneOpts.length) {
            const opt = this.#phoneOpts[this.#currentPhoneIdx] || {};
            if (opt.label) label += ` (${opt.label})`;
          }
          return { type: 'CALL_TEXTMESSAGE', label, colour: 'btn--orange' };
        }
        case 'CALL': {
          let label =
            this.#isMobileUi() && this.#usesSystemDialer()
              ? 'Call Mobile'
              : 'Call';
          if (this.#phoneOpts.length) {
            const opt = this.#phoneOpts[this.#currentPhoneIdx] || {};
            if (opt.label) label += ` (${opt.label})`;
          }
          return { type: 'CALL', label, colour: 'btn--orange' };
        }
        case 'LINKEDIN':
          return {
            type: 'LINKEDIN',
            label: this.#linkedinStarted ? 'Complete' : 'Start',
            colour: 'btn--orange',
          };
        case 'LINKEDIN_ACTIVITY':
          return {
            type: 'LINKEDIN_ACTIVITY',
            label: this.#linkedinStarted ? 'Complete' : 'Start',
            colour: 'btn--orange',
          };
        case 'LINKEDIN_MESSAGE':
          return {
            type: 'LINKEDIN_MESSAGE',
            label: this.#linkedinStarted ? 'Mark Sent' : 'Start',
            colour: 'btn--orange',
          };
        case 'TEXT':
          return {
            type: 'TEXT',
            label: this.#smsStarted ? 'Complete' : 'TEXT',
            colour: 'btn--orange',
          };
        case 'TEXT_MESSAGE':
          return {
            type: 'TEXT_MESSAGE',
            label: this.#smsStarted ? 'Complete' : 'TEXT MESSAGE',
            colour: 'btn--orange',
          };
        default:
          return { type: explicit, label: 'Complete', colour: 'btn--orange' };
      }
    }

    let t = (this.#task.type || '').toUpperCase();
    if (t === 'SMS') t = 'TEXT';
    if (t === 'SMS_MESSAGE') t = 'TEXT_MESSAGE';

    if (!t) {
      const subj =
        this.#task.subject ||
        this.#task.hs_task_subject ||
        this.#task.properties?.hs_task_subject ||
        '';
      const m = /^\[([A-Z_]+)]/.exec(subj);
      if (m) t = m[1].toUpperCase();
    }

    if (!t) {
      const subj = (this.#task.subject || '').toLowerCase();
      if (subj.includes('email')) t = 'EMAIL';
      else if (subj.includes('call')) t = 'CALL';
      else t = 'TASK';
    }

    switch (t) {
      case 'EMAIL':
        return { type: 'EMAIL', label: 'Email', colour: 'btn--orange' };
      case 'CALL_EMAIL': {
        let label = 'Call + Email';
        if (this.#phoneOpts.length) {
          const opt = this.#phoneOpts[this.#currentPhoneIdx] || {};
          if (opt.label) label += ` (${opt.label})`;
        }
        return { type: 'CALL_EMAIL', label, colour: 'btn--orange' };
      }
      case 'CALL_TEXTMESSAGE': {
        if (this.#callTextCompleteReady) {
          return {
            type: 'CALL_TEXTMESSAGE_COMPLETE',
            label: 'Complete',
            colour: 'btn--orange',
          };
        }
        let label = 'Call + Text';
        if (this.#phoneOpts.length) {
          const opt = this.#phoneOpts[this.#currentPhoneIdx] || {};
          if (opt.label) label += ` (${opt.label})`;
        }
        return { type: 'CALL_TEXTMESSAGE', label, colour: 'btn--orange' };
      }
      case 'CALL': {
        let label =
          this.#isMobileUi() && this.#usesSystemDialer()
            ? 'Call Mobile'
            : 'Call';
        if (this.#phoneOpts.length) {
          const opt = this.#phoneOpts[this.#currentPhoneIdx] || {};
          if (opt.label) label += ` (${opt.label})`;
        }
        return { type: 'CALL', label, colour: 'btn--orange' };
      }
      case 'LINKEDIN':
        return {
          type: 'LINKEDIN',
          label: this.#linkedinStarted ? 'Complete' : 'Start',
          colour: 'btn--orange',
        };
      case 'LINKEDIN_ACTIVITY':
        return {
          type: 'LINKEDIN_ACTIVITY',
          label: this.#linkedinStarted ? 'Complete' : 'Start',
          colour: 'btn--orange',
        };
      case 'LINKEDIN_MESSAGE':
        return {
          type: 'LINKEDIN_MESSAGE',
          label: this.#linkedinStarted ? 'Mark Sent' : 'Start',
          colour: 'btn--orange',
        };
      case 'TEXT':
        return {
          type: 'TEXT',
          label: this.#smsStarted ? 'Complete' : 'TEXT',
          colour: 'btn--orange',
        };
      case 'TEXT_MESSAGE':
        return {
          type: 'TEXT_MESSAGE',
          label: this.#smsStarted ? 'Complete' : 'TEXT MESSAGE',
          colour: 'btn--orange',
        };
      default:
        return {
          type: 'TASK',
          label: !hasAction && !hasType ? 'Unknown Task' : 'Complete',
          colour: 'btn--orange',
        };
    }
  }

  /* ─────────── primary CTA ─────────── */
  async handlePrimary(event) {
    this.#log('handlePrimary()');
    const type = (this.#spec?.type || 'CALL').toUpperCase();
    const isCallLike = this.#isCallType(type);
    this.#log('resolved type =', type);
    const contactId =
      this.#task.contactId ||
      this.#task.associated_contact_id ||
      this.#task.contact_id;
    const loading = this.#ensureLoadingService();

    try {
      const pendingCallTextOutcome = this.#pendingCallTextOutcome;
      if (pendingCallTextOutcome) {
        loading.showAfterDelay('tasks', 200, LOGGING_OUTCOME_MESSAGE);
        const notesText = this.#getTaskNotesText();
        if (notesText && notesText !== pendingCallTextOutcome.notesText) {
          void this.#queueNotesSave(notesText);
        }
        const finalNotesText =
          notesText || pendingCallTextOutcome.notesText || null;
        const tenantId = await this.#resolveTenantId();
        await this.#completeWithOutcome(
          this.#task,
          {
            taskId: this.#task.id,
            userId: this.#task.userId ?? this.#services.auth?.currentUser?.uid,
            outcome: pendingCallTextOutcome.outcome,
            action: pendingCallTextOutcome.action,
            channel: this.#channelForOutcome(),
            behaviors: pendingCallTextOutcome.behaviors,
            sequenceId: this.#task.sequenceId || this.#task.enrolId,
            stepIndex: this.#task.stepIdx,
            metadata: pendingCallTextOutcome.metadata,
            ...(pendingCallTextOutcome.endSequence
              ? { endSequence: true }
              : {}),
          },
          { tenantIdOverride: tenantId }
        );
        try {
          await this.#services.focusModeTaskService.completeTask(
            this.#task.id,
            finalNotesText,
            {
              logOutcome: false,
              tenantIdOverride: tenantId,
              ...(pendingCallTextOutcome.endSequence
                ? { endSequence: true }
                : {}),
            }
          );
        } catch (e) {
          console.warn('[CallStepController] mirror completion failed', {
            taskId: this.#task.id,
            e,
          });
        }
        this.#pendingCallTextOutcome = null;
        this.#callTextCompleteReady = false;
        this.#noRespDetails = null;
        await this.#finishTaskStep();
        return;
      }
      // Save typed notes when completing EMAIL, TASK, or finishing LINKEDIN steps
      const isLinkedInTask = this.#isLinkedInTask(type);
      if (!isCallLike && !(isLinkedInTask && !this.#linkedinStarted)) {
        const notesText = this.#getTaskNotesText();
        if (notesText) {
          void this.#queueNotesSave(notesText);
        }
      }
      if (isCallLike) {
        const loading = this.#ensureLoadingService();
        const callWindowDecision = await this.#evaluateContactLocalCallWindow();
        if (
          (callWindowDecision.shouldHold || callWindowDecision.shouldDefer) &&
          !this.#contactLocalCallWindowOverrideAccepted
        ) {
          loading?.hide?.('tasks');
          if (callWindowDecision.mode === 'automatic') {
            try {
              if (callWindowDecision.shouldHold) {
                await this.#holdForContactLocalCallWindow(callWindowDecision);
              } else {
                await this.#deferForContactLocalCallWindow(callWindowDecision);
              }
            } catch (err) {
              this.#services?.errorUI?.global?.({
                text:
                  err?.message ||
                  (callWindowDecision.shouldHold
                    ? 'Unable to hold this task for later today.'
                    : 'Unable to defer this task right now.'),
                type: 'error',
                duration: 4000,
              });
            }
            return;
          }

          await this.#openContactLocalCallWindowModal(callWindowDecision);
          return;
        }
        this.#contactLocalCallWindowBlock = null;
        this.#closeContactLocalCallWindowModal();
        if (!this.#services.canCall) {
          loading?.hide?.('tasks');
          this.#services.errorUI?.global?.({
            text: 'Calling is disabled',
            type: 'error',
            duration: 4000,
          });
          return;
        }

        if (this.#phoneOpts.length === 0) {
          loading.showAfterDelay('tasks', 200, LOGGING_OUTCOME_MESSAGE);
          const tenantId = await this.#resolveTenantId();
          try {
            await this.#completeWithOutcome(
              this.#task,
              {
                taskId: this.#task.id,
                userId:
                  this.#task.userId ?? this.#services.auth?.currentUser?.uid,
                outcome: 'NO_PHONE',
                action: this.#noRespDetails?.action,
                channel: this.#channelForOutcome(),
                sequenceId: this.#task.sequenceId || this.#task.enrolId,
                stepIndex: this.#task.stepIdx,
                metadata: { reason: 'Missing phone number', contactId },
              },
              { tenantIdOverride: tenantId }
            );
          } catch (err) {
            console.error('[CallStepController] skip-complete failed', err);
            return;
          }
          try {
            await this.#services.focusModeTaskService.completeTask(
              this.#task.id,
              null,
              {
                logOutcome: false,
                tenantIdOverride: tenantId,
              }
            );
          } catch (e) {
            console.warn('[CallStepController] mirror completion failed', {
              taskId: this.#task.id,
              e,
            });
          }

          this.#services.analytics
            ?.track('FocusMode - SkipContact - NoPhone', {
              contactId,
              taskId: this.#task.id,
            })
            .catch(() => {});

          this.#services.errorUI?.global?.({
            text: 'No phone number found – skipping contact',
            type: 'warning',
            duration: 4000,
          });
          await this.#finishTaskStep();
          return;
        }
        const btn = event?.currentTarget;
        let to = btn?.dataset.phone || '';
        const opt = this.#phoneOpts[this.#currentPhoneIdx] || {};
        const extension = sanitizePhoneExtension(opt.extension || '');
        if (!to) {
          to = opt.number || '';
        }
        if (/^\d{10}$/.test(to)) to = '+1' + to;

        if (to) {
          loading.show('tasks', STARTING_CALL_MESSAGE);
          loading.show('tasks', 'Dialing…');
          this.#log('dial →', {
            to,
            currentIdx: this.#currentPhoneIdx,
            opts: this.#phoneOpts,
          });
          this.#lastDialed = to;
          this.#lastDialedTo = to;
          this.#lastDialedExtension = extension;
          this.#showRedial = false;
          this.#clearRedialCooldown();
          try {
            if (this.#callProvider === 'zoom') {
              await this.#startZoomCall({
                to,
                extension,
                contactId,
                taskId: this.#task.id,
              });
            } else if (this.#usesSystemDialer()) {
              await this.#startSystemCall({
                to,
                extension,
                contactId,
                taskId: this.#task.id,
              });
            } else {
              await this.#startFocusModeCall({
                to,
                extension,
                contactId,
                taskId: this.#task.id,
              });
            }
          } catch (err) {
            console.warn('[CallStepController] dial failed', err);
            loading?.hide?.('tasks');
            return;
          }
        }
        return;
      } else if (this.#isLinkedInTask(type)) {
        if (type === 'LINKEDIN_ACTIVITY') {
          const link = this.#resolveLinkedInProfileUrl();

          if (!this.#linkedinStarted) {
            if (!link) {
              this.#services.errorUI?.global?.({
                text: 'No LinkedIn profile URL found for this contact.',
                type: 'error',
                duration: 4000,
              });
              return;
            }

            window.open(link, '_blank');
            this.#linkedinStarted = true;
            this.#armLinkedInReturnListeners();
            this.#services?.loading?.hide('tasks');
            this.#render();
            return;
          }

          await this.#openLinkedInActivityModal({ trigger: 'manual' });
          this.#services?.loading?.hide('tasks');
          return;
        }

        const isLinkedInMessage = type === 'LINKEDIN_MESSAGE';
        const link = this.#resolveLinkedInProfileUrl();

        if (!this.#linkedinStarted) {
          if (!link) {
            this.#services.errorUI?.global?.({
              text: 'No LinkedIn profile URL found for this contact.',
              type: 'error',
              duration: 4000,
            });
            return;
          }

          let modalResp = null;
          if (isLinkedInMessage) {
            const {
              tpl: templateId = '',
              templates = [],
              data: templateData = {},
              mergeVars = {},
              name: contactName = '',
            } = this.#meta || {};
            const templateIds = Array.isArray(templates)
              ? templates.filter(Boolean)
              : [];
            const baseTemplateId = templateId || templateIds[0] || '';
            try {
              modalResp = await openLinkedinMessageModal({
                templateId: baseTemplateId,
                templateIds,
                templateData,
                mergeVars,
                contactName,
                contactId,
                taskId: this.#task.id,
                linkedinUrl: link,
                enableTooltips: this.#services?.focusModeTooltips !== false,
              });
            } catch (err) {
              console.error(
                '[CallStepController] LinkedIn message modal failed',
                err
              );
              this.#services.errorUI?.global?.({
                text: 'Unable to load LinkedIn message template.',
                type: 'error',
                duration: 4000,
              });
              return;
            }

            if (!modalResp) return;

            const resolvedTemplateId =
              modalResp.templateId || baseTemplateId || null;
            const copiedAt =
              typeof modalResp.copiedAt === 'number'
                ? modalResp.copiedAt
                : null;

            this.#linkedinMessageMeta = {
              templateId: resolvedTemplateId,
              copied: Boolean(modalResp.copied),
              copiedAt,
              copyMethod: modalResp.copyMethod || null,
              messageLength: modalResp.message ? modalResp.message.length : 0,
              mediaAssetId: modalResp.personalizedMediaAsset?.assetId || null,
              mediaLocalExported: Boolean(
                modalResp.personalizedMediaLocalExported
              ),
              mediaUploadStatus: modalResp.personalizedMediaUploadPromise
                ? 'uploading'
                : modalResp.personalizedMediaAsset?.status || null,
            };
            if (
              modalResp.personalizedMediaUploadPromise &&
              typeof modalResp.personalizedMediaUploadPromise.then ===
                'function'
            ) {
              void modalResp.personalizedMediaUploadPromise.then((asset) => {
                if (!this.#linkedinMessageMeta) return;
                this.#linkedinMessageMeta.mediaAssetId =
                  asset?.assetId ||
                  this.#linkedinMessageMeta.mediaAssetId ||
                  null;
                this.#linkedinMessageMeta.mediaUploadStatus = asset
                  ? 'ready'
                  : 'failed';
              });
            }
          }

          if (!modalResp?.linkedinOpened) {
            window.open(link, '_blank');
          }
          this.#linkedinStarted = true;
          this.#services?.loading?.hide('tasks');
          this.#render();
          return;
        }

        const tenantId = await this.#resolveTenantId();
        const metadata = {
          stepType: type,
          contactId,
        };
        if (isLinkedInMessage) {
          const extra = this.#linkedinMessageMeta || {};
          if (Object.prototype.hasOwnProperty.call(extra, 'templateId')) {
            metadata.templateId = extra.templateId ?? null;
          }
          if (typeof extra.copied === 'boolean') metadata.copied = extra.copied;
          if (extra.copiedAt) metadata.copiedAt = extra.copiedAt;
          if (extra.copyMethod) metadata.copyMethod = extra.copyMethod;
          if (typeof extra.messageLength === 'number') {
            metadata.messageLength = extra.messageLength;
          }
          if (extra.mediaAssetId) {
            metadata.mediaAssetId = extra.mediaAssetId;
          }
          if (typeof extra.mediaLocalExported === 'boolean') {
            metadata.mediaLocalExported = extra.mediaLocalExported;
          }
          if (extra.mediaUploadStatus) {
            metadata.mediaUploadStatus = extra.mediaUploadStatus;
          }
        }

        loading.showAfterDelay('tasks', 200, LOGGING_OUTCOME_MESSAGE);
        await this.#completeWithOutcome(
          this.#task,
          {
            taskId: this.#task.id,
            userId: this.#task.userId ?? this.#services.auth?.currentUser?.uid,
            outcome: 'TASK_COMPLETE',
            action: this.#noRespDetails?.action,
            channel: 'LINKEDIN',
            sequenceId: this.#task.sequenceId || this.#task.enrolId,
            stepIndex: this.#task.stepIdx,
            metadata,
          },
          { tenantIdOverride: tenantId }
        );
        try {
          await this.#services.focusModeTaskService.completeTask(
            this.#task.id,
            null,
            {
              logOutcome: false,
              tenantIdOverride: tenantId,
            }
          );
        } catch (e) {
          console.warn('[CallStepController] mirror completion failed', {
            taskId: this.#task.id,
            e,
          });
        }
        await this.#finishTaskStep();
        return;
      } else if (type === 'TEXT' || type === 'SMS') {
        const { sms_p2p_deeplink: smsDeeplinkRaw = '' } = this.#meta || {};

        let smsDeeplink =
          typeof smsDeeplinkRaw === 'string' ? smsDeeplinkRaw.trim() : '';
        const appConfig =
          typeof window !== 'undefined' ? window.APP_CONFIG : null;
        if (!appConfig || appConfig.__hydrated !== true) {
          this.#services.errorUI?.global?.({
            text: 'Loading text settings… please try again in a moment.',
            type: 'warning',
            duration: 4000,
          });
          return;
        }
        if (appConfig.__hydratedError) {
          this.#services.errorUI?.global?.({
            text: 'Unable to load text settings (blocked by policy or network). Please refresh or contact IT.',
            type: 'error',
            duration: 5000,
          });
          return;
        }
        const smsProvider = getSmsProvider();
        if (
          smsProvider === SMS_PROVIDER.ZOOM &&
          smsDeeplink.toLowerCase?.().startsWith('sms:')
        ) {
          const linkNumber = smsDeeplink.replace(/^sms:/i, '').split(/[?&]/)[0];
          const linkBody = extractSmsBodyFromLink(smsDeeplink);
          const rebuilt = buildTextDeeplink(linkNumber, linkBody, {
            provider: smsProvider,
          });
          if (rebuilt) {
            smsDeeplink = rebuilt;
          }
        }
        if (!smsDeeplink) {
          const fallbackNumber =
            (this.#phoneOpts[this.#currentPhoneIdx] || {}).number ||
            this.#task.mobilePhone ||
            this.#task.phone ||
            '';
          const built = buildTextDeeplink(fallbackNumber, '', {
            provider: smsProvider,
          });
          if (built) {
            smsDeeplink = built;
          }
        }

        if (!this.#smsStarted) {
          if (!smsDeeplink) {
            this.#services.errorUI?.global?.({
              text: 'No SMS deep link found for this contact.',
              type: 'error',
              duration: 4000,
            });
            this.#services?.loading?.hide('tasks');
            return;
          }

          try {
            window.open(smsDeeplink, '_blank');
          } catch (err) {
            console.warn(
              '[CallStepController] SMS deep link redirect failed',
              err
            );
          }

          this.#smsStarted = true;
          this.#services?.loading?.hide('tasks');
          this.#render();
          return;
        }

        const tenantId = await this.#resolveTenantId();
        const metadata = {
          stepType: 'TEXT',
          contactId,
          smsDeeplink: smsDeeplink || null,
        };

        loading.showAfterDelay('tasks', 200, LOGGING_OUTCOME_MESSAGE);
        await this.#completeWithOutcome(
          this.#task,
          {
            taskId: this.#task.id,
            userId: this.#task.userId ?? this.#services.auth?.currentUser?.uid,
            outcome: 'TASK_COMPLETE',
            action: this.#noRespDetails?.action,
            channel: 'SMS',
            sequenceId: this.#task.sequenceId || this.#task.enrolId,
            stepIndex: this.#task.stepIdx,
            metadata,
          },
          { tenantIdOverride: tenantId }
        );
        try {
          await this.#services.focusModeTaskService.completeTask(
            this.#task.id,
            null,
            {
              logOutcome: false,
              tenantIdOverride: tenantId,
            }
          );
        } catch (e) {
          console.warn('[CallStepController] mirror completion failed', {
            taskId: this.#task.id,
            e,
          });
        }
        await this.#finishTaskStep();
        return;
      } else if (type === 'TEXT_MESSAGE' || type === 'SMS_MESSAGE') {
        const {
          tpl: templateId = '',
          templates = [],
          data: templateData = {},
          mergeVars = {},
          name: contactName = '',
          sms_p2p_deeplink: smsDeeplinkRaw = '',
        } = this.#meta || {};
        const templateIds = Array.isArray(templates)
          ? templates.filter(Boolean)
          : [];
        const baseTemplateId = templateId || templateIds[0] || '';
        let smsDeeplink =
          typeof smsDeeplinkRaw === 'string' ? smsDeeplinkRaw.trim() : '';
        let initialMessage =
          extractSmsBodyFromLink(smsDeeplink) ||
          (typeof this.#meta?.body === 'string' ? this.#meta.body : '');
        const appConfig =
          typeof window !== 'undefined' ? window.APP_CONFIG : null;
        if (!appConfig || appConfig.__hydrated !== true) {
          this.#services.errorUI?.global?.({
            text: 'Loading text settings… please try again in a moment.',
            type: 'warning',
            duration: 4000,
          });
          return;
        }
        if (appConfig.__hydratedError) {
          this.#services.errorUI?.global?.({
            text: 'Unable to load text settings (blocked by policy or network). Please refresh or contact IT.',
            type: 'error',
            duration: 5000,
          });
          return;
        }
        const smsProvider = getSmsProvider();
        const isZoomSmsProvider = smsProvider === SMS_PROVIDER.ZOOM;
        if (
          isZoomSmsProvider &&
          smsDeeplink.toLowerCase?.().startsWith('sms:')
        ) {
          const linkNumber = smsDeeplink.replace(/^sms:/i, '').split(/[?&]/)[0];
          const linkBody = extractSmsBodyFromLink(smsDeeplink);
          const rebuilt = buildTextDeeplink(linkNumber, linkBody, {
            provider: smsProvider,
          });
          if (rebuilt) {
            smsDeeplink = rebuilt;
            initialMessage = linkBody || initialMessage;
          }
        }

        if (!smsDeeplink) {
          const fallbackNumber =
            (this.#phoneOpts[this.#currentPhoneIdx] || {}).number ||
            this.#task.mobilePhone ||
            this.#task.phone ||
            '';
          const fallbackBody =
            initialMessage || `Hi ${contactName || ''}`.trim();
          const built = buildTextDeeplink(fallbackNumber, fallbackBody, {
            provider: smsProvider,
          });
          if (built) {
            smsDeeplink = built;
            initialMessage = fallbackBody;
          }
        }

        if (!this.#smsStarted) {
          if (!smsDeeplink) {
            this.#services.errorUI?.global?.({
              text: 'No SMS deep link found for this contact.',
              type: 'error',
              duration: 4000,
            });
            this.#services?.loading?.hide('tasks');
            return;
          }

          let modalResp = null;
          try {
            modalResp = await openSmsMessageModal({
              templateId: baseTemplateId,
              templateIds,
              templateData,
              mergeVars,
              contactName,
              initialMessage,
              copyFirst: isZoomSmsProvider,
              helperMessage: isZoomSmsProvider
                ? 'Review the message below, tweak if needed, then copy it before texting in Zoom.'
                : '',
              enableTooltips: this.#services?.focusModeTooltips !== false,
            });
          } catch (err) {
            console.error('[CallStepController] SMS message modal failed', err);
            this.#services.errorUI?.global?.({
              text: 'Unable to load SMS template.',
              type: 'error',
              duration: 4000,
            });
            this.#services?.loading?.hide('tasks');
            return;
          }

          if (!modalResp) {
            this.#services?.loading?.hide('tasks');
            return;
          }

          const resolvedTemplateId =
            modalResp.templateId || baseTemplateId || null;
          const messageBody = modalResp.message || initialMessage || '';
          const deeplinkWithBody = applySmsBodyToDeeplink(
            smsDeeplink,
            messageBody
          );

          if (!deeplinkWithBody) {
            this.#services.errorUI?.global?.({
              text: 'Unable to build SMS link for this contact.',
              type: 'error',
              duration: 4000,
            });
            this.#services?.loading?.hide('tasks');
            return;
          }

          const metaForMessage = {
            templateId: resolvedTemplateId,
            messageLength: messageBody.length,
            usedDeeplink: true,
            deeplink: deeplinkWithBody,
          };
          if (messageBody) {
            metaForMessage.smsBody = messageBody;
          }
          if (
            isZoomSmsProvider &&
            Object.prototype.hasOwnProperty.call(modalResp, 'copied')
          ) {
            metaForMessage.copied = Boolean(modalResp.copied);
          }
          if (isZoomSmsProvider && typeof modalResp?.copiedAt === 'number') {
            metaForMessage.copiedAt = modalResp.copiedAt;
          }
          if (isZoomSmsProvider && modalResp?.copyMethod) {
            metaForMessage.copyMethod = modalResp.copyMethod;
          }

          this.#smsMessageMeta = metaForMessage;

          try {
            window.open(deeplinkWithBody, '_blank');
          } catch (err) {
            console.warn(
              '[CallStepController] SMS deep link redirect failed',
              err
            );
          }

          this.#smsStarted = true;
          this.#services?.loading?.hide('tasks');
          this.#render();
          return;
        }

        const tenantId = await this.#resolveTenantId();
        const metadata = {
          stepType: type,
          contactId,
          smsDeeplink: smsDeeplink || null,
        };
        const extra = this.#smsMessageMeta || {};
        if (Object.prototype.hasOwnProperty.call(extra, 'templateId')) {
          metadata.templateId = extra.templateId ?? null;
        }
        if (typeof extra.messageLength === 'number') {
          metadata.messageLength = extra.messageLength;
        }
        metadata.usedSmsDeeplink = Boolean(extra.usedDeeplink);
        if (Object.prototype.hasOwnProperty.call(extra, 'copied')) {
          metadata.copied = Boolean(extra.copied);
        }
        if (typeof extra.copiedAt === 'number') {
          metadata.copiedAt = extra.copiedAt;
        }
        if (extra.copyMethod) {
          metadata.copyMethod = extra.copyMethod;
        }

        const smsBody = extractSmsBodyFromLink(extra.deeplink || smsDeeplink);
        if (smsBody) {
          metadata.smsBody = smsBody;
        } else if (extra.smsBody) {
          metadata.smsBody = extra.smsBody;
        }

        loading.showAfterDelay('tasks', 200, LOGGING_OUTCOME_MESSAGE);
        await this.#completeWithOutcome(
          this.#task,
          {
            taskId: this.#task.id,
            userId: this.#task.userId ?? this.#services.auth?.currentUser?.uid,
            outcome: 'TASK_COMPLETE',
            action: this.#noRespDetails?.action,
            channel: 'SMS',
            sequenceId: this.#task.sequenceId || this.#task.enrolId,
            stepIndex: this.#task.stepIdx,
            metadata,
          },
          { tenantIdOverride: tenantId }
        );
        try {
          await this.#services.focusModeTaskService.completeTask(
            this.#task.id,
            null,
            {
              logOutcome: false,
              tenantIdOverride: tenantId,
            }
          );
        } catch (e) {
          console.warn('[CallStepController] mirror completion failed', {
            taskId: this.#task.id,
            e,
          });
        }

        await this.#finishTaskStep();
        return;
      } else if (type === 'EMAIL') {
        // Restored EMAIL flow
        const {
          tpl: templateId = null,
          templates = [],
          data: templateData = {},
          mergeVars = {},
        } = this.#meta;
        console.log('[CallStepController] EMAIL meta →', {
          templateId,
          templateData,
          mergeVars,
        });
        console.log('[CallStepController] contactId →', contactId);
        if (!contactId) {
          return this.#services.errorUI?.global?.({
            text: 'No contactId available for email',
            type: 'error',
            duration: 3000,
          });
        }

        const to = await lookupStagedEmail(contactId);
        console.log('[CallStepController] email to →', to);
        if (!to) {
          return this.#services.errorUI?.global?.({
            text: 'No staged email found for contact',
            type: 'error',
            duration: 3000,
          });
        }

        loading.hide('tasks');
        const resp = await openEmailComposer({
          to,
          templateId,
          templateIds: templates,
          templateData,
          mergeVars,
          manual: !this.#emailConnected, // informs modal to relabel Send
          brainstormContext: this.#buildEmailBrainstormContext(templateId),
          stepTemplateManagement:
            this.#buildStepTemplateManagementContext(templates),
          enableTooltips: this.#services?.focusModeTooltips !== false,
          allowAttachments: true,
        });

        if (!resp) return;
        loading.show('tasks', FOLLOW_UP_EMAIL_MESSAGE);

        const payload = {
          contactId,
          to,
          subject: resp.subject,
          html: resp.html,
          taskId: this.#task.id,
          fontFamily: resp.fontFamily,
        };

        const chosenId = resp.templateId || templateId;
        payload.templateId = chosenId || undefined;
        payload.adHocAttachments = resp.adHocAttachments || [];
        let outcome = 'EMAIL_SENT';
        let metadata = { contactId, custom: !!chosenId };

        /* ─── ensure threading IDs are loaded ─── */
        if (!this.#task.emailMessageId) {
          try {
            /* 1) resolve tenant from user-uid */
            const tenantId = useTenant(this.#services.auth ?? {});
            if (!tenantId) throw new Error('tenantId missing');
            const ref = docWithTenant(tenantId, `tasks/${this.#task.id}`);
            const snap = await this.#services.fsService.getDocument(
              ref.path,
              false
            );
            if (snap) Object.assign(this.#task, snap);
          } catch (e) {
            console.warn('[CallStep] failed to hydrate email IDs', e);
          }
        }
        console.log('[DEBUG] at send-decision →', {
          emailConnected: this.#emailConnected,
          threadFlag: this.#meta.thread,
          msgId: this.#task.emailMessageId,
        });
        if (this.#emailConnected) {
          if (this.#meta.thread && this.#task.emailMessageId) {
            try {
              await this.#services.emailService.replyInThread({
                taskId: this.#task.id,
                html: resp.html,
                fontFamily: resp.fontFamily,
                subject: resp.subject,
                to,
                templateId: chosenId || undefined,
                adHocAttachments: resp.adHocAttachments || [],
              });
            } catch (err) {
              const errMsg = err?.message || err?.details?.message || '';
              const threadingUnavailable =
                (err?.code === 'failed-precondition' &&
                  /Task missing emailMessageId|Graph reply requires graphId/i.test(
                    errMsg
                  )) ||
                err?.details?.code === 'THREADING_UNAVAILABLE' ||
                err?.details?.code === 'GRAPH_MESSAGE_MISSING';
              const hardStop =
                err?.details?.code === 'EMAIL_OAUTH_DISCONNECTED' ||
                err?.details?.code === 'graph-missing-mail-read' ||
                [
                  'permission-denied',
                  'unauthenticated',
                  'invalid-argument',
                ].includes(err?.code);
              if (!threadingUnavailable || hardStop) throw err;

              console.warn(
                '[CallStepController] replyInThread fallback to sendCustomEmail',
                {
                  taskId: this.#task.id,
                  code: err?.code,
                  message: errMsg,
                }
              );
              const sendRes =
                await this.#services.emailService.sendCustomEmail(payload);
              if (sendRes?.messageId) {
                Object.assign(this.#task, {
                  emailProvider: sendRes.provider,
                  emailMessageId: sendRes.messageId,
                  emailThreadId: sendRes.threadId,
                });
              }
            }
          } else {
            console.log('👀 sendCustomEmail payload:', payload);
            const sendRes =
              await this.#services.emailService.sendCustomEmail(payload);
            if (sendRes.messageId) {
              Object.assign(this.#task, {
                emailProvider: sendRes.provider,
                emailMessageId: sendRes.messageId,
                emailThreadId: sendRes.threadId,
              });
            }
          }
        } else {
          outcome = 'EMAIL_SENT_MANUAL';
          metadata.manual = true;
        }

        const tenantId = await this.#resolveTenantId();
        loading.showAfterDelay('tasks', 200, LOGGING_OUTCOME_MESSAGE);
        await this.#completeWithOutcome(
          this.#task,
          {
            taskId: this.#task.id,
            userId: this.#task.userId ?? this.#services.auth?.currentUser?.uid,
            outcome,
            action: this.#noRespDetails?.action,
            channel: 'EMAIL',
            sequenceId: this.#task.sequenceId || this.#task.enrolId,
            stepIndex: this.#task.stepIdx,
            metadata,
          },
          { tenantIdOverride: tenantId }
        );
        try {
          await this.#services.focusModeTaskService.completeTask(
            this.#task.id,
            null,
            {
              logOutcome: false,
              tenantIdOverride: tenantId,
            }
          );
        } catch (e) {
          console.warn('[CallStepController] mirror completion failed', {
            taskId: this.#task.id,
            e,
          });
        }

        await this.#finishTaskStep();
        return;
      }

      // TASK default
      const tenantId = await this.#resolveTenantId();
      loading.showAfterDelay('tasks', 200, LOGGING_OUTCOME_MESSAGE);
      await this.#completeWithOutcome(
        this.#task,
        {
          taskId: this.#task.id,
          userId: this.#task.userId || this.#services.auth?.currentUser?.uid,
          outcome: 'TASK_COMPLETE',
          action: this.#noRespDetails?.action,
          channel: this.#channelForOutcome(),
          sequenceId: this.#task.sequenceId || this.#task.enrolId,
          stepIndex: this.#task.stepIdx,
          metadata: { contactId },
        },
        { tenantIdOverride: tenantId }
      );
      try {
        await this.#services.focusModeTaskService.completeTask(
          this.#task.id,
          null,
          {
            logOutcome: false,
            tenantIdOverride: tenantId,
          }
        );
      } catch (e) {
        console.warn('[CallStepController] mirror completion failed', {
          taskId: this.#task.id,
          e,
        });
      }
      await this.#finishTaskStep();
    } catch (err) {
      console.error(err);
      this.#services.errorUI?.global?.({
        text: err.message,
        type: 'error',
        duration: 3000,
      });
    } finally {
      if (!isCallLike) {
        this.#services?.loading?.hide('tasks');
      }
    }
  }

  /* ─────────── secondary CTAs ─────────── */
  /**
   * Launch the follow-up email composer for Call + Email tasks.
   * Ensures an email address is staged and, when connected, sends immediately.
   *
   * @param {string|null|undefined} contactId - Identifier for the current contact.
   * @returns {Promise<{ok:boolean,attempted:boolean,skipped?:boolean,skipReason?:string}>}
   *   Follow-up result used to decide whether the call outcome can continue.
   */
  async #handleCallEmailFollowup(contactId) {
    const loading = this.#services?.loading;

    if (!contactId) {
      this.#services?.errorUI?.global?.({
        text: 'No contactId available for email follow-up',
        type: 'error',
        duration: 3000,
      });
      return { ok: false, attempted: false };
    }

    const { templateId, templateIds: templates } =
      await this.#resolveCallEmailTemplateSet();
    const templateData =
      this.#meta?.data ??
      this.#meta?.templateData ??
      this.#task?.templateData ??
      {};
    const mergeVars = this.#meta?.mergeVars ?? this.#task?.mergeVars ?? {};

    let to = null;
    let lookupError = null;
    try {
      to = await lookupStagedEmail(contactId);
    } catch (err) {
      lookupError = err;
      console.warn('[CallStepController] staged email lookup failed', err);
    }

    const missingEmailLookup =
      lookupError &&
      /no staging record found|has no email field/i.test(
        String(lookupError?.message || '')
      );
    if (lookupError && !missingEmailLookup) {
      this.#services?.errorUI?.global?.({
        text: 'Unable to check the contact email. Please try again.',
        type: 'error',
        duration: 4000,
      });
      return { ok: false, attempted: false };
    }

    const normalizedTo = String(to || '').trim();
    const sequencePausedForEmail =
      String(this.#task?.status || '').toUpperCase() === 'PAUSED' &&
      this.#task?.pauseReason === 'EMAIL_PROBLEM';
    if (sequencePausedForEmail) {
      this.#services?.errorUI?.global?.({
        text: 'This sequence is paused for an email problem. Fix the contact email and resume the sequence before continuing.',
        type: 'error',
        duration: 4000,
      });
      return { ok: false, attempted: false };
    }

    const emailSkipReason = !normalizedTo
      ? 'missing_recipient'
      : !/^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(normalizedTo)
        ? 'invalid_recipient'
        : null;

    if (emailSkipReason) {
      this.#services?.errorUI?.global?.({
        text: 'Email skipped: no valid recipient address. Continuing with the call outcome.',
        type: 'warning',
        duration: 4000,
      });
      return {
        ok: true,
        attempted: false,
        skipped: true,
        skipReason: emailSkipReason,
      };
    }

    const resp = await openEmailComposer({
      to: normalizedTo,
      templateId,
      templateIds: templates,
      templateData,
      mergeVars,
      manual: !this.#emailConnected,
      brainstormContext: this.#buildEmailBrainstormContext(templateId),
      stepTemplateManagement:
        this.#buildStepTemplateManagementContext(templates),
      enableTooltips: this.#services?.focusModeTooltips !== false,
      allowAttachments: true,
    });
    if (!resp) return { ok: false, attempted: false };

    if (!this.#emailConnected) {
      return { ok: true, attempted: true }; // seller confirmed manual send
    }

    const emailSvc = this.#services?.emailService;
    if (!emailSvc) {
      this.#services?.errorUI?.global?.({
        text: 'Email service unavailable. Please try again later.',
        type: 'error',
        duration: 4000,
      });
      return { ok: false, attempted: false };
    }

    loading?.show('tasks', FOLLOW_UP_EMAIL_MESSAGE);

    const payload = {
      contactId,
      to: normalizedTo,
      subject: resp.subject,
      html: resp.html,
      taskId: this.#task.id,
      fontFamily: resp.fontFamily,
      templateId: resp.templateId || templateId || undefined,
      adHocAttachments: resp.adHocAttachments || [],
    };

    try {
      if (this.#meta?.thread && this.#task?.emailMessageId) {
        try {
          await emailSvc.replyInThread?.({
            taskId: this.#task.id,
            html: resp.html,
            fontFamily: resp.fontFamily,
            subject: resp.subject,
            to: normalizedTo,
            templateId: resp.templateId || templateId || undefined,
            adHocAttachments: resp.adHocAttachments || [],
          });
        } catch (err) {
          const errMsg = err?.message || err?.details?.message || '';
          const threadingUnavailable =
            (err?.code === 'failed-precondition' &&
              /Task missing emailMessageId|Graph reply requires graphId/i.test(
                errMsg
              )) ||
            err?.details?.code === 'THREADING_UNAVAILABLE' ||
            err?.details?.code === 'GRAPH_MESSAGE_MISSING';
          const hardStop =
            err?.details?.code === 'EMAIL_OAUTH_DISCONNECTED' ||
            err?.details?.code === 'graph-missing-mail-read' ||
            [
              'permission-denied',
              'unauthenticated',
              'invalid-argument',
            ].includes(err?.code);
          if (!threadingUnavailable || hardStop) throw err;

          console.warn(
            '[CallStepController] replyInThread fallback to sendCustomEmail',
            {
              taskId: this.#task.id,
              code: err?.code,
              message: errMsg,
            }
          );
          const sendRes = await emailSvc.sendCustomEmail?.(payload);
          if (sendRes?.messageId) {
            Object.assign(this.#task, {
              emailProvider: sendRes.provider,
              emailMessageId: sendRes.messageId,
              emailThreadId: sendRes.threadId,
            });
          }
        }
      } else {
        const sendRes = await emailSvc.sendCustomEmail?.(payload);
        if (sendRes?.messageId) {
          Object.assign(this.#task, {
            emailProvider: sendRes.provider,
            emailMessageId: sendRes.messageId,
            emailThreadId: sendRes.threadId,
          });
        }
      }
    } catch (err) {
      console.error('[CallStepController] follow-up email send failed', err);
      const needsReconnect = err?.details?.code === 'EMAIL_OAUTH_DISCONNECTED';
      const providerLabel =
        err?.details?.provider === 'outlook'
          ? 'Outlook'
          : err?.details?.provider === 'gmail'
            ? 'Gmail'
            : 'email';
      this.#services?.errorUI?.global?.({
        text: needsReconnect
          ? `Reconnect ${providerLabel} to send follow-up email.`
          : 'Unable to send follow-up email',
        type: 'error',
        duration: 4000,
      });
      loading?.hide('tasks');
      return { ok: false, attempted: true };
    }

    return { ok: true, attempted: true };
  }

  /**
   * Launch the follow-up SMS composer for Call + Text tasks.
   * Opens the SMS modal and deep link using the same metadata structure
   * as the TEXT_MESSAGE flow.
   *
   * @param {string|null|undefined} contactId - Identifier for the current contact.
   * @returns {Promise<{ ok: boolean, error?: string, meta?: object }>}
   */
  async #handleCallTextMessageFollowup(contactId) {
    if (!contactId) {
      this.#services?.errorUI?.global?.({
        text: 'No contactId available for text follow-up',
        type: 'error',
        duration: 3000,
      });
      return { ok: false, error: 'missing_contact' };
    }

    const {
      tpl: metaTemplateId = '',
      templates: metaTemplates = [],
      data: metaTemplateData = {},
      mergeVars: metaMergeVars = {},
      name: contactName = '',
      sms_p2p_deeplink: smsDeeplinkRaw = '',
    } = this.#meta || {};
    const templateId =
      metaTemplateId || this.#task?.templateId || this.#task?.tpl || '';
    const templateData =
      Object.keys(metaTemplateData || {}).length > 0
        ? metaTemplateData
        : this.#task?.templateData || this.#task?.data || {};
    const mergeVars =
      Object.keys(metaMergeVars || {}).length > 0
        ? metaMergeVars
        : this.#task?.mergeVars || {};
    const templates = Array.isArray(metaTemplates)
      ? metaTemplates
      : Array.isArray(this.#task?.templateIds)
        ? this.#task.templateIds
        : [];
    const templateIds = Array.isArray(templates)
      ? templates.filter(Boolean)
      : [];
    const baseTemplateId = templateId || templateIds[0] || '';
    let smsDeeplink =
      typeof smsDeeplinkRaw === 'string' ? smsDeeplinkRaw.trim() : '';
    let initialMessage =
      extractSmsBodyFromLink(smsDeeplink) ||
      (typeof this.#meta?.body === 'string' ? this.#meta.body : '');
    const appConfig = typeof window !== 'undefined' ? window.APP_CONFIG : null;
    if (!appConfig || appConfig.__hydrated !== true) {
      this.#services.errorUI?.global?.({
        text: 'Loading text settings… please try again in a moment.',
        type: 'warning',
        duration: 4000,
      });
      return { ok: false, error: 'settings_unavailable' };
    }
    if (appConfig.__hydratedError) {
      this.#services.errorUI?.global?.({
        text: 'Unable to load text settings (blocked by policy or network). Please refresh or contact IT.',
        type: 'error',
        duration: 5000,
      });
      return { ok: false, error: 'settings_failed' };
    }

    const smsProvider = getSmsProvider();
    const isZoomSmsProvider = smsProvider === SMS_PROVIDER.ZOOM;
    if (isZoomSmsProvider && smsDeeplink.toLowerCase?.().startsWith('sms:')) {
      const linkNumber = smsDeeplink.replace(/^sms:/i, '').split(/[?&]/)[0];
      const linkBody = extractSmsBodyFromLink(smsDeeplink);
      const rebuilt = buildTextDeeplink(linkNumber, linkBody, {
        provider: smsProvider,
      });
      if (rebuilt) {
        smsDeeplink = rebuilt;
        initialMessage = linkBody || initialMessage;
      }
    }

    if (!smsDeeplink) {
      const fallbackNumber =
        (this.#phoneOpts[this.#currentPhoneIdx] || {}).number ||
        this.#task.mobilePhone ||
        this.#task.phone ||
        '';
      const fallbackBody = initialMessage || `Hi ${contactName || ''}`.trim();
      const built = buildTextDeeplink(fallbackNumber, fallbackBody, {
        provider: smsProvider,
      });
      if (built) {
        smsDeeplink = built;
        initialMessage = fallbackBody;
      }
    }

    if (!smsDeeplink) {
      this.#services.errorUI?.global?.({
        text: 'No SMS deep link found for this contact.',
        type: 'error',
        duration: 4000,
      });
      return { ok: false, error: 'missing_deeplink' };
    }

    let modalResp = null;
    try {
      modalResp = await openSmsMessageModal({
        templateId: baseTemplateId,
        templateIds,
        templateData,
        mergeVars,
        contactName,
        initialMessage,
        copyFirst: isZoomSmsProvider,
        helperMessage: isZoomSmsProvider
          ? 'Review the message below, tweak if needed, then copy it before texting in Zoom.'
          : '',
        enableTooltips: this.#services?.focusModeTooltips !== false,
      });
    } catch (err) {
      console.error('[CallStepController] SMS message modal failed', err);
      this.#services.errorUI?.global?.({
        text: 'Unable to load SMS template.',
        type: 'error',
        duration: 4000,
      });
      return { ok: false, error: 'modal_failed' };
    }

    if (!modalResp) {
      return { ok: false, error: 'composer_cancelled' };
    }

    const resolvedTemplateId = modalResp.templateId || baseTemplateId || null;
    const messageBody = modalResp.message || initialMessage || '';
    const deeplinkWithBody = applySmsBodyToDeeplink(smsDeeplink, messageBody);

    if (!deeplinkWithBody) {
      this.#services.errorUI?.global?.({
        text: 'Unable to build SMS link for this contact.',
        type: 'error',
        duration: 4000,
      });
      return { ok: false, error: 'missing_deeplink' };
    }

    const metaForMessage = {
      templateId: resolvedTemplateId,
      messageLength: messageBody.length,
      usedDeeplink: true,
      deeplink: deeplinkWithBody,
    };
    if (messageBody) {
      metaForMessage.smsBody = messageBody;
    }
    if (
      isZoomSmsProvider &&
      Object.prototype.hasOwnProperty.call(modalResp, 'copied')
    ) {
      metaForMessage.copied = Boolean(modalResp.copied);
    }
    if (isZoomSmsProvider && typeof modalResp?.copiedAt === 'number') {
      metaForMessage.copiedAt = modalResp.copiedAt;
    }
    if (isZoomSmsProvider && modalResp?.copyMethod) {
      metaForMessage.copyMethod = modalResp.copyMethod;
    }

    try {
      window.open(deeplinkWithBody, '_blank');
    } catch (err) {
      console.warn('[CallStepController] SMS deep link redirect failed', err);
    }

    return { ok: true, meta: metaForMessage };
  }

  async handleSecondary(action, inlineResponse = null) {
    console.log('[FM] secondary invoked', {
      raw: action,
      type: typeof action,
      asString: String(action),
    });
    await this.#services.auth?.waitForLogin?.().catch(() => {});
    console.log('[FocusModeCallStep] secondary action', { action });
    const { emailService, auth } = this.#services;
    const contactId =
      this.#task.contactId ||
      this.#task.associated_contact_id ||
      this.#task.contact_id;
    const loading = this.#services?.loading;
    if (
      action === 'NO_RESP' &&
      !inlineResponse &&
      this.#isMobileUi() &&
      this.#currentPhoneIdx >= this.#phoneOpts.length - 1
    ) {
      loading?.hide?.('tasks');
      this.#state = 'noResp';
      this.#render();
      return;
    }
    if (this.#outcomeActionBusy) {
      return;
    }
    this.#setOutcomeActionBusy(true, action);
    try {
      let outcome;
      let noRespCallNext = false;
      let noRespDeletedNumber = false;

      /* 🛡 ensure referrals is always a defined, clean array */
      if (!Array.isArray(this.#referrals)) this.#referrals = [];
      this.#referrals = this.#referrals.filter(
        (r) => r && (r.name || r.company || r.notes)
      ); // strip empties

      if (action !== 'NO_RESP') this.#noRespDetails = null; // clear stale details

      const behaviors = {};
      for (const label of this.#behaviorSelections.keys()) {
        behaviors[label] = true;
      }
      /** keeps the actual referral objects (may be an empty array) */
      behaviors.referrals = this.#referrals;

      const metadata = { contactId }; // ← keeps manual / reason flags
      const isCallTextMessageTask =
        String(this.#task.type ?? '').toUpperCase() === 'CALL_TEXTMESSAGE';

      console.log('[CallStepController] behaviors payload →', behaviors);

      switch (action) {
        case 'GO': {
          console.log('[CallStepController] GO meta →', this.#meta);
          if (!contactId) {
            this.#services?.loading?.hide('tasks');
            return this.#services.errorUI?.global?.({
              text: 'No contactId available for GO',
              type: 'error',
              duration: 3000,
            });
          }

          const emailOptOut = this.#emailOptOut;
          if (emailOptOut) {
            metadata.manual = true;
            metadata.emailOptOut = true;
          }

          // 1. pull the template ID & data from meta *or* from task itself
          const templateSet = await this.#resolveCallEmailTemplateSet({
            preferGoTemplates: true,
          });
          const { templateId, templateIds: templates } = templateSet;
          const templateData =
            this.#meta.data ??
            this.#meta.templateData ??
            this.#task.templateData ??
            {};
          const mergeVars = this.#meta.mergeVars ?? this.#task.mergeVars ?? {};
          const hasTemplate = Boolean(templateId || templates.length);
          const hasSmsTemplate = Boolean(
            templateId ||
              templates.length ||
              this.#meta?.body ||
              this.#meta?.sms_p2p_deeplink
          );

          let showTaskButton = true;
          try {
            const user =
              auth?.currentUser ??
              (await auth?.waitForLogin?.().catch(() => null));
            if (user?.uid && this.#services?.crmUtils?.getCurrentCRM) {
              const crm = await this.#services.crmUtils.getCurrentCRM(user.uid);
              showTaskButton = Boolean(crm) && crm !== 'local';
            }
          } catch (err) {
            console.warn(
              '[CallStepController] failed to resolve CRM for GO modal',
              err
            );
          }

          const goResp = await openGoModal({
            allowEmailComposer:
              !emailOptOut &&
              (isCallTextMessageTask ? hasSmsTemplate : hasTemplate),
            showTaskButton,
            enableTooltips: this.#services?.focusModeTooltips !== false,
          });
          if (!goResp) {
            this.#services?.loading?.hide('tasks');
            return;
          }

          const shouldCreateTask = goResp.action === 'task';
          const followupRequested =
            !emailOptOut &&
            (goResp.emailComposerRequested || goResp.action === 'email');

          metadata.meetingInviteSent = !!goResp.meetingInvite;
          metadata.meetingBooked = !!goResp.meetingBooked;
          metadata.eventInviteSent = !!goResp.eventInvite;
          if (goResp.other) metadata.goOtherText = goResp.other.trim();
          metadata.goReasons = Array.isArray(goResp.selectedReasons)
            ? goResp.selectedReasons
            : Array.isArray(goResp.selectedReasonIds)
              ? goResp.selectedReasonIds.map((id, index) => ({
                  id,
                  label: goResp.selectedReasonLabels?.[index] || '',
                  text: goResp.reasonTextById?.[id] || '',
                }))
              : [];

          if (shouldCreateTask) {
            this.#services?.loading?.hide('tasks');
            const followUp = await this.#promptFollowUpTask(contactId);
            if (followUp?.cancelled) return;
            if (!followUp?.taskId) {
              console.warn('[CallStepController] follow-up task missing id');
            } else {
              metadata.followUpTaskId = followUp.taskId;
            }
          }

          if (isCallTextMessageTask && followupRequested) {
            metadata.smsAttempted = true;
            let smsSendError = '';
            loading?.hide('tasks');
            const smsResp =
              await this.#handleCallTextMessageFollowup(contactId);
            if (!smsResp?.ok) {
              smsSendError = smsResp?.error || 'send_failed';
            } else if (smsResp?.meta) {
              const extra = smsResp.meta;
              if (Object.prototype.hasOwnProperty.call(extra, 'templateId')) {
                metadata.templateId = extra.templateId ?? null;
              }
              if (typeof extra.messageLength === 'number') {
                metadata.messageLength = extra.messageLength;
              }
              metadata.usedSmsDeeplink = Boolean(extra.usedDeeplink);
              if (extra.deeplink) {
                metadata.smsDeeplink = extra.deeplink;
              }
              if (Object.prototype.hasOwnProperty.call(extra, 'copied')) {
                metadata.copied = Boolean(extra.copied);
              }
              if (typeof extra.copiedAt === 'number') {
                metadata.copiedAt = extra.copiedAt;
              }
              if (extra.copyMethod) {
                metadata.copyMethod = extra.copyMethod;
              }
              if (extra.smsBody) {
                metadata.smsBody = extra.smsBody;
              }
            }
            loading?.showAfterDelay('tasks', 200, LOGGING_OUTCOME_MESSAGE);

            if (smsSendError) {
              metadata.smsSendError = smsSendError;
              this.#services?.errorUI?.global?.({
                text:
                  smsSendError === 'composer_cancelled'
                    ? 'Text cancelled. Outcome saved.'
                    : 'Text failed to send. Outcome saved.',
                type: 'error',
                duration: 3000,
              });
            }
          } else if (hasTemplate && followupRequested) {
            metadata.emailAttempted = true;
            let emailSendError = '';
            let to = '';
            try {
              // 2. lookup recipient email
              to = await lookupStagedEmail(contactId);
              if (!to) {
                emailSendError = 'missing_recipient';
              } else {
                // 3. open composer with exactly the meta you configured
                loading?.hide('tasks');
                const resp = await openEmailComposer({
                  to,
                  templateId,
                  templateIds: templates,
                  templateData,
                  mergeVars,
                  manual: !this.#emailConnected,
                  goOpts: false,
                  brainstormContext:
                    this.#buildEmailBrainstormContext(templateId),
                  stepTemplateManagement:
                    templateSet.source === 'default'
                      ? this.#buildStepTemplateManagementContext(templates)
                      : null,
                  enableTooltips: this.#services?.focusModeTooltips !== false,
                  allowAttachments: true,
                });
                if (!resp) {
                  emailSendError = 'composer_cancelled';
                } else {
                  loading?.show('tasks', FOLLOW_UP_EMAIL_MESSAGE);
                  const chosenId = resp.templateId || templateId;
                  metadata.custom = !!chosenId;
                  const payload = {
                    contactId,
                    to,
                    subject: resp.subject,
                    html: resp.html,
                    taskId: this.#task.id,
                    fontFamily: resp.fontFamily,
                    templateId: chosenId || undefined,
                    adHocAttachments: resp.adHocAttachments || [],
                  };

                  try {
                    if (this.#emailConnected) {
                      if (this.#meta.thread && this.#task.emailMessageId) {
                        try {
                          await emailService.replyInThread({
                            taskId: this.#task.id,
                            html: resp.html,
                            fontFamily: resp.fontFamily,
                            subject: resp.subject,
                            to,
                            templateId: chosenId || undefined,
                            adHocAttachments: resp.adHocAttachments || [],
                          });
                        } catch (err) {
                          const errMsg =
                            err?.message || err?.details?.message || '';
                          const threadingUnavailable =
                            (err?.code === 'failed-precondition' &&
                              /Task missing emailMessageId|Graph reply requires graphId/i.test(
                                errMsg
                              )) ||
                            err?.details?.code === 'THREADING_UNAVAILABLE' ||
                            err?.details?.code === 'GRAPH_MESSAGE_MISSING';
                          const hardStop =
                            err?.details?.code === 'EMAIL_OAUTH_DISCONNECTED' ||
                            err?.details?.code === 'graph-missing-mail-read' ||
                            [
                              'permission-denied',
                              'unauthenticated',
                              'invalid-argument',
                            ].includes(err?.code);
                          if (!threadingUnavailable || hardStop) throw err;

                          console.warn(
                            '[CallStepController] replyInThread fallback to sendCustomEmail',
                            {
                              taskId: this.#task.id,
                              code: err?.code,
                              message: errMsg,
                            }
                          );
                          const sendRes =
                            await emailService.sendCustomEmail(payload);
                          if (sendRes?.messageId) {
                            Object.assign(this.#task, {
                              emailProvider: sendRes.provider,
                              emailMessageId: sendRes.messageId,
                              emailThreadId: sendRes.threadId,
                            });
                          }
                        }
                      } else {
                        const sendRes =
                          await emailService.sendCustomEmail(payload);
                        if (sendRes.messageId) {
                          Object.assign(this.#task, {
                            emailProvider: sendRes.provider,
                            emailMessageId: sendRes.messageId,
                            emailThreadId: sendRes.threadId,
                          });
                        }
                      }
                    } else {
                      metadata.manual = true;
                    }
                  } catch (err) {
                    console.warn('[CallStepController] email send failed', err);
                    emailSendError = 'send_failed';
                  }
                }
              }
            } catch (err) {
              console.warn('[CallStepController] email attempt failed', err);
              emailSendError = 'send_failed';
            }

            if (emailSendError) {
              metadata.emailSendError = emailSendError;
              this.#services?.errorUI?.global?.({
                text:
                  emailSendError === 'missing_recipient'
                    ? 'Email not sent: missing recipient address. Outcome saved.'
                    : emailSendError === 'composer_cancelled'
                      ? 'Email cancelled. Outcome saved.'
                      : 'Email failed to send. Outcome saved.',
                type: 'error',
                duration: 3000,
              });
            }
          } else {
            metadata.manual =
              emailOptOut || (!hasTemplate && !isCallTextMessageTask);
          }

          outcome = 'GO';
          break;
        }
        case 'NO_RESP': {
          const resp =
            inlineResponse ||
            (await openNoRespModal({
              hasAnotherLine:
                this.#currentPhoneIdx < this.#phoneOpts.length - 1,
              enableTooltips: this.#services?.focusModeTooltips !== false,
            }));
          if (!resp) {
            this.#services?.loading?.hide('tasks');
            if (this.#state === 'noResp') {
              this.#state = 'awaitingOutcome';
              this.#render();
            }
            return;
          }

          noRespCallNext = resp.action === 'call';
          // Persist user's selection (voicemail, message, none, etc.)
          metadata.noRespReason = resp.option;
          metadata.reason = resp.option;

          this.#noRespDetails = {
            option: resp.option,
            action: resp.action,
            nextTouch: resp.nextTouch,
          };

          if (this.#isCallEmailTask() && resp.action !== 'call') {
            this.#services?.loading?.hide('tasks');
            const emailResult = await this.#handleCallEmailFollowup(contactId);
            if (!emailResult.ok) {
              return;
            }
            metadata.emailAttempted = emailResult.attempted;
            if (emailResult.skipped) {
              metadata.emailSkipped = true;
              metadata.emailSkipReason = emailResult.skipReason;
            }
            this.#services?.loading?.show('tasks', LOGGING_OUTCOME_MESSAGE);
          }
          if (isCallTextMessageTask && resp.action !== 'call') {
            metadata.smsAttempted = true;
            this.#services?.loading?.hide('tasks');
            const smsResp =
              await this.#handleCallTextMessageFollowup(contactId);
            if (!smsResp?.ok) {
              return;
            }
            if (smsResp?.meta) {
              const extra = smsResp.meta;
              if (Object.prototype.hasOwnProperty.call(extra, 'templateId')) {
                metadata.templateId = extra.templateId ?? null;
              }
              if (typeof extra.messageLength === 'number') {
                metadata.messageLength = extra.messageLength;
              }
              metadata.usedSmsDeeplink = Boolean(extra.usedDeeplink);
              if (extra.deeplink) {
                metadata.smsDeeplink = extra.deeplink;
              }
              if (Object.prototype.hasOwnProperty.call(extra, 'copied')) {
                metadata.copied = Boolean(extra.copied);
              }
              if (typeof extra.copiedAt === 'number') {
                metadata.copiedAt = extra.copiedAt;
              }
              if (extra.copyMethod) {
                metadata.copyMethod = extra.copyMethod;
              }
              if (extra.smsBody) {
                metadata.smsBody = extra.smsBody;
              }
            }
            this.#services?.loading?.showAfterDelay(
              'tasks',
              200,
              LOGGING_OUTCOME_MESSAGE
            );
          }

          if (resp.nextTouch) {
            metadata.nextTouchDate = resp.nextTouch;
          }

          outcome = 'NO_RESP';
          if (noRespCallNext) {
            await this.#releaseActiveCall();
            this.#resetUi();
            this.#showRedial = false;
          }
          break;
        }
        case 'NO_GO': {
          const { openNoGoModal } = await import(
            /* webpackChunkName: "noGoModal" */
            '../modals/noGoModal.js'
          );
          const resp = await openNoGoModal({
            enableTooltips: this.#services?.focusModeTooltips !== false,
          });
          console.log('[FM] NoGoModal resp', resp);
          metadata.noGoReason = resp.reason; // flat
          metadata.nextTouchDate = resp.nextTouch; // flat ISO
          metadata.reason = resp.reason; // keep nested for legacy
          if (resp.nextTouch && resp.sequenceResumeMode) {
            metadata.sequenceResumeMode = resp.sequenceResumeMode;
          }
          metadata.deleteContactInfo = resp.deleteContactInfo === true;
          if (!metadata.reason) {
            this.#services?.loading?.hide('tasks');
            return; // user cancelled ➜ abort save/advance
          }
          outcome = 'NO_GO';
          break;
        }
        default: {
          console.error('[FocusModeCallStep] unknown secondary action', {
            action,
          });
          this.#services?.loading?.hide('tasks');
          return;
        }
      }

      if (outcome === 'NO_RESP' && this.#noRespDetails) {
        behaviors.noRespMessage = this.#noRespDetails.option || '';
        behaviors.noRespNextTouch = this.#noRespDetails.nextTouch || '';
      } else {
        this.#noRespDetails = null; // ensure noResp fields only persist for NO_RESP
      }

      if (outcome === 'NO_RESP') {
        const selectedReason = metadata.noRespReason;
        const activePhone = this.#phoneOpts[this.#currentPhoneIdx] || {};
        const activeLabel = String(activePhone.label || '');
        const activeLabelNormalized = activeLabel.toLowerCase();
        let deleteContactInfo = false;
        let deleteCheckExecuted = false;
        try {
          if (selectedReason && this.#services?.fsService) {
            const config =
              await this.#services.fsService.getDocument('AppConfig/outcomes');
            const reasons = (config?.noRespReasons || [])
              .map((reason) => ({
                label: `${reason.label ?? ''}`.trim(),
                value: `${reason.value ?? reason.label ?? ''}`.trim(),
                allowsNextTouch: !!reason.allowsNextTouch,
                deleteContactInfo: reason.deleteContactInfo === true,
              }))
              .filter(
                (reason) => reason.label.length > 0 && reason.value.length > 0
              );
            const reasonMatch = reasons.find(
              (reason) => reason.value === `${selectedReason}`.trim()
            );
            deleteContactInfo = reasonMatch?.deleteContactInfo === true;
            deleteCheckExecuted = true;
          }
        } catch (err) {
          console.warn(
            '[FocusModeCallStep] failed to load no-resp reasons',
            err
          );
        }

        console.log('[FM] delete gate', {
          outcome,
          selectedReason,
          deleteContactInfo,
          activeLabel: activePhone.label,
          idx: this.#currentPhoneIdx,
        });
        let deletedNumber = false;
        if (deleteContactInfo) {
          console.log('[FM] activePhone debug', {
            activePhone,
            idx: this.#currentPhoneIdx,
          });
          const dialedRaw =
            this.#lastDialedTo ||
            activePhone.e164 ||
            activePhone.value ||
            activePhone.phone ||
            activePhone.number ||
            '';
          let contact = null;
          let tenantId = null;
          let contactSource = 'none';
          let contactPhones = {
            mobilePhone: null,
            officePhone: null,
            mainOfficePhone: null,
          };
          try {
            tenantId = await this.#resolveTenantId();
            if (tenantId && contactId && this.#services?.fsService) {
              const stagedRef = docWithTenant(
                tenantId,
                `ContactsStaging/${contactId}`
              );
              const stagedDoc = await this.#services.fsService.getDocument(
                stagedRef.path,
                false
              );
              if (stagedDoc) {
                contact = stagedDoc;
                contactSource = 'ContactsStaging';
                contactPhones = {
                  mobilePhone: stagedDoc?.mobilePhone ?? null,
                  officePhone: stagedDoc?.officePhone ?? null,
                  mainOfficePhone: stagedDoc?.mainOfficePhone ?? null,
                };
              }
            }
          } catch (err) {
            console.warn(
              '[FocusModeCallStep] failed to load staged contact for delete',
              err
            );
          }
          if (
            (!contactPhones.mobilePhone &&
              !contactPhones.officePhone &&
              !contactPhones.mainOfficePhone) ||
            !contact
          ) {
            try {
              if (tenantId && contactId && this.#services?.fsService) {
                const contactRef = docWithTenant(
                  tenantId,
                  `Contacts/${contactId}`
                );
                const contactDoc = await this.#services.fsService.getDocument(
                  contactRef.path,
                  false
                );
                if (contactDoc) {
                  contact = contactDoc;
                  contactSource = 'Contacts';
                  contactPhones = {
                    mobilePhone: contactDoc?.mobilePhone ?? null,
                    officePhone: contactDoc?.officePhone ?? null,
                    mainOfficePhone: contactDoc?.mainOfficePhone ?? null,
                  };
                }
              }
            } catch (err) {
              console.warn(
                '[FocusModeCallStep] failed to load contact for delete',
                err
              );
            }
          }

          console.log('[FM] delete inputs', {
            dialedRaw,
            dialed: normPhone(dialedRaw),
            activePhone,
            phoneOpts: this.#phoneOpts,
            contactId,
            tenantId,
            sourceLastDialedTo: this.#lastDialedTo,
          });
          console.log('[FM] contact source', {
            source: contactSource,
            contactId,
            tenantId,
          });

          const { updates, dialed, matched } = buildDeleteByValueUpdates({
            dialedRaw,
            contact,
          });
          console.log('[FM] delete-by-value', {
            dialedRaw,
            dialed,
            matchedFields: matched,
            updates,
            contactPhones,
          });

          let finalUpdates = updates;
          if (Object.keys(finalUpdates).length === 0) {
            console.log('[FM] delete-by-value no matches', {
              dialedRaw,
              dialed,
              contactPhones,
            });
            finalUpdates = {};
            if (!activePhone || Object.keys(activePhone).length === 0) {
              this.#log('no-resp delete skip: no active phone', {
                activePhone,
                activePhoneIndex: this.#currentPhoneIdx,
              });
            }
            if (!activeLabelNormalized) {
              this.#log('no-resp delete skip: missing active phone label', {
                activePhone,
                activePhoneIndex: this.#currentPhoneIdx,
              });
            }
            if (activeLabelNormalized === 'mobile') {
              finalUpdates.mobilePhone = null;
            } else if (activeLabelNormalized === 'office') {
              finalUpdates.officePhone = null;
            } else if (
              activeLabelNormalized === 'main office' ||
              activeLabelNormalized === 'main'
            ) {
              finalUpdates.mainOfficePhone = null;
            }
          }

          if (Object.keys(finalUpdates).length > 0) {
            const normalizedUpdates = Object.fromEntries(
              Object.entries(finalUpdates).map(([key, value]) => {
                if (typeof value === 'string') {
                  const trimmed = value.trim();
                  return [key, trimmed === '' ? null : trimmed];
                }
                if (value == null) return [key, null];
                return [key, value];
              })
            );
            deletedNumber = true;
            console.log('[FM] refreshPhones.start', {
              updates: normalizedUpdates,
            });
            try {
              await this.refreshPhones(normalizedUpdates, {
                preferUpdated: false,
                persistToStaging: false,
              });
              console.log('[FM] refreshPhones.ok', {
                updates: normalizedUpdates,
              });
            } catch (error) {
              console.error('[FM] refreshPhones.fail', error, {
                updates: normalizedUpdates,
              });
              throw error;
            }
            if (tenantId && contactId) {
              try {
                const { stagingDocPath } = await patchStagingSameAsContactEdit({
                  tenantId,
                  contactId,
                  patch: normalizedUpdates,
                  services: this.#services,
                });
                console.log('[FM][Delete] wrote staging', {
                  contactId,
                  path: stagingDocPath,
                  updates: normalizedUpdates,
                });
              } catch (err) {
                console.warn('[FM][Delete] staging patch failed', err, {
                  contactId,
                  updates: normalizedUpdates,
                });
              }
            }
            if (this.#services?.contactMetaService && contactId) {
              await this.#services.contactMetaService.update(
                contactId,
                normalizedUpdates
              );
            }
          } else {
            this.#log('no-resp delete skip: no matching label', {
              activeLabel,
              activePhoneIndex: this.#currentPhoneIdx,
              deleteContactInfo,
            });
          }
        }

        console.log('[FocusModeCallStep] no-resp delete', {
          reason: selectedReason,
          deleteContactInfo,
          phoneLabel: activeLabel,
          deletedNumber,
          deleteCheckExecuted,
        });
        noRespDeletedNumber = deletedNumber;
        if (noRespCallNext) {
          this.#noRespDetails = null; // reset when calling another line
          if (
            !noRespDeletedNumber &&
            Number.isInteger(this.#queuedPhoneIdxAfterOutcome)
          ) {
            this.#currentPhoneIdx = Math.max(
              0,
              Math.min(
                this.#queuedPhoneIdxAfterOutcome,
                this.#phoneOpts.length - 1
              )
            );
          } else if (
            !noRespDeletedNumber &&
            this.#currentPhoneIdx < this.#phoneOpts.length - 1
          ) {
            this.#currentPhoneIdx++;
          }
          this.#queuedPhoneIdxAfterOutcome = null;
          this.#state = 'initial';
          this.#render();
          this.#services?.loading?.hide('tasks');
          return;
        }
      }

      if (outcome === 'NO_GO') {
        const selectedReason = metadata.noGoReason;
        const activePhone = this.#phoneOpts[this.#currentPhoneIdx] || {};
        const activeLabel = String(activePhone.label || '');
        const activeLabelNormalized = String(activePhone.label || '')
          .trim()
          .toLowerCase();
        let deleteContactInfo = metadata?.deleteContactInfo === true;
        let deleteCheckExecuted = deleteContactInfo;
        try {
          if (
            !deleteContactInfo &&
            selectedReason &&
            this.#services?.fsService
          ) {
            const config =
              await this.#services.fsService.getDocument('AppConfig/outcomes');
            const reasons = (config?.noGoReasons || [])
              .map((reason) => ({
                label: `${reason.label ?? ''}`.trim().toLowerCase(),
                allowsNextTouch: !!reason.allowsNextTouch,
                deleteContactInfo: reason.deleteContactInfo === true,
              }))
              .filter((reason) => reason.label.length > 0);
            const selectedReasonNormalized = `${selectedReason}`
              .trim()
              .toLowerCase();
            const reasonMatch = reasons.find(
              (reason) => reason.label === selectedReasonNormalized
            );
            deleteContactInfo = reasonMatch?.deleteContactInfo === true;
            deleteCheckExecuted = true;
          }
        } catch (err) {
          console.warn('[FocusModeCallStep] failed to load no-go reasons', err);
        }

        console.log('[FM] delete gate', {
          outcome,
          selectedReason,
          deleteContactInfo,
          deleteCheckExecuted,
          activeLabel: activePhone.label,
          idx: this.#currentPhoneIdx,
        });
        let deletedNumber = false;
        if (deleteContactInfo) {
          console.log('[FM] activePhone debug', {
            activePhone,
            idx: this.#currentPhoneIdx,
          });
          const dialedRaw =
            this.#lastDialedTo ||
            activePhone.e164 ||
            activePhone.value ||
            activePhone.phone ||
            activePhone.number ||
            '';
          let contact = null;
          let tenantId = null;
          let contactSource = 'none';
          let contactPhones = {
            mobilePhone: null,
            officePhone: null,
            mainOfficePhone: null,
          };
          try {
            tenantId = await this.#resolveTenantId();
            if (tenantId && contactId && this.#services?.fsService) {
              const stagedRef = docWithTenant(
                tenantId,
                `ContactsStaging/${contactId}`
              );
              const stagedDoc = await this.#services.fsService.getDocument(
                stagedRef.path,
                false
              );
              if (stagedDoc) {
                contact = stagedDoc;
                contactSource = 'ContactsStaging';
                contactPhones = {
                  mobilePhone: stagedDoc?.mobilePhone ?? null,
                  officePhone: stagedDoc?.officePhone ?? null,
                  mainOfficePhone: stagedDoc?.mainOfficePhone ?? null,
                };
              }
            }
          } catch (err) {
            console.warn(
              '[FocusModeCallStep] failed to load staged contact for delete',
              err
            );
          }
          if (
            (!contactPhones.mobilePhone &&
              !contactPhones.officePhone &&
              !contactPhones.mainOfficePhone) ||
            !contact
          ) {
            try {
              if (tenantId && contactId && this.#services?.fsService) {
                const contactRef = docWithTenant(
                  tenantId,
                  `Contacts/${contactId}`
                );
                const contactDoc = await this.#services.fsService.getDocument(
                  contactRef.path,
                  false
                );
                if (contactDoc) {
                  contact = contactDoc;
                  contactSource = 'Contacts';
                  contactPhones = {
                    mobilePhone: contactDoc?.mobilePhone ?? null,
                    officePhone: contactDoc?.officePhone ?? null,
                    mainOfficePhone: contactDoc?.mainOfficePhone ?? null,
                  };
                }
              }
            } catch (err) {
              console.warn(
                '[FocusModeCallStep] failed to load contact for delete',
                err
              );
            }
          }

          console.log('[FM] delete inputs', {
            dialedRaw,
            dialed: normPhone(dialedRaw),
            activePhone,
            phoneOpts: this.#phoneOpts,
            contactId,
            tenantId,
            sourceLastDialedTo: this.#lastDialedTo,
          });
          console.log('[FM] contact source', {
            source: contactSource,
            contactId,
            tenantId,
          });

          const { updates, dialed, matched } = buildDeleteByValueUpdates({
            dialedRaw,
            contact,
          });
          console.log('[FM] delete-by-value', {
            dialedRaw,
            dialed,
            matchedFields: matched,
            updates,
            contactPhones,
          });

          let finalUpdates = updates;
          if (Object.keys(finalUpdates).length === 0) {
            console.log('[FM] delete-by-value no matches', {
              dialedRaw,
              dialed,
              contactPhones,
            });
            finalUpdates = {};
            if (!activePhone || Object.keys(activePhone).length === 0) {
              this.#log('no-go delete skip: no active phone', {
                activePhone,
                activePhoneIndex: this.#currentPhoneIdx,
              });
            }
            if (!activeLabelNormalized) {
              this.#log('no-go delete skip: missing active phone label', {
                activePhone,
                activePhoneIndex: this.#currentPhoneIdx,
              });
            }
            if (activeLabelNormalized === 'mobile') {
              finalUpdates.mobilePhone = null;
            } else if (activeLabelNormalized === 'office') {
              finalUpdates.officePhone = null;
            } else if (
              activeLabelNormalized === 'main office' ||
              activeLabelNormalized === 'main'
            ) {
              finalUpdates.mainOfficePhone = null;
            }
          }

          if (Object.keys(finalUpdates).length > 0) {
            const normalizedUpdates = Object.fromEntries(
              Object.entries(finalUpdates).map(([key, value]) => {
                if (typeof value === 'string') {
                  const trimmed = value.trim();
                  return [key, trimmed === '' ? null : trimmed];
                }
                if (value == null) return [key, null];
                return [key, value];
              })
            );
            deletedNumber = true;
            console.log('[FM] refreshPhones.start', {
              updates: normalizedUpdates,
            });
            try {
              await this.refreshPhones(normalizedUpdates, {
                preferUpdated: false,
                persistToStaging: false,
              });
              console.log('[FM] refreshPhones.ok', {
                updates: normalizedUpdates,
              });
            } catch (error) {
              console.error('[FM] refreshPhones.fail', error, {
                updates: normalizedUpdates,
              });
              throw error;
            }
            if (tenantId && contactId) {
              try {
                const { stagingDocPath } = await patchStagingSameAsContactEdit({
                  tenantId,
                  contactId,
                  patch: normalizedUpdates,
                  services: this.#services,
                });
                console.log('[FM][Delete] wrote staging', {
                  contactId,
                  path: stagingDocPath,
                  updates: normalizedUpdates,
                });
              } catch (err) {
                console.warn('[FM][Delete] staging patch failed', err, {
                  contactId,
                  updates: normalizedUpdates,
                });
              }
            }
            if (this.#services?.contactMetaService && contactId) {
              await this.#services.contactMetaService.update(
                contactId,
                normalizedUpdates
              );
            }
          } else {
            this.#log('no-go delete skip: no matching label', {
              activeLabel,
              activePhoneIndex: this.#currentPhoneIdx,
              deleteContactInfo,
            });
          }
        }

        console.log('[FocusModeCallStep] no-go delete', {
          reason: selectedReason,
          deleteContactInfo,
          phoneLabel: activeLabel,
          deletedNumber,
          deleteCheckExecuted,
        });
      }

      console.log('[FM] complete payload', { outcome, metadata });
      const tenantId = await this.#resolveTenantId();
      const endSequence = outcome === 'GO';
      const notesText = this.#getTaskNotesText();
      if (notesText) {
        void this.#queueNotesSave(notesText);
      }
      if (isCallTextMessageTask) {
        this.#pendingCallTextOutcome = {
          outcome,
          action: this.#noRespDetails?.action,
          behaviors,
          metadata,
          endSequence,
          notesText,
        };
        this.#callTextCompleteReady = true;
        this.#state = 'initial';
        this.#render();
        return;
      }
      // 1) Post legacy/server outcome DTO (existing behavior)
      this.#services?.loading?.showAfterDelay(
        'tasks',
        200,
        LOGGING_OUTCOME_MESSAGE
      );
      await this.#completeWithOutcome(
        this.#task,
        {
          taskId: this.#task.id,
          userId: this.#task.userId || auth?.currentUser?.uid,
          outcome,
          action: this.#noRespDetails?.action,
          channel: this.#channelForOutcome(),
          behaviors,
          sequenceId: this.#task.sequenceId || this.#task.enrolId,
          stepIndex: this.#task.stepIdx,
          metadata,
          ...(endSequence ? { endSequence: true } : {}),
        },
        { tenantIdOverride: tenantId }
      );
      // 2) Mark completion in Firestore (mirror/server) — do not log outcome here
      try {
        await this.#services.focusModeTaskService.completeTask(
          this.#task.id,
          notesText,
          {
            logOutcome: false,
            tenantIdOverride: tenantId,
            ...(endSequence ? { endSequence: true } : {}),
          }
        );
      } catch (e) {
        console.warn('[CallStepController] mirror completion failed', {
          taskId: this.#task.id,
          e,
        });
      }

      console.log('[CallStepController] Firestore save dispatched ✔'); // 🪵

      await this.#finishTaskStep();
    } catch (err) {
      this.#services.errorUI?.global?.({
        text: err.message,
        type: 'error',
        duration: 3000,
      });
    } finally {
      this.#setOutcomeActionBusy(false);
      this.#services?.loading?.hide('tasks');
    }
  }

  // eslint-disable-next-line no-unused-private-class-members
  async #handleEndSequence() {
    const uid = this.#task.userId || this.#services.auth?.currentUser?.uid;
    const contactId =
      this.#task.contactId ||
      this.#task.associated_contact_id ||
      this.#task.contact_id;
    const tenantId = await this.#resolveTenantId();
    try {
      await this.#completeWithOutcome(
        this.#task,
        {
          taskId: this.#task.id,
          userId: uid,
          outcome: 'NO_GO',
          action: this.#noRespDetails?.action,
          channel: this.#channelForOutcome(),
          endSequence: true,
          sequenceId: this.#task.sequenceId || this.#task.enrolId,
          stepIndex: this.#task.stepIdx,
          metadata: { reason: 'reply-end', contactId },
        },
        { tenantIdOverride: tenantId }
      );
    } catch (err) {
      console.error('[CallStepController] task completion failed', err);
      this.#services?.errorUI?.global({
        text: err.message,
        type: 'error',
        duration: 3000,
      });
      return;
    }
    try {
      await this.#services.focusModeTaskService.completeTask(
        this.#task.id,
        null,
        {
          logOutcome: false,
          tenantIdOverride: tenantId,
          endSequence: true,
        }
      );
    } catch (e) {
      console.warn('[CallStepController] mirror completion failed', {
        taskId: this.#task.id,
        e,
      });
    }
    await this.#services.taskReplyService?.acknowledge(
      this.#meta.replyTaskId || this.#task.id
    );
    await this.#finishTaskStep();
  }

  // eslint-disable-next-line no-unused-private-class-members
  async #handleSkip() {
    const uid = this.#task.userId || this.#services.auth?.currentUser?.uid;
    const contactId =
      this.#task.contactId ||
      this.#task.associated_contact_id ||
      this.#task.contact_id;
    const tenantId = await this.#resolveTenantId();
    try {
      await this.#completeWithOutcome(
        this.#task,
        {
          taskId: this.#task.id,
          userId: uid,
          outcome: 'TASK_COMPLETE',
          action: this.#noRespDetails?.action,
          channel: this.#channelForOutcome(),
          sequenceId: this.#task.sequenceId || this.#task.enrolId,
          stepIndex: this.#task.stepIdx,
          metadata: { skipped: 'reply', contactId },
        },
        { tenantIdOverride: tenantId }
      );
    } catch (err) {
      console.error('[CallStepController] task completion failed', err);
      this.#services?.errorUI?.global({
        text: err.message,
        type: 'error',
        duration: 3000,
      });
      return; // Abort if completion fails
    }
    try {
      await this.#services.focusModeTaskService.completeTask(
        this.#task.id,
        null,
        {
          logOutcome: false,
          tenantIdOverride: tenantId,
        }
      );
    } catch (e) {
      console.warn('[CallStepController] mirror completion failed', {
        taskId: this.#task.id,
        e,
      });
    }
    await this.#services.taskReplyService?.acknowledge(
      this.#meta.replyTaskId || this.#task.id
    );
    await this.#finishTaskStep();
  }

  async #handlePass() {
    const { openPassModal } = await import(
      /* webpackChunkName: "passModal" */
      '../modals/passModal.js'
    );
    const loading = this.#ensureLoadingService();
    const data = await openPassModal();
    if (!data) return;

    const uid = this.#task.userId || this.#services.auth?.currentUser?.uid;
    const contactId =
      this.#task.contactId ||
      this.#task.associated_contact_id ||
      this.#task.contact_id;
    const selectedReason =
      data?.selectedReason && typeof data.selectedReason === 'object'
        ? data.selectedReason
        : null;
    const selectedReasonOutcome = `${selectedReason?.outcomeType ?? ''}`
      .trim()
      .toUpperCase();
    const endSequence = data.action === 'end';
    const outcome = endSequence
      ? selectedReasonOutcome === 'GO'
        ? 'GO'
        : 'NO_GO'
      : 'TASK_COMPLETE';
    const workingMsg = endSequence
      ? 'Ending this sequence…'
      : 'Skipping this step…';
    const fallbackReason =
      `${data.reason ?? selectedReason?.label ?? ''}`.trim();
    const meetingBooked = selectedReason?.meetingBooked === true;
    const passReasonId = `${selectedReason?.id ?? ''}`.trim();
    const passReasonCode =
      `${selectedReason?.value ?? ''}`.trim() || passReasonId || null;
    const passReasonLabel =
      `${selectedReason?.label ?? ''}`.trim() || fallbackReason || null;
    const passReasonText = `${selectedReason?.text ?? ''}`.trim() || null;
    const metadata = {
      passAction: data.action,
      passReason: fallbackReason,
      reason: fallbackReason,
      contactId,
      passReasonId: passReasonId || null,
      passReasonCode,
      passReasonLabel,
      passReasonText,
      passReasonOutcomeType: selectedReasonOutcome || null,
      passMeetingBooked: meetingBooked,
    };
    if (outcome === 'NO_GO' && fallbackReason) {
      metadata.noGoReason = fallbackReason;
    }
    if (outcome === 'GO') {
      metadata.meetingBooked = meetingBooked;
      metadata.goReasons = [
        {
          id: passReasonId || null,
          label: passReasonLabel || '',
          text: passReasonText || '',
          meetingBooked,
        },
      ];
    }
    loading.show('tasks', workingMsg);
    try {
      const tenantId = await this.#resolveTenantId();
      if (!tenantId) {
        console.warn(
          '[CallStepController] pass abort: unable to resolve tenantId'
        );
        return;
      }
      try {
        await this.#completeWithOutcome(
          this.#task,
          {
            taskId: this.#task.id,
            userId: uid,
            outcome,
            action: this.#noRespDetails?.action,
            channel: this.#channelForOutcome(),
            endSequence,
            sequenceId: this.#task.sequenceId || this.#task.enrolId,
            stepIndex: this.#task.stepIdx,
            metadata,
          },
          { tenantIdOverride: tenantId }
        );
      } catch (err) {
        console.error('[CallStepController] task completion failed', err);
        this.#services?.errorUI?.global({
          text: err.message,
          type: 'error',
          duration: 3000,
        });
        return; // Abort subsequent UI steps if completion fails
      }
      try {
        await this.#services.focusModeTaskService.completeTask(
          this.#task.id,
          null,
          {
            logOutcome: false,
            tenantIdOverride: tenantId,
            ...(endSequence ? { endSequence: true } : {}),
          }
        );
      } catch (e) {
        console.warn('[CallStepController] mirror completion failed', {
          taskId: this.#task.id,
          e,
        });
      }

      await this.#services.taskReplyService?.acknowledge(
        this.#meta.replyTaskId || this.#task.id
      );
      await this.#finishTaskStep();
    } finally {
      loading.hide?.('tasks');
    }
  }

  /**
   * Resolve and cache the tenant ID for mirror writes.
   * Falls back to the Users/{uid} document when not present on the auth user.
   * @returns {Promise<string|null>}
   */
  async #resolveTenantId() {
    if (this.#tenantIdCache) return this.#tenantIdCache;
    const { auth, fsService, tenant } = this.#services;
    try {
      const user = await auth?.waitForLogin?.().catch(() => auth?.currentUser);
      // Fall back to the task owner when auth has not rehydrated yet
      const uid =
        user?.uid ?? auth?.currentUser?.uid ?? this.#task?.userId ?? null;
      if (!uid) {
        this.#tenantIdCache = null;
        return null;
      }
      // Resolution order (fastest/most reliable first)
      let tenantId =
        tenant?.getTenantId?.() ??
        this.#task?.tenantId ??
        auth?.currentUser?.tenantId ??
        useTenant(auth ?? {});
      // helper path
      if (!tenantId && fsService?.getTenantId) {
        tenantId = await fsService.getTenantId(uid).catch(() => null);
      }
      // Users/{uid} doc fallback
      if (!tenantId && fsService) {
        const userDoc = await fsService
          .getDocument(`Users/${uid}`, false)
          .catch(() => null);
        const pick = (v) => (typeof v === 'string' ? v.trim() : (v ?? null));
        tenantId =
          pick(userDoc?.tenantId) ??
          pick(userDoc?.tenant_id) ??
          pick(userDoc?.tenant) ??
          null;
      }
      this.#tenantIdCache = tenantId ?? null;
      return this.#tenantIdCache;
    } catch (err) {
      console.warn('[CallStepController] resolveTenantId failed', err);
      this.#tenantIdCache = null;
      return null;
    }
  }

  /**
   * Prompt the rep to create a follow-up CRM task and persist it via manageUserTask.
   *
   * @param {string|null} contactId - CRM contact identifier to associate with the task.
   * @returns {Promise<{taskId: string|null; cancelled: boolean}>}
   */
  async #promptFollowUpTask(contactId) {
    const companyId =
      this.#task.companyId ||
      this.#task.associated_company_id ||
      this.#task.company_id ||
      null;

    const { openTaskCreationModal } = await import(
      '../modals/taskCreationModal.js'
    );
    const store = this.#services?.tasksStore;
    if (!store?.createTask) {
      throw new Error('Task creation service unavailable');
    }

    const modalResp = await openTaskCreationModal({
      defaultSubject: this.#meta?.name
        ? `Follow up with ${this.#meta.name}`
        : 'Follow up task',
    });
    if (!modalResp) return { taskId: null, cancelled: true };

    const context = contactId ? { contactId } : {};
    const taskId = await store.createTask(
      {
        subject: modalResp.subject,
        status: 'NOT_STARTED',
        description: modalResp.notes,
        contactId: contactId || undefined,
        companyId: companyId || undefined,
        dueDateISO: modalResp.dueDate || undefined,
        metadata: {
          focusmodeHidden: true,
          origin: 'followup',
        },
      },
      context,
      { skipCrmCreates: false }
    );
    return { taskId: taskId ?? null, cancelled: false };
  }

  async #saveNotes(text) {
    console.debug('[CallStepController] #saveNotes() entry ➜', { text });
    /* 1️⃣ ignore empty / whitespace-only notes */
    const notesText = String(text ?? '').trim();
    if (!notesText) {
      console.warn('[CallStepController] #saveNotes abort: empty after trim');
      return null;
    }

    try {
      const { auth, endpoints, apiClient, crmUtils } = this.#services;
      if (!auth) {
        console.warn(
          '[CallStepController] #saveNotes abort: auth service missing'
        );
        return null;
      }

      /* ⏳ ensure Firebase has restored the session *
       * AuthService.waitForLogin() resolves with the user object. */
      let user = await auth?.waitForLogin?.().catch(() => null);
      if (!user) user = auth?.currentUser ?? null;
      if (!user) {
        console.warn('[CallStepController] #saveNotes abort: no auth user');
        return null;
      }

      const contactId =
        this.#task.contactId ||
        this.#task.associated_contact_id ||
        this.#task.contact_id;
      if (!contactId) {
        console.warn('[CallStepController] #saveNotes abort: no contactId');
        return null;
      }

      /* 2️⃣ build CF URL – keep blank noteId so the CF creates a new note */
      const crm = await crmUtils.getCurrentCRM(user.uid);

      if (crm === 'local') {
        const mirrorSvc = this.#services?.mirrorNotesService;
        if (!mirrorSvc?.save) {
          throw new Error('Mirror notes service unavailable');
        }
        const saved = await mirrorSvc.save({ contactId, text: notesText });
        console.log('[CallStepController] #saveNotes success (mirror)');
        return saved;
      }

      const tenantId = resolveActiveTenantId({
        tenant: this.#services?.tenant,
        auth,
      });
      if (!tenantId) {
        throw new Error('Missing tenant ID');
      }
      const url = endpoints.saveContactNotes({
        userId: user.uid,
        contactId,
        noteId: '', // leave empty → “create”
        crm,
        tenantId,
      });

      console.debug('[CallStepController] #saveNotes URL ➜', url);
      console.debug('[CallStepController] #saveNotes payload ➜', {
        userId: user.uid,
        contactId,
        notesText,
        crm,
        tenantId,
      });

      /* 3️⃣ post JSON with auth token */
      const resp = await apiClient.fetchJson(url, {
        method: 'POST',
        auth: true,
        body: { userId: user.uid, contactId, notesText, crm, tenantId },
      });

      /* 4️⃣ surface backend errors */
      console.debug('[CallStepController] #saveNotes response ➜', resp);
      if (resp?.error || resp?.status >= 400) {
        throw new Error(
          resp.error || `Failed to save note (status ${resp.status})`
        );
      }
      console.log('[CallStepController] #saveNotes success', resp);
      return resp;
    } catch (err) {
      console.error('[CallStepController] saveNotes error:', err);
      this.#services?.errorUI?.global({
        text: err.message,
        type: 'error',
        duration: 3000,
      });
      return null;
    }
  }

  /**
   * Lifecycle teardown invoked when the Focus Mode view unmounts.
   *
   * @returns {void}
   */
  destroy() {
    this.#clearRedialCooldown();
    this.#teardownLinkedInReturnListeners();
    this.#linkedinModalOpen = false;
    this.#linkedinActivityModal?.close?.();
    this.#clearContactLocalCallWindowPromptRetry();
    this.#closeContactLocalCallWindowModal();
    this.#contactLocalCallWindowBlock = null;
    this.#contactLocalCallWindowOverrideAccepted = false;
  }

  /* ─────────── behaviour buttons (Contract / 30-Second / Referral) ─────────── */
  async handleBehavior(key, evt) {
    const btn = evt?.currentTarget ?? null;

    const behavior = btn?.dataset?.behavior ?? key;
    const label = `${
      btn?.dataset?.behaviorLabel ??
      btn?.getAttribute?.('data-behavior-label') ??
      btn?.textContent ??
      ''
    }`.trim();
    if (!behavior || !label) return;

    const isSelected = this.#behaviorSelections.has(label);
    const setSelected = (nextSelected) => {
      if (nextSelected) {
        this.#behaviorSelections.set(label, behavior);
      } else {
        this.#behaviorSelections.delete(label);
      }
      btn?.classList.toggle('behavior-btn--active', nextSelected);
    };

    if (behavior === 'referral') {
      if (isSelected) {
        setSelected(false);
        const hasReferralSelection = Array.from(
          this.#behaviorSelections.values()
        ).some((value) => value === 'referral');
        if (!hasReferralSelection) {
          this.#referrals = [];
        }
        return;
      }

      setSelected(true);
      const { openReferralModal } = await import(
        /* webpackChunkName: "referralModal" */
        '../modals/referralModal.js'
      );

      const data = await openReferralModal();
      const normalized = (data?.referrals ?? [])
        .map((r) => ({
          name: r.name ?? r.referralName ?? '',
          company: r.company ?? r.referralCompany ?? '',
          notes: r.notes ?? r.referralNotes ?? '',
        }))
        .filter((r) => r.name || r.company || r.notes);

      if (!normalized.length) {
        return;
      }

      this.#referrals = normalized;
      return;
    }

    setSelected(!isSelected);
  }
}

export { normPhone, buildDeleteByValueUpdates };
